import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// React Router hooks removed for navigation - but need useLocation for URL params
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import { Child, Activity } from '../types';
import TimePickerDropdown from './TimePickerDropdown';
import CalendarDatePicker from './CalendarDatePicker';
import ClubsScreen from './ClubsScreen';
import ConnectionsScreen from './ConnectionsScreen';
import PhoneInput from './PhoneInput';
import ActivityReminderComponent from './ActivityReminder';
import LocalNotificationService, { REMINDER_OPTIONS } from '../services/localNotifications';
import { Capacitor } from '@capacitor/core';
import './ChildActivityScreen.css';
import { formatDateUK } from '../utils/dateFormat';

// Global function declaration for scroll restoration
declare global {
  interface Window {
    restoreClubScrollPosition?: () => boolean;
  }
}

// Helper function to ensure URLs have proper protocol
const ensureProtocol = (url: string): string => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

// Helper function to get notifications for a specific activity
const getActivityNotifications = (activity: any, notifications: any[]): any[] => {
  const matches = notifications.filter(notification => {
    // Match notification to activity by UUID (preferred)
    if (notification.data && notification.data.activity_uuid) {
      const activityUuid = activity.uuid || activity.activity_uuid;
      return notification.data.activity_uuid === activityUuid;
    }
    
    // REMOVED: No numeric ID matching - UUIDs only for security
    
    // Fallback matching by activity name and date
    if (notification.data && notification.data.activityName && notification.data.activityDate) {
      return notification.data.activityName === activity.name && 
             notification.data.activityDate === activity.start_date;
    }
    
    // Legacy fallback with different field names
    if (notification.data && notification.data.activity_name) {
      return notification.data.activity_name === activity.name;
    }
    
    if (notification.data && notification.data.activity_name && notification.data.activity_date) {
      return notification.data.activity_name === activity.name && 
             notification.data.activity_date === activity.start_date;
    }
    
    return false;
  });

  // Debug logging for "Test act not 1" activity
  if (activity.name === 'Test act not 1') {
  }
  
  return matches;
};

// Activity types for dropdown
const ACTIVITY_TYPES = [
  'Drama',
  'Multi-Sport', 
  'Football',
  'Rugby',
  'Athletics',
  'Netball',
  'Tech',
  'Science',
  'Craft',
  'Cooking',
  'Other'
];

interface ActivityFormState {
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  location: string;
  venue?: string;
  city?: string;
  website_url: string;
  cost: string;
  max_participants: string;
  activity_type: string;
  auto_notify_new_connections: boolean;
  kit_needed: string[];
  contact_phone?: string;
  contact_email?: string;
  passcode?: string;
  is_tentative?: boolean;
  responsible_parent_uuid?: string;
}

interface ChildActivityScreenProps {
  child: Child;
  onBack: () => void;
  onDataChanged?: () => void;
  onNavigateToConnections?: (isInActivityCreation?: boolean) => void;
  onNavigateToActivity?: (child: Child, activity: any) => void;
  initialActivityUuid?: string;
  shouldRestoreActivityCreation?: boolean;
  refreshTrigger?: number;
}

const ChildActivityScreen: React.FC<ChildActivityScreenProps> = ({ child, onBack, onDataChanged, onNavigateToConnections, onNavigateToActivity, initialActivityUuid, shouldRestoreActivityCreation, refreshTrigger }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Helper function to get initial activity state with user's location
  const getInitialActivityState = (): ActivityFormState => {

    return {
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      location: user?.town_city || '',
      website_url: '',
      cost: '',
      max_participants: '',
      activity_type: '',
      auto_notify_new_connections: false,
      kit_needed: [],
      contact_phone: '',
      contact_email: '',
      is_tentative: false
    };
  };
  
  // Parse URL parameters for initial calendar view and date
  const urlParams = new URLSearchParams(location.search);
  const initialView = urlParams.get('view') === 'week' ? '7day' : '5day';
  const initialDate = urlParams.get('date');
  
  const [activities, setActivities] = useState<Activity[]>([]);
  const [invitedActivities, setInvitedActivities] = useState<any[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [currentDateRange, setCurrentDateRange] = useState<{startDate: string, endDate: string} | null>(null);
  const [declinedInvitations, setDeclinedInvitations] = useState<any[]>([]);
  // Removed childNotifications - not needed for this screen
  // Initialize loading - only show loading when we need to fetch activity data
  const [loading, setLoading] = useState(() => {
    const navigationActivity = (location.state as any)?.activity;
    // Show loading ONLY if:
    // 1. We have an activity UUID to fetch AND
    // 2. We don't have full navigation data (just a UUID stub)
    if (initialActivityUuid && initialActivityUuid !== 'new' && (!navigationActivity || !navigationActivity.name)) {
      return true;
    }
    return false;
  });
  // Page visibility control - delay rendering for scroll restoration
  // Modal state for clubs browsing
  const [isClubsModalOpen, setIsClubsModalOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [clubSelectionFieldType, setClubSelectionFieldType] = useState<'location' | 'website_url'>('website_url');

  // Modal state for connections
  const [isConnectionsModalOpen, setIsConnectionsModalOpen] = useState(false);
  const [connectionScrollPosition, setConnectionScrollPosition] = useState(0);

  // State for decline dialog
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineDialogActivity, setDeclineDialogActivity] = useState<any>(null);
  const [declineOption, setDeclineOption] = useState('single');

  // State for reactivate dialog
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [reactivateDialogActivity, setReactivateDialogActivity] = useState<any>(null);
  const [reactivateOption, setReactivateOption] = useState('single');

  // State for accept invitation dialog
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [acceptDialogData, setAcceptDialogData] = useState<any>(null);
  const [acceptOption, setAcceptOption] = useState('single');

  // State for decline invitation dialog - using existing showDeclineDialog state
  const [declineDialogData, setDeclineDialogData] = useState<any>(null);
  const [declineInvitationOption, setDeclineInvitationOption] = useState('single');

  // State for parents list (for designated parent selection)
  const [parents, setParents] = useState<any[]>([]);

  // State for multi-select participant selection (children and/or parents) in activity creation
  // Initialize from navigation state - supports both single selection (from modal) and multi-select
  const [selectedParticipantChildUuids, setSelectedParticipantChildUuids] = useState<string[]>(() => {
    const navState = (location.state as any);
    // If navigated with a specific child (not parent activity), include the child
    if (!navState?.isParentActivity && child?.uuid) {
      return [child.uuid];
    }
    return [];
  });
  const [selectedParticipantParentUuids, setSelectedParticipantParentUuids] = useState<string[]>(() => {
    const navState = (location.state as any);
    // If navigated with a specific parent, include the parent
    if (navState?.isParentActivity && navState?.parentUuid) {
      return [navState.parentUuid];
    }
    return [];
  });

  // Close clubs modal and restore scroll position
  const closeClubsModal = (selectedClubData?: any) => {
    setIsClubsModalOpen(false);

    // Restore scroll position
    window.scrollTo(0, scrollPosition);

    // Allow background scrolling
    document.body.style.overflow = "auto";

    // If club was selected, populate the field
    if (selectedClubData) {

      if (clubSelectionFieldType === 'website_url') {
        const updatedActivity = {
          ...newActivity,
          website_url: selectedClubData.website_url || '',
          city: selectedClubData.location || newActivity.city,  // Club's location is the town/city
          venue: selectedClubData.venue || newActivity.venue,
          activity_type: selectedClubData.activity_type || newActivity.activity_type,
          contact_phone: selectedClubData.contact_phone || newActivity.contact_phone,
          contact_email: selectedClubData.contact_email || newActivity.contact_email,
          club_uuid: selectedClubData.uuid || null,
          club: selectedClubData.uuid ? {
            uuid: selectedClubData.uuid,
            name: selectedClubData.name,
            location: selectedClubData.location,
            venue: selectedClubData.venue,
            website_url: selectedClubData.website_url,
            activity_type: selectedClubData.activity_type
          } : null
        };
        console.log('🏢 Club selected, setting newActivity with club:', {
          clubName: selectedClubData.name,
          clubUuid: selectedClubData.uuid,
          city: selectedClubData.location,
          venue: selectedClubData.venue,
          fullClubObject: updatedActivity.club
        });
        setNewActivity(updatedActivity);
      } else if (clubSelectionFieldType === 'location') {
        setNewActivity(prev => ({
          ...prev,
          city: selectedClubData.location || '',  // Club's location is the town/city
          venue: selectedClubData.venue || '',
          club_uuid: selectedClubData.uuid || null,
          club: selectedClubData.uuid ? {
            uuid: selectedClubData.uuid,
            name: selectedClubData.name,
            location: selectedClubData.location,
            venue: selectedClubData.venue,
            website_url: selectedClubData.website_url,
            activity_type: selectedClubData.activity_type
          } : null
        }));
        console.log('🏢 Club selected via location field, setting city and venue:', {
          city: selectedClubData.location,
          venue: selectedClubData.venue
        });
      }
    }
  };

  // Close connections modal and restore scroll position
  const closeConnectionsModal = () => {
    setIsConnectionsModalOpen(false);

    // Restore scroll position
    window.scrollTo(0, connectionScrollPosition);

    // Allow background scrolling
    document.body.style.overflow = "auto";

    // Refresh connections data when modal closes
    loadConnectionsData();
  };

  // Handle decline dialog
  const handleDeclineClick = (activity: any) => {
    setDeclineDialogActivity(activity);
    setDeclineOption('single'); // Reset to default
    setShowDeclineDialog(true);
  };

  const handleDeclineDialogClose = () => {
    setShowDeclineDialog(false);
    setDeclineDialogActivity(null);
  };

  const handleReactivateDialogClose = () => {
    setShowReactivateDialog(false);
    setReactivateDialogActivity(null);
    setReactivateOption('single'); // Reset to default
  };

  const handleAcceptDialogClose = () => {
    setShowAcceptDialog(false);
    setAcceptDialogData(null);
    setAcceptOption('single'); // Reset to default
  };

  const handleDeclineInvitationDialogClose = () => {
    setShowDeclineDialog(false);
    setDeclineDialogData(null);
    setDeclineInvitationOption('single'); // Reset to default
  };

  // Execute accept invitation from modal
  const executeAcceptInvitation = async (acceptSeries: boolean) => {
    if (!acceptDialogData) return;

    const { invitationUuid, seriesInvitations } = acceptDialogData;
    const action = 'accept';

    try {
      if (acceptSeries) {
        // Accept the entire series - since backend handles bulk update,
        // we only need to respond to the first invitation

        const firstInvitation = seriesInvitations[0];
        const response = await apiService.respondToActivityInvitation(firstInvitation.invitation_uuid, action);

        const successCount = response.success ? seriesInvitations.length : 0;

        if (successCount > 0) {
          const message = `Series accepted! ${successCount} recurring activities will appear in your calendar.`;
          alert(message);

          // If series was accepted, increment club usage for each accepted invitation
          try {
            const acceptedInvitations = seriesInvitations;

            for (const invitation of acceptedInvitations) {
              const clubUsageData = {
                website_url: invitation.website_url,
                activity_type: invitation.activity_type,
                location: invitation.location || undefined,
                child_age: child.age || undefined,
                activity_start_date: invitation.start_date,
                activity_uuid: invitation.activity_uuid
              };

              if (clubUsageData.website_url && clubUsageData.activity_type) {
                await apiService.incrementClubUsage(clubUsageData);
              } else {
                console.warn('❌ SERIES ACCEPT: Missing required data for club usage:', {
                  website_url: clubUsageData.website_url,
                  activity_type: clubUsageData.activity_type
                });
              }
            }
          } catch (clubError) {
            console.warn('Error in series club increment:', clubError);
            // Don't fail invitation acceptance if club increment fails
          }

          // Reload activities to update the display
          loadActivities();

          // Close the detail modal and refresh parent data
          goBack();
          onDataChanged?.();
        } else {
          alert(`Error: Failed to accept series invitations`);
        }
      } else {
        // Handle single invitation (user chose single from modal)
        const response = await apiService.respondToActivityInvitation(invitationUuid, action);

        if (response.success) {
          alert('Invitation accepted! Activity will appear in your calendar.');

          // Refresh invitation data immediately to update button state
          if (selectedActivity) {
            const activityId = selectedActivity.uuid || selectedActivity.activity_uuid;

            if (activityId) {
              try {
                // Refresh the invitation data for this activity (pass child UUID)
                const response = await apiService.getActivityParticipants(activityId, child.uuid);

                if (response.success && response.data?.user_invitation) {
                  const invitation = response.data.user_invitation;

                  const invitationInfo = {
                    isPendingInvitation: invitation.status === 'pending',
                    isAcceptedInvitation: invitation.status === 'accepted',
                    isDeclinedInvitation: invitation.status === 'declined' || invitation.status === 'rejected',
                    invitationUuid: invitation.invitation_uuid || invitation.uuid,
                    invitation_message: invitation.invitation_message
                  };

                  setInvitationData(invitationInfo);
                }
              } catch (error) {
                console.error('Error refreshing invitation data:', error);
              }
            }
          }

          // If invitation was accepted, increment club usage count
          // Skip for Pitchero/SOCS activities as they are externally managed
          try {
            if (acceptDialogData.currentInvitation) {
              const invitation = acceptDialogData.currentInvitation;
              const activitySource = invitation.activity_source;
              const isPitcheroOrSocs = activitySource === 'pitchero' || activitySource === 'socs';

              if (!isPitcheroOrSocs) {
                const clubUsageData = {
                  website_url: invitation.website_url,
                  activity_type: invitation.activity_type,
                  location: invitation.location || undefined,
                  child_age: child.age || undefined,
                  activity_start_date: invitation.start_date,
                  activity_uuid: invitation.activity_uuid
                };

                if (clubUsageData.website_url && clubUsageData.activity_type) {
                  await apiService.incrementClubUsage(clubUsageData);
                } else {
                  console.warn('❌ ACCEPT SINGLE: Missing required data for club usage:', {
                    website_url: clubUsageData.website_url,
                    activity_type: clubUsageData.activity_type
                  });
                }
              }
            }
          } catch (clubError) {
            console.warn('Error in single accept club increment:', clubError);
            // Don't fail invitation acceptance if club increment fails
          }

          // Reload activities list but DON'T close the detail page
          // This allows the user to see the accepted state
          await loadActivities(true);

          // Update selectedActivity with the accepted status
          if (selectedActivity) {
            setSelectedActivity({
              ...selectedActivity,
              isPendingInvitation: false,
              isAcceptedInvitation: true,
              isDeclinedInvitation: false,
              invitation_status: 'accepted'
            } as Activity);
          }

          // Notify parent to reload data when user navigates back
          onDataChanged?.();

          // Don't call goBack() - stay on the page to show accepted state
          // goBack();
        } else {
          alert('Error accepting invitation: ' + (response.error || 'Unknown error'));
        }
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      alert('Error accepting invitation');
    } finally {
      handleAcceptDialogClose();
    }
  };

  // Execute decline invitation from modal
  const executeDeclineInvitation = async (declineSeries: boolean) => {
    if (!declineDialogData) return;

    const { invitationUuid, seriesInvitations } = declineDialogData;
    const action = 'reject';

    try {
      if (declineSeries) {
        // Decline the entire series - since backend handles bulk update,
        // we only need to respond to the first invitation

        const firstInvitation = seriesInvitations[0];
        const response = await apiService.respondToActivityInvitation(firstInvitation.invitation_uuid, action);

        const successCount = response.success ? seriesInvitations.length : 0;

        if (successCount > 0) {
          const message = `Series declined. ${successCount} invitations removed.`;
          alert(message);

          // Reload activities to update the display
          loadActivities();

          // Close the detail modal and refresh parent data
          goBack();
          onDataChanged?.();
        } else {
          alert(`Error: Failed to decline series invitations`);
        }
      } else {
        // Handle single invitation (user chose single from modal)
        const response = await apiService.respondToActivityInvitation(invitationUuid, action);

        if (response.success) {
          alert('Invitation declined.');

          // Refresh invitation data immediately to update button state
          if (selectedActivity) {
            const activityId = selectedActivity.uuid || selectedActivity.activity_uuid;

            if (activityId) {
              try {
                // Refresh the invitation data for this activity (pass child UUID)
                const response = await apiService.getActivityParticipants(activityId, child.uuid);

                if (response.success && response.data?.user_invitation) {
                  const invitation = response.data.user_invitation;

                  const invitationInfo = {
                    isPendingInvitation: invitation.status === 'pending',
                    isAcceptedInvitation: invitation.status === 'accepted',
                    isDeclinedInvitation: invitation.status === 'declined' || invitation.status === 'rejected',
                    invitationUuid: invitation.invitation_uuid || invitation.uuid,
                    invitation_message: invitation.invitation_message
                  };

                  setInvitationData(invitationInfo);
                }
              } catch (error) {
                console.error('Error refreshing invitation data:', error);
              }
            }
          }

          // Clear API cache to force fresh data from backend
          apiService.clearAllCaches();

          // Reload activities list but DON'T close the detail page
          // This allows the user to see the declined state and "Can Now Attend" button
          await loadActivities(true);

          // Update selectedActivity with the declined status to show grey state
          if (selectedActivity) {
            setSelectedActivity({
              ...selectedActivity,
              isPendingInvitation: false,
              isAcceptedInvitation: false,
              isDeclinedInvitation: true,
              invitation_status: 'declined'
            } as Activity);
          }

          // Notify parent to reload data when user navigates back
          onDataChanged?.();

          // Don't call goBack() - stay on the page to show declined state
          // goBack();
        } else {
          alert('Error declining invitation: ' + (response.error || 'Unknown error'));
        }
      }
    } catch (error) {
      console.error('Error declining invitation:', error);
      alert('Error declining invitation');
    } finally {
      handleDeclineInvitationDialogClose();
    }
  };

  // Guest can now attend (reactivate for guests)
  const handleGuestCanNowAttend = async (activity: any) => {

    // Check if activity is part of a recurring series
    const seriesId = (activity as any).series_id;
    const isMigrationArtifact = seriesId && typeof seriesId === 'string' && seriesId.startsWith('migrated_');

    // If activity has a valid series_id, it's part of a series
    const isSeriesActivity = seriesId && !isMigrationArtifact;


    if (isSeriesActivity) {
      // Show reactivate dialog for series choice
      setReactivateDialogActivity({ ...activity, is_host: false });
      setReactivateOption('single');
      setShowReactivateDialog(true);
    } else {
      // Single activity - reactivate directly
      await guestReactivateActivity(activity, false);
    }
  };

  // Guest reactivate activity function
  const guestReactivateActivity = async (activity: any, reactivateEntireSeries: boolean) => {
    try {
      const activitySource = activity.activity_source || activity.source;
      const isPitcheroOrSocs = activitySource === 'pitchero' || activitySource === 'socs';
      const invitationUuid = activity.invitationId || activity.invitation_uuid || activity.invitationUuid;

      let response;

      // For Pitchero/SOCS activities, use the respond endpoint (has Pitchero API sync)
      if (isPitcheroOrSocs && !reactivateEntireSeries && invitationUuid) {
        console.log(`🔄 Using respond endpoint for Pitchero/SOCS reactivate: invitation=${invitationUuid}`);
        response = await apiService.respondToActivityInvitation(invitationUuid, 'accept');
      } else {
        // For regular activities, use reactivate endpoint
        const endpoint = reactivateEntireSeries
          ? '/api/activities/reactivate-series'
          : '/api/activities/reactivate-single';

        response = await apiService.post(endpoint, {
          activityUuid: activity.activity_uuid || activity.uuid,
          isHost: false
        });
      }

      if (response.success) {
        const message = reactivateEntireSeries
          ? `Successfully accepted the entire series!`
          : `Successfully accepted the activity!`;

        alert(message);

        // Clear ALL cached data first to ensure fresh data
        apiService.clearAllCaches();

        // Refresh activities to update the list
        await loadActivities(true);

        // Notify parent to reload calendar data
        onDataChanged?.();

        // Navigate back to calendar (dismiss activity screen)
        setSelectedActivity(null);
        goBack();

      } else {
        console.error('Guest reactivate failed:', response.error);
        alert(`Failed to accept activity: ${response.error}`);
      }
    } catch (error) {
      console.error('Guest reactivate error:', error);
      alert('Failed to accept activity. Please try again.');
    }
  };

  // Decline/not attending activity
  const declineActivity = async (activity: any, declineEntireSeries: boolean) => {
    try {
      const activitySource = activity.activity_source || activity.source;
      const isPitcheroOrSocs = activitySource === 'pitchero' || activitySource === 'socs';

      // For Pitchero/SOCS, we MUST have the invitation_uuid (not activity UUID)
      // Try invitationId first (from CalendarScreen mapping), then invitation_uuid (from backend)
      const invitationUuid = activity.invitationId || activity.invitation_uuid;

      // DEBUG: Log all available IDs
      console.log('🔍 DECLINE DEBUG - Activity object:', {
        invitationId: activity.invitationId,
        invitation_uuid: activity.invitation_uuid,
        invitationUuid: activity.invitationUuid,
        uuid: activity.uuid,
        activity_uuid: activity.activity_uuid,
        id: activity.id,
        activity_id: activity.activity_id,
        source: activitySource,
        isPitcheroOrSocs,
        finalInvitationUuid: invitationUuid
      });

      let response;

      // For Pitchero/SOCS activities, use the respond endpoint (has Pitchero API sync)
      if (isPitcheroOrSocs && !declineEntireSeries && invitationUuid) {
        console.log(`🔄 Using respond endpoint for Pitchero/SOCS decline: invitation=${invitationUuid}, activity=${activity.uuid || activity.activity_uuid}`);
        response = await apiService.respondToActivityInvitation(invitationUuid, 'reject');
      } else {
        // For regular activities, use the decline endpoint
        const endpoint = declineEntireSeries
          ? '/api/activities/decline-series'
          : '/api/activities/decline-single';

        response = await apiService.post(endpoint, {
          activityUuid: activity.uuid || activity.activity_uuid,
          isHost: activity.is_host || false
        });
      }

      if (response.success) {

        // Show success message
        alert(`Successfully marked as not attending ${declineEntireSeries ? 'entire series' : 'this session'}`);

        // Clear cached data BEFORE navigating back
        apiService.clearAllCaches();

        // Force immediate refresh before navigation
        await loadActivities(true);

        // Navigate away after data is refreshed
        setSelectedActivity(null);
        goBack();
      } else {
        throw new Error(response.error || 'Failed to decline activity');
      }
    } catch (error) {
      console.error('❌ Error declining activity:', error);
      alert('Failed to mark as not attending. Please try again.');
    }
  };
  // Initialize currentPage - if we have an initialActivityUuid with navigation state and FULL DATA, start on activity-detail
  const [currentPage, setCurrentPage] = useState<'main' | 'add-activity' | 'activity-detail' | 'edit-activity' | 'invite-activity'>(() => {
    // Check for navigation state activity on initial mount
    const navigationActivity = (location.state as any)?.activity;
    // Only start on activity-detail if we have FULL data (not just a UUID stub)
    if (initialActivityUuid && navigationActivity && navigationActivity.name) {
      return 'activity-detail';
    }
    if (initialActivityUuid === 'new') {
      return 'add-activity';
    }
    return 'main';
  });
  const [calendarView, setCalendarView] = useState<'5day' | '7day'>(initialView);
  const [currentWeek, setCurrentWeek] = useState(() => {
    if (initialDate) {
      return new Date(initialDate);
    }
    return new Date();
  });
  // Initialize selectedActivity with navigation state if available
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(() => {
    const navigationActivity = (location.state as any)?.activity;
    if (initialActivityUuid && navigationActivity) {
      return navigationActivity;
    }
    return null;
  });
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [invitingActivity, setInvitingActivity] = useState<Activity | null>(null);
  const [connectedFamilies, setConnectedFamilies] = useState<any[]>([]);
  const [invitationMessage, setInvitationMessage] = useState('');
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [isRecurringActivity, setIsRecurringActivity] = useState(false);
  const [recurringDaysOfWeek, setRecurringDaysOfWeek] = useState<number[]>([]);
  const [recurringStartDate, setRecurringStartDate] = useState('');
  const [recurringEndDate, setRecurringEndDate] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteDialogActivity, setDeleteDialogActivity] = useState<Activity | null>(null);
  const [deleteOption, setDeleteOption] = useState('single');
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updateDialogActivity, setUpdateDialogActivity] = useState<Activity | null>(null);
  const [updateOption, setUpdateOption] = useState('single');
  const [isSharedActivity, setIsSharedActivity] = useState(false);
  const [autoNotifyNewConnections, setAutoNotifyNewConnections] = useState(false);
  const [selectedConnectedChildren, setSelectedConnectedChildren] = useState<(number | string)[]>([]);
  const [jointHostChildren, setJointHostChildren] = useState<string[]>([]); // UUIDs of additional host children
  const [connectedChildren, setConnectedChildren] = useState<any[]>([]);
  const [connectedParents, setConnectedParents] = useState<Array<{uuid: string, name: string}>>([]);
  const [accountParents, setAccountParents] = useState<Array<{uuid: string, name: string, is_primary: boolean}>>([]);
  const [parentChildren, setParentChildren] = useState<any[]>([]);
  const [jointHostConnections, setJointHostConnections] = useState<{ [childUuid: string]: any[] }>({});
  const [pendingConnectionRequests, setPendingConnectionRequests] = useState<any[]>([]);
  // Classes this child's parent reps. A class is a group the rep can invite in
  // one go; it is not a set of personal connections.
  const [repClasses, setRepClasses] = useState<any[]>([]);
  const [selectedClassUuids, setSelectedClassUuids] = useState<string[]>([]);
  const [activityDraft, setActivityDraft] = useState<any>(null);
  const [newActivity, setNewActivity] = useState<ActivityFormState>(getInitialActivityState);
  const [addingActivity, setAddingActivity] = useState(false);
  const [selectedReminderMinutes, setSelectedReminderMinutes] = useState<number | null>(null);
  const [selectedReminderTarget, setSelectedReminderTarget] = useState<'start' | 'end' | 'both' | null>(null);
  const [activityParticipants, setActivityParticipants] = useState<any>(null);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [batchParticipantsData, setBatchParticipantsData] = useState<any>({});

  // Kit tracking state
  const [kitTracking, setKitTracking] = useState<any>(null);
  const [loadingKit, setLoadingKit] = useState(false);
  const [newKitItem, setNewKitItem] = useState('');

  // Tentative responses state
  const [tentativeResponses, setTentativeResponses] = useState<any>(null);
  const [loadingTentativeResponses, setLoadingTentativeResponses] = useState(false);
  const [showTentativeResponses, setShowTentativeResponses] = useState(false);

  const [invitationData, setInvitationData] = useState<any>(() => {
    // Initialize from navigation state immediately if available AND child UUID matches
    const navigationState = location.state as any;
    if (navigationState?.invitationStatus) {
      // Only use this invitation status if it's for the current child
      if (navigationState.invitationStatus.childUuid === child.uuid) {
        return navigationState.invitationStatus;
      } else {
      }
    }
    return null;
  });

  // Activity Templates
  const [activityTemplates, setActivityTemplates] = useState<any[]>([]);
  const [activityTypes, setActivityTypes] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [pendingTemplateData, setPendingTemplateData] = useState<any>(null);
  const [templateConfirmationMessage, setTemplateConfirmationMessage] = useState<string>('');
  
  // Clubs data for URL/Location selection
  const [clubs, setClubs] = useState<any[]>([]);
  const [loadingClubs, setLoadingClubs] = useState<boolean>(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateDialogMessage, setTemplateDialogMessage] = useState('');
  const [dialogTemplateData, setDialogTemplateData] = useState<any>(null);
  
  // Connection search
  const [connectionSearchTerm, setConnectionSearchTerm] = useState('');
  
  const apiService = ApiService.getInstance();

  // Template dialog handlers  
  const handleTemplateYes = async () => {
    if (!dialogTemplateData) return;

    try {
      // Check if a template with the same name already exists
      const existingTemplate = activityTemplates.find(
        t => t.name.toLowerCase().trim() === dialogTemplateData.name.toLowerCase().trim()
      );

      let templateResponse;
      if (existingTemplate) {
        // Update existing template
        console.log('📝 Updating existing template:', existingTemplate.uuid);
        templateResponse = await apiService.updateActivityTemplate(existingTemplate.uuid, dialogTemplateData);
      } else {
        // Create new template
        console.log('✨ Creating new template:', dialogTemplateData.name);
        templateResponse = await apiService.createActivityTemplate(dialogTemplateData);
      }

      if (templateResponse.success) {
        await loadActivityTemplates();
      } else {
        console.error('❌ Failed to save template:', templateResponse.error);
        alert(`Failed to save template: ${templateResponse.error}`);
      }
    } catch (templateError: any) {
      console.error('💥 Error saving template:', templateError);
      alert(`Error saving template: ${templateError?.message || 'Unknown error'}`);
    }

    // Close dialog
    setShowTemplateDialog(false);
    setTemplateDialogMessage('');
    setDialogTemplateData(null);

    // Navigate back to activities list after template saved
    // Force refresh when returning to calendar
    setCurrentDateRange(null);
    if (onBack) {
      onBack();
    } else {
      navigateToPage('main');
    }
  };

  const handleTemplateNo = () => {
    // Close dialog
    setShowTemplateDialog(false);
    setTemplateDialogMessage('');
    setDialogTemplateData(null);
    
    // Navigate back to activities list after declining template
    // Force refresh when returning to calendar
    setCurrentDateRange(null);
    if (onBack) {
      onBack();
    } else {
      navigateToPage('main');
    }
  };

  // Get user-specific draft key to prevent cross-user data leakage
  const getDraftKey = () => {
    const currentUser = JSON.parse(localStorage.getItem('userData') || '{}');
    return `activityDraft_${currentUser.id || 'unknown'}`;
  };

  // Save activity draft to localStorage
  const saveActivityDraft = () => {
    const draft = {
      newActivity,
      selectedDates,
      isSharedActivity,
      autoNotifyNewConnections,
      selectedConnectedChildren,
      jointHostChildren,
      childUuid: child.uuid
    };
    localStorage.setItem(getDraftKey(), JSON.stringify(draft));
    return draft;
  };

  // Restore activity draft from localStorage
  const restoreActivityDraft = () => {
    try {
      const draftStr = localStorage.getItem(getDraftKey());
      if (draftStr) {
        const draft = JSON.parse(draftStr);
        // Only restore if it's for the same child
        if (draft.childUuid === child.uuid) {
          setNewActivity(draft.newActivity ? {
            ...getInitialActivityState(),
            ...draft.newActivity,
            // Ensure location defaults to user's town_city if not already set
            location: draft.newActivity.location || user?.town_city || ''
          } : getInitialActivityState());
          setSelectedDates(draft.selectedDates || []);
          setIsSharedActivity(draft.isSharedActivity || false);
          setAutoNotifyNewConnections(draft.autoNotifyNewConnections || false);
          setSelectedConnectedChildren(draft.selectedConnectedChildren || []);
          setJointHostChildren(draft.jointHostChildren || []);
          // Restore recurring activity state
          setIsRecurringActivity(draft.isRecurringActivity || false);
          setRecurringDaysOfWeek(draft.recurringDaysOfWeek || []);
          setRecurringStartDate(draft.recurringStartDate || '');
          setRecurringEndDate(draft.recurringEndDate || '');
          setActivityDraft(draft);
          
          // Automatically navigate to add-activity screen when draft is restored
          setTimeout(() => {
            navigateToPage('add-activity');
          }, 100);
          
          return true;
        }
      }
    } catch (error) {
      console.error('Failed to restore activity draft:', error);
    }
    return false;
  };

  // Clear activity draft
  const clearActivityDraft = () => {
    localStorage.removeItem(getDraftKey());
    setActivityDraft(null);
  };

  // Open connections modal instead of navigating
  const handleNavigateToConnections = () => {
    // Save scroll position and open modal instead of navigating
    const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    setConnectionScrollPosition(currentScrollPosition);
    setIsConnectionsModalOpen(true);

    // Prevent background scrolling
    document.body.style.overflow = "hidden";
  };

  // Handle cancel with draft consideration
  const handleCancelActivity = () => {
    if (activityDraft) {
      // If there's a draft, ask if they want to keep it
      const keepDraft = window.confirm(
        'You have a saved draft of this activity. Would you like to keep it for later?\n\n' +
        'Click "OK" to keep the draft, or "Cancel" to discard it.'
      );
      if (!keepDraft) {
        clearActivityDraft();
      }
    }
    
    // Reset form state
    setNewActivity(getInitialActivityState());
    setSelectedDates([]);
    setIsSharedActivity(false);
    setAutoNotifyNewConnections(false);
    setSelectedConnectedChildren([]);
    setSelectedClassUuids([]);
    setJointHostChildren([]);
    setSelectedTemplate(null);
    setPendingTemplateData(null);
    
    goBack();
  };

  useEffect(() => {
    // Clean up any old non-user-specific drafts (security fix)
    localStorage.removeItem('activityDraft');

    // Check if returning from club selection - only hide page for main view scroll restoration
    const urlParams = new URLSearchParams(window.location.search);
    const isReturningFromClubs = urlParams.get('clubData') || urlParams.get('clubCancel');
    const currentPath = window.location.pathname;
    const isNewActivityForm = currentPath.includes('/activities/new');

    if (isReturningFromClubs && !isNewActivityForm) {
    } else {
    }

    // Load only essential data first (connections needed for display)
    const loadEssentialData = async () => {
      // REMOVED: loadActivities() - only load when user actually views the calendar
      // This prevents unnecessary API calls on initial load
      await loadConnectionsData();

      // Check if there's a saved draft to restore
      restoreActivityDraft();

      if (isReturningFromClubs) {
        // Handle club selection and scroll restoration
        // Modal approach - no longer need to check for club selection from URL
        // checkForClubSelection();
      }
      // Note: If not returning from clubs, page is already ready (set in initial state)

      // Check if returning from connections screen (after skeleton account creation)
      checkForConnectionUpdates();
    };

    // Load essential data first
    loadEssentialData();

    // Load non-essential data asynchronously (these don't block page display)
    setTimeout(() => {
      loadActivityTemplates();
      loadActivityTypes();
      loadParentChildren();
      loadAccountParents();
      loadClubs();
    }, 100);

  }, [child.uuid]);

  // Check for month changes when user navigates or toggles calendar
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && activities.length > 0) {
        loadActivities(); // This will check for month change internally
      }
    };

    const handleFocusChange = () => {
      if (activities.length > 0) {
        loadActivities(); // This will check for month change internally
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocusChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocusChange);
    };
  }, [activities.length, currentDateRange]);

  // No longer needed - using modal instead of navigation

  // Load connections when shared activity is enabled
  useEffect(() => {
    if (isSharedActivity && connectedChildren.length === 0) {
      loadConnectionsData();
    }
  }, [isSharedActivity]);

  // The rep's classes are an audience the activity can be aimed at, so they
  // load with the form rather than waiting for "shared" to be ticked.
  useEffect(() => {
    loadRepClasses();
  }, []);

  // Creating on behalf of a class. A rep doing this wants a note on the
  // class's calendar — not a club, a passcode or a designated parent — so the
  // form drops everything that only matters for a family's own activity.
  const isClassActivity = selectedClassUuids.length > 0;

  // Arriving from "Who is this activity for?" with a class chosen.
  useEffect(() => {
    const preselected = (location.state as any)?.preselectClassUuid;
    if (preselected) {
      setSelectedClassUuids(prev => (prev.includes(preselected) ? prev : [...prev, preselected]));
      setIsSharedActivity(true);
    }
  }, [location.state]);

  // Refresh connections data when refreshTrigger changes (e.g., returning from connections screen)
  useEffect(() => {
    if (refreshTrigger && (isSharedActivity || currentPage === 'activity-detail')) {
      loadConnectionsData();
    }
  }, [refreshTrigger]);

  // Load connections for joint host children when they change
  useEffect(() => {
    if (jointHostChildren.length > 0) {
      jointHostChildren.forEach(childUuid => {
        // Only load if we don't already have connections for this child
        if (!jointHostConnections[childUuid]) {
          loadJointHostConnections(childUuid);
        }
      });
    }
  }, [jointHostChildren]);

  // Debug parentChildren state changes
  useEffect(() => {
  }, [parentChildren]);

  // Filter and sort connections
  const getFilteredAndSortedConnections = () => {
    let filtered = connectedChildren;
    
    // Filter by search term
    if (connectionSearchTerm.trim()) {
      filtered = connectedChildren.filter(child =>
        child.name.toLowerCase().includes(connectionSearchTerm.toLowerCase())
      );
    }
    
    // Sort alphabetically by name
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  };

  const getFilteredAndSortedPendingRequests = () => {
    let filtered = pendingConnectionRequests;
    
    // Filter by search term
    if (connectionSearchTerm.trim()) {
      filtered = pendingConnectionRequests.filter(request =>
        (request.child_name || request.username || '').toLowerCase().includes(connectionSearchTerm.toLowerCase())
      );
    }
    
    // Sort alphabetically by name
    return filtered.sort((a, b) => {
      const nameA = a.child_name || a.username || '';
      const nameB = b.child_name || b.username || '';
      return nameA.localeCompare(nameB);
    });
  };

  // Page navigation function
  const navigateToPage = (page: typeof currentPage) => {
    if (page !== currentPage) {
      // Dismiss keyboard before navigating
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // If returning to main and an invitation was viewed, refresh both local and parent data
      if (page === 'main' && sessionStorage.getItem('invitationViewed') === 'true') {
        sessionStorage.removeItem('invitationViewed');
        loadActivities(); // Refresh local activity data to update envelope icons
        onDataChanged?.(); // Refresh parent data
      }

      setCurrentPage(page);
      // Don't push to browser history - internal page state only
      // React Router already manages navigation history

      // Scroll to top when navigating to add-activity, edit-activity, or activity-detail pages
      if (page === 'add-activity' || page === 'edit-activity' || page === 'activity-detail') {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    }
  };

  // Track if we've already fetched to prevent double-fetching
  const hasFetchedActivityRef = React.useRef(false);

  // Reset scroll position when navigating to activity pages
  useEffect(() => {
    if (currentPage === 'activity-detail' || currentPage === 'add-activity' || currentPage === 'edit-activity') {
      window.scrollTo(0, 0);
    }
  }, [currentPage]);

  // Handle initial activity UUID for direct navigation to specific activities
  useEffect(() => {
    // Handle special "new" UUID for activity creation
    if (initialActivityUuid === 'new') {
      navigateToPage('add-activity');
      return;
    }

    // Check if activity was passed via navigation state (from calendar click)
    // ONLY use it if it has actual data (not just a UUID stub)
    const navigationActivity = (location.state as any)?.activity;
    if (initialActivityUuid && navigationActivity && navigationActivity.name) {
      // We have full activity data from navigation state
      console.log('🔵 NAVIGATION ACTIVITY FROM CALENDAR:', navigationActivity);
      console.log('🔵 activity_source in navigationActivity:', navigationActivity.activity_source);
      setSelectedActivity(navigationActivity);

      // Populate newActivity state with the activity data for editing
      setNewActivity({
        name: navigationActivity.name || '',
        description: navigationActivity.description || '',
        start_date: navigationActivity.start_date ? navigationActivity.start_date.split('T')[0] : '',
        end_date: navigationActivity.end_date ? navigationActivity.end_date.split('T')[0] : '',
        start_time: navigationActivity.start_time || '',
        end_time: navigationActivity.end_time || '',
        location: navigationActivity.location || '',
        website_url: navigationActivity.website_url || '',
        contact_phone: navigationActivity.contact_phone || '',
        contact_email: navigationActivity.contact_email || '',
        cost: navigationActivity.cost ? String(navigationActivity.cost) : '',
        max_participants: navigationActivity.max_participants ? String(navigationActivity.max_participants) : '',
        activity_type: navigationActivity.activity_type || '',
        auto_notify_new_connections: navigationActivity.auto_notify_new_connections || false,
        kit_needed: navigationActivity.kit_needed || [],
        responsible_parent_uuid: navigationActivity.responsible_parent_uuid || undefined
      });

      // Load participants for the activity (kit_tracking already in activity data)
      // For Pitchero/SOCS invitations, activity_uuid is the actual activity UUID
      const activityUuid = navigationActivity.activity_uuid || navigationActivity.uuid;
      if (activityUuid) {
        loadActivityParticipants(activityUuid);
        // No need to load kit tracking - it's already in the activity response
      }

      navigateToPage('activity-detail');
      return;
    }

    // If no navigation state but we have initialActivityUuid, fetch just that activity
    // OR if navigationActivity exists but only has UUID (minimal data from notification)
    const needsFetch = initialActivityUuid && (
      !navigationActivity ||
      (navigationActivity && !navigationActivity.name) // If no name, it's just a stub
    ) && activities.length === 0;

    console.log('🟡 CHILD ACTIVITY SCREEN - Activity Load Check:', {
      initialActivityUuid,
      hasNavigationActivity: !!navigationActivity,
      navigationActivityKeys: navigationActivity ? Object.keys(navigationActivity) : [],
      navigationActivityName: navigationActivity?.name,
      activitiesLength: activities.length,
      needsFetch,
      locationState: location.state
    });

    if (needsFetch && !hasFetchedActivityRef.current) {
      console.log('🟡 FETCHING ACTIVITY:', initialActivityUuid);
      hasFetchedActivityRef.current = true; // Mark as fetched to prevent double-fetching
      const fetchSingleActivity = async () => {
        try {
          setLoading(true); // Show loading state while fetching
          console.log('🟡 Calling apiService.getActivity for:', initialActivityUuid);
          const response = await apiService.getActivity(initialActivityUuid);
          console.log('🔍 GET ACTIVITY RESPONSE:', {
            success: response.success,
            hasData: !!response.data,
            data: response.data,
            fields: response.data ? Object.keys(response.data) : [],
            activityName: response.data?.name,
            contactPhone: response.data?.contact_phone,
            contactEmail: response.data?.contact_email
          });
          if (response.success && response.data) {
            console.log('🟡 Setting activity data and navigating to detail');
            console.log('🔍 API RESPONSE DATA:', response.data);
            console.log('🔍 activity_source in response:', response.data.activity_source);
            console.log('🔍 source in response:', response.data.source);

            // 🔄 RECURRING ACTIVITIES FIX: If this is a recurring activity, fetch all activities in the series
            const seriesId = response.data.series_id;
            const isRecurring = response.data.is_recurring || seriesId;

            if (isRecurring && seriesId) {
              console.log('🔄 RECURRING ACTIVITY DETECTED - Fetching full series:', seriesId);
              try {
                // Fetch all child activities to find other activities in this series
                const childActivitiesResponse = await apiService.getActivities(response.data.child_uuid);
                if (childActivitiesResponse.success && childActivitiesResponse.data) {
                  // Filter to get all activities with the same series_id
                  const seriesActivities = childActivitiesResponse.data.filter((a: any) => a.series_id === seriesId);
                  console.log(`🔄 Found ${seriesActivities.length} activities in series ${seriesId}`);
                  setActivities(seriesActivities);
                } else {
                  console.warn('⚠️ Failed to fetch series activities, using single activity');
                  setActivities([response.data]);
                }
              } catch (error) {
                console.error('❌ Error fetching series activities:', error);
                setActivities([response.data]);
              }
            } else {
              // Single activity - just set the one activity
              setActivities([response.data]);
            }

            setSelectedActivity(response.data);

            // Load participants for the activity
            // For Pitchero/SOCS invitations, activity_uuid is the actual activity UUID
            const activityUuid = response.data.activity_uuid || response.data.uuid;
            if (activityUuid) {
              loadActivityParticipants(activityUuid);

              // Also load tentative responses if it's a tentative activity and user is host
              if (response.data.is_tentative && response.data.is_host) {
                loadTentativeResponses(activityUuid);
              }
            }

            setLoading(false); // Hide loading before showing detail
            navigateToPage('activity-detail');
          } else {
            console.error('❌ Activity fetch failed:', response);
            setLoading(false);
          }
        } catch (error) {
          console.error('❌ Failed to fetch activity:', error);
          setLoading(false);
        }
      };
      fetchSingleActivity();
      return;
    }

    // If we have navigation state with full data, use it
    if (initialActivityUuid && navigationActivity && navigationActivity.name) {
      return;
    }


    if (initialActivityUuid && (activities.length > 0 || pendingInvitations.length > 0)) {

      // Search in both activities and pending invitations
      // After UUID normalization in api.ts, all activities have uuid = activity UUID
      let activity = activities.find(a => {
        const activityUuid = (a as any).uuid || (a as any).activity_uuid;
        return activityUuid === initialActivityUuid;
      });

      // If not found in activities, search in pending invitations
      if (!activity) {
        activity = pendingInvitations.find(p => {
          // Normalized pending invitations also have uuid = activity UUID
          const activityUuid = (p as any).uuid || (p as any).activity_uuid;
          return activityUuid === initialActivityUuid;
        });
        if (activity) {

          // Determine the actual invitation type based on status
          const invitationStatus = (activity as any).status || (activity as any).invitation_status;
          const isPending = invitationStatus === 'pending';
          const isRejected = invitationStatus === 'rejected' || invitationStatus === 'declined';
          const isAccepted = invitationStatus === 'accepted';


          // Mark it with the correct invitation type and normalize field names
          activity = {
            ...activity,
            isPendingInvitation: isPending,
            isRejectedInvitation: isRejected,
            isDeclinedInvitation: isRejected,
            isAcceptedInvitation: isAccepted,
            invitation_status: invitationStatus,
            invitationUuid: (activity as any).invitation_uuid || (activity as any).uuid,
            name: (activity as any).activity_name || (activity as any).name,
            uuid: (activity as any).activity_uuid || (activity as any).uuid,
            activity_source: (activity as any).activity_source  // Preserve activity_source for Delete button
          } as any;
        } else {
        }
      }
      
      
      if (activity) {
        // For initial activity navigation from URL, go to detail page (same as clicking activity)

        // Capture activity in a const for the async function closure
        const foundActivity = activity;

        // Check if this activity has a pending invitation (same logic as handleActivityClick)
        const checkAndSetActivity = async () => {
          let enhancedActivity = { ...foundActivity };

          // ALWAYS check for invitation data to ensure we have the latest status and invitation UUID
          // (Even if activity already has flags, we need the invitation UUID for buttons to work)
          try {
            // Check ALL invitations (pending, accepted, declined) to see if current user has an invitation for this activity
            const today = new Date();
            const oneYearLater = new Date();
            oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
            const startDate = today.toISOString().split('T')[0];
            const endDate = oneYearLater.toISOString().split('T')[0];

            // Use batch invitations endpoint to get ALL invitations (not just pending)
            const batchResponse = await apiService.getBatchInvitations(startDate, endDate);

            if (batchResponse.success && batchResponse.data) {
              // Combine all invitation types (calendar_invitations and pending_invitations)
              const allInvitations = [
                ...(batchResponse.data.calendar_invitations || []),
                ...(batchResponse.data.pending_invitations || [])
              ];

              // Look for an invitation that matches this activity (any status: pending, accepted, or declined)
              // After UUID normalization, both inv.uuid and foundActivity.uuid contain activity UUIDs
              const matchingInvitation = allInvitations.find((inv: any) => {
                // Match by activity UUID
                if (foundActivity.uuid && inv.uuid && foundActivity.uuid === inv.uuid) {
                  return true;
                }
                if (foundActivity.uuid && inv.activity_uuid) {
                  return inv.activity_uuid === foundActivity.uuid;
                }
                if ((foundActivity as any).activity_uuid && inv.activity_uuid) {
                  return inv.activity_uuid === (foundActivity as any).activity_uuid;
                }
                // Fallback to matching by name, date, and time
                return inv.activity_name === foundActivity.name &&
                       inv.start_date === foundActivity.start_date &&
                       inv.start_time === foundActivity.start_time;
              });

              if (matchingInvitation) {
                // Notifications disabled for this screen
                const hasMatchingNotification = false;

                // Only convert to pending invitation format if NO notification exists (fallback to old system)
                if (!hasMatchingNotification) {
                  // Determine the actual invitation type based on status
                  const invitationStatus = matchingInvitation.status || matchingInvitation.invitation_status;
                  const isPending = invitationStatus === 'pending';
                  const isRejected = invitationStatus === 'rejected' || invitationStatus === 'declined';
                  const isAccepted = invitationStatus === 'accepted';

                  enhancedActivity = {
                    ...foundActivity,
                    isPendingInvitation: isPending,
                    isRejectedInvitation: isRejected,
                    isDeclinedInvitation: isRejected,
                    isAcceptedInvitation: isAccepted,
                    invitation_status: invitationStatus,
                    invitationUuid: matchingInvitation.uuid,
                    invitation_message: matchingInvitation.invitation_message,
                    host_child_name: matchingInvitation.host_child_name,
                    host_parent_name: matchingInvitation.host_parent_username,
                    class_name: matchingInvitation.class_name,
                    class_rep_name: matchingInvitation.class_rep_name,
                    activity_uuid: matchingInvitation.activity_uuid,
                    activity_source: matchingInvitation.activity_source  // pitchero/socs/manual
                  } as any;
                }
              }
            }
          } catch (error) {
            console.error('Failed to check for pending invitations during URL navigation:', error);
          }

          setSelectedActivity(enhancedActivity);
          
          // Populate newActivity state with enhanced activity data
          setNewActivity({
            name: enhancedActivity.name || '',
            description: enhancedActivity.description || '',
            start_date: enhancedActivity.start_date ? enhancedActivity.start_date.split('T')[0] : '',
            end_date: enhancedActivity.end_date ? enhancedActivity.end_date.split('T')[0] : '',
            start_time: enhancedActivity.start_time || '',
            end_time: enhancedActivity.end_time || '',
            location: enhancedActivity.location || '',
            website_url: (enhancedActivity as any).website_url || '',
            contact_phone: (enhancedActivity as any).contact_phone || '',
            contact_email: (enhancedActivity as any).contact_email || '',
            cost: (enhancedActivity as any).cost ? String((enhancedActivity as any).cost) : '',
            max_participants: (enhancedActivity as any).max_participants ? String((enhancedActivity as any).max_participants) : '',
            activity_type: (enhancedActivity as any).activity_type || '',
            auto_notify_new_connections: (enhancedActivity as any).auto_notify_new_connections || false,
            kit_needed: (enhancedActivity as any).kit_needed || []
          });
          
          // Load participants for the activity
          const activityUuid = (enhancedActivity as any).activity_uuid || (enhancedActivity as any).uuid;
          if (activityUuid) {
            loadActivityParticipants(activityUuid);

            // Also load tentative responses if it's a tentative activity and user is host
            if ((enhancedActivity as any).is_tentative && enhancedActivity.is_host) {
              loadTentativeResponses(activityUuid);
            }
          }
          
          setCurrentPage('activity-detail');
        };
        
        checkAndSetActivity();
        
        // Load connections data for activity detail page if the activity is hosted by this child
        if (selectedActivity?.is_host) {
          loadConnectionsData();
        }
      } else {
        // If activity not found in activities array, only reset if no navigation state
        const navActivity = (location.state as any)?.activity;
        if (!navActivity) {
          setSelectedActivity(null);
          setCurrentPage('main');
        } else {
        }
      }
    } else if (!initialActivityUuid) {
      // When no activity UUID in URL, ensure we're on main page
      setSelectedActivity(null);
      setCurrentPage('main');
    }
  }, [initialActivityUuid, activities, pendingInvitations]);

  // Track currentPage state changes for debugging
  useEffect(() => {
  }, [currentPage]);

  // Remove popstate handling - let React Router handle URL changes and Dashboard handle navigation
  // The component should respond to URL changes through props, not manage browser history directly

  // Load activities when needed (lazy load for performance)
  useEffect(() => {
    // Only load calendar activities when viewing the main calendar page
    // Do NOT load if we have an initialActivityUuid (handled separately above)
    if (currentPage === 'main' && activities.length === 0 && !initialActivityUuid) {
      loadActivities(true);
    } else if (currentPage === 'main' && sessionStorage.getItem('invitationViewed') === 'true') {
      // Refresh if returning from viewing invitations
      sessionStorage.removeItem('invitationViewed');
      loadActivities();
      onDataChanged?.();
    }
  }, [currentPage, activities.length, initialActivityUuid, onDataChanged]);

  // Load connections data when viewing activity detail for host activities
  useEffect(() => {
    if (currentPage === 'activity-detail') {
      // Always load account parents for designated parent dropdown
      loadAccountParents();

      // Load connections data only for host activities
      if (selectedActivity?.is_host) {
        loadConnectionsData();
      }
    }
  }, [currentPage, selectedActivity]);

  // No longer blocking content - using immediate scroll restoration instead

  // Generate current date range - optimized for performance
  const getCurrentDateRange = () => {
    // If navigating to a specific activity, load a wider range to ensure we find it
    if (initialActivityUuid) {
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

      const threeMonthsLater = new Date();
      threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);

      const startDate = threeMonthsAgo.toISOString().split('T')[0];
      const endDate = threeMonthsLater.toISOString().split('T')[0];
      return { startDate, endDate };
    }

    // Otherwise use a 2-week window (1 week back, 1 week forward) for fast initial load
    // This loads just enough data for the current week view
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    const oneWeekLater = new Date();
    oneWeekLater.setDate(oneWeekLater.getDate() + 7);

    const startDate = oneWeekAgo.toISOString().split('T')[0];
    const endDate = oneWeekLater.toISOString().split('T')[0];
    return { startDate, endDate };
  };

  // Check if we need to refresh activities due to month change
  const shouldRefreshForDateChange = () => {
    if (!currentDateRange) return true; // First load
    
    const newRange = getCurrentDateRange();
    const currentMonth = currentDateRange.startDate.substring(0, 7); // YYYY-MM
    const newMonth = newRange.startDate.substring(0, 7); // YYYY-MM
    
    const monthChanged = currentMonth !== newMonth;
    if (monthChanged) {
    }
    
    return monthChanged;
  };

  const loadActivities = async (forceRefresh = false) => {
    try {
      // Check if we already have data loaded and it's not a forced refresh
      if (!forceRefresh && activities.length > 0 && !shouldRefreshForDateChange()) {
        return;
      }

      setLoading(true);

      // Clear old batch participants data
      setBatchParticipantsData({});


      // Load child's own activities using calendar endpoint to get status change notifications
      // Get wider date range to include future activities (current date to 1 year ahead)
      const dateRange = getCurrentDateRange();
      const { startDate, endDate } = dateRange;

      // Update the current date range state
      setCurrentDateRange(dateRange);
      
      
      // Load all activities (owned + invited) using the unified activities endpoint
      const activitiesResponse = await apiService.getCalendarActivities(startDate, endDate);
      
      if (activitiesResponse.success && activitiesResponse.data) {
        const allActivities = Array.isArray(activitiesResponse.data) ? activitiesResponse.data : [];

        // Filter activities for this specific child using UUIDs only (secure)
        // Include activities where:
        // 1. Child owns the activity (child_uuid matches this child's UUID)
        // 2. Child is invited to the activity (invited_child_uuid matches AND invitation_status is pending/accepted)
        const childActivities = allActivities.filter(activity => {
          const ownsActivity = activity.child_uuid === child.uuid;
          const isInvited = activity.invited_child_uuid === child.uuid && 
                           activity.invitation_status && 
                           activity.invitation_status !== 'none';
          const shouldInclude = ownsActivity || isInvited;
          
          
          return shouldInclude;
        });
        
        
        // Also load rejected invitations to include them as grey activities
        const invitationsResponse = await apiService.getAllInvitations(startDate, endDate);
        
        if (invitationsResponse.success && invitationsResponse.data) {
          const rejectedInvitations = invitationsResponse.data.filter(
            invitation => invitation.status === 'rejected' && 
                         invitation.invited_child_name === child.name
          );
          
          
          // Convert rejected invitations to activity format and add them
          const rejectedActivities = rejectedInvitations.map(invitation => ({
            activity_uuid: invitation.activity_uuid,
            name: invitation.activity_name,
            description: invitation.activity_description,
            start_date: invitation.start_date,
            end_date: invitation.end_date,
            start_time: invitation.start_time,
            end_time: invitation.end_time,
            location: invitation.location,
            website_url: invitation.website_url,
            cost: invitation.cost,
            child_name: invitation.child_name,
            child_uuid: invitation.child_uuid,
            is_host: false,
            is_shared: true,
            invitation_status: 'rejected',
            invited_child_uuid: child.uuid,
            invitationUuid: invitation.uuid,
            isRejectedInvitation: true,
            host_parent_name: invitation.host_parent_username
          }));
          
          // Merge rejected invitations with existing activities
          const allActivitiesWithRejected = [...childActivities, ...rejectedActivities];

          setActivities(allActivitiesWithRejected);

          // Participants will be loaded on-demand when viewing activity details
        } else {
          setActivities(childActivities);

          // Participants will be loaded on-demand when viewing activity details
        }
      } else {
        setActivities([]);
      }
    } catch (error) {
      console.error('Failed to load activities:', error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInvitationResponse = async (invitationUuid: string, action: 'accept' | 'reject') => {
    try {
      
      // First, let's make the API call to get the invitation details
      // Since the activities structure may not have complete invitation data,
      // we'll use the activities to identify series based on activity names
      
      // For now, let's focus on finding activities with the same name and pending status
      // and use the series_id approach once we have the invitation details
      
      // Load fresh invitations data to get series information
      const today = new Date();
      const oneYearLater = new Date();
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
      const startDate = today.toISOString().split('T')[0];
      const endDate = oneYearLater.toISOString().split('T')[0];
      
      const invitationsResponse = await apiService.getAllInvitations(startDate, endDate);
      
      if (invitationsResponse.success && invitationsResponse.data) {
        // Find the current invitation by UUID
        const currentInvitation = invitationsResponse.data.find((inv: any) => 
          inv.invitation_uuid === invitationUuid
        );
        
        // console.log(`🔍 Current invitation found in fresh data:`, currentInvitation);
        
        if (currentInvitation) {
          const seriesId = currentInvitation.series_id;
          const activityName = currentInvitation.activity_name;
          const activitySource = currentInvitation.activity_source;

          // Check if this is a Pitchero/SOCS activity
          const isPitcheroOrSocs = activitySource === 'pitchero' || activitySource === 'socs';

          // Pitchero training activities with series_id starting with 'pitchero_training_' ARE recurring
          // Pitchero matches (fixtures) are NOT recurring - they don't have series_id
          const isPitcheroTrainingSeries = isPitcheroOrSocs && seriesId && typeof seriesId === 'string' && seriesId.startsWith('pitchero_training_');

          // console.log(`🔍 Current invitation details:`);
          // console.log(`🔍 - Name: ${activityName}`);
          // console.log(`🔍 - Series ID: ${seriesId}`);
          // console.log(`🔍 - Status: ${currentInvitation.status}`);
          // console.log(`🔍 - Source: ${activitySource}`);

          // Find all related invitations for this child
          let seriesInvitations = [];

          // Allow recurring series for:
          // 1. Non-Pitchero/SOCS activities with valid series_id
          // 2. Pitchero training activities with pitchero_training_ series_id
          if (seriesId && seriesId !== 'undefined' && seriesId !== null && (!isPitcheroOrSocs || isPitcheroTrainingSeries)) {
            // This is a recurring series (regular recurring OR Pitchero training)
            seriesInvitations = invitationsResponse.data.filter((inv: any) =>
              inv.series_id === seriesId &&
              inv.invited_child_id === currentInvitation.invited_child_id &&
              inv.status === 'pending' // Only pending invitations can be accepted/declined
            );
            // console.log(`🔍 Found ${seriesInvitations.length} pending invitations in series using series_id: ${seriesId}`);
          } else {
            // No series_id OR Pitchero/SOCS matches mean this is a single activity invitation
            // Even if multiple children are invited, each child accepts individually
            seriesInvitations = [currentInvitation];
            // console.log(`🔍 Single activity invitation - no series_id found or Pitchero/SOCS match, treating as individual invitation`);
          }
          
          // console.log(`🔍 Series invitations:`, seriesInvitations.map(inv => ({
          //   name: inv.activity_name,
          //   invitation_uuid: inv.invitation_uuid,
          //   start_date: inv.start_date,
          //   status: inv.status,
          //   series_id: inv.series_id
          // })));
          
          if (seriesInvitations.length > 1) {
            // This is a recurring series - show modal for accept, existing decline modal for decline
            if (action === 'accept') {
              // Show accept dialog modal
              setAcceptDialogData({
                invitationUuid,
                activityName,
                sessionCount: seriesInvitations.length,
                seriesInvitations,
                currentInvitation
              });
              setAcceptOption('single'); // Reset to default
              setShowAcceptDialog(true);
              return; // Exit early, modal will handle the rest
            }

            // For decline, show decline dialog modal
            if (action === 'reject') {
              setDeclineDialogData({
                invitationUuid,
                activityName,
                sessionCount: seriesInvitations.length,
                seriesInvitations,
                currentInvitation
              });
              setDeclineInvitationOption('single'); // Reset to default
              setShowDeclineDialog(true);
              return; // Exit early, modal will handle the rest
            }

            // This code below shouldn't be reached for accept/reject, but keeping as fallback
            const acceptSeries = window.confirm(`This is a recurring activity "${activityName}" with ${seriesInvitations.length} pending sessions. Do you want to decline the entire series?\n\nClick OK for entire series, Cancel for just this activity.`);

            if (acceptSeries) {
              // Decline the entire series - since backend handles bulk update,
              // we only need to respond to the first invitation

              const firstInvitation = seriesInvitations[0];
              const response = await apiService.respondToActivityInvitation(firstInvitation.invitation_uuid, action);

              const successCount = response.success ? seriesInvitations.length : 0;

              if (successCount > 0) {
                const message = `Series declined. ${successCount} invitations removed.`;
                alert(message);

                // No club usage increment for declined invitations
                if (false) {
                  try {
                    const acceptedInvitations = seriesInvitations;
                    
                    
                    for (const invitation of acceptedInvitations) {
                      // console.log('🎯 SERIES ACCEPT DEBUG: Processing invitation for club usage:', invitation);
                      
                      const clubUsageData = {
                        website_url: invitation.website_url,
                        activity_type: invitation.activity_type,
                        location: invitation.location || undefined,
                        child_age: child.age || undefined,
                        activity_start_date: invitation.start_date,
                        activity_uuid: invitation.activity_uuid
                      };
                      
                      
                      
                      if (clubUsageData.website_url && clubUsageData.activity_type) {
                        await apiService.incrementClubUsage(clubUsageData);
                      } else {
                        console.warn('❌ SERIES ACCEPT: Missing required data for club usage:', {
                          website_url: clubUsageData.website_url,
                          activity_type: clubUsageData.activity_type
                        });
                      }
                    }
                  } catch (clubError) {
                    console.warn('Error in series club increment:', clubError);
                    // Don't fail invitation acceptance if club increment fails
                  }
                }
                
                // Reload activities to update the display
                loadActivities();
                
                // Close the detail modal and refresh parent data
                goBack();
                onDataChanged?.();
              } else {
                alert(`Error: Failed to ${action} series invitations`);
              }
              
              return;
            }
          }
        }
      }
      
      // Handle single invitation (either non-recurring or user chose single)
      const response = await apiService.respondToActivityInvitation(invitationUuid, action);
      
      if (response.success) {
        const message = action === 'accept' 
          ? 'Invitation accepted! Activity will appear in your calendar.' 
          : 'Invitation declined.';
        alert(message);
        
        // Refresh invitation data immediately to update button state
        if (selectedActivity) {
          const activityUuid = (selectedActivity as any).activity_uuid || (selectedActivity as any).uuid;
          if (activityUuid) {
            // Reload participants to get updated invitation status
            loadActivityParticipants(activityUuid);
          }
        }

        // If invitation was accepted, increment club usage for this guest
        if (action === 'accept') {
          try {
            
            // Find the activity details from the current invitation
            const today = new Date();
            const oneYearLater = new Date();
            oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
            const startDate = today.toISOString().split('T')[0];
            const endDate = oneYearLater.toISOString().split('T')[0];
            
            const invitationsResponse = await apiService.getAllInvitations(startDate, endDate);
            if (invitationsResponse.success && invitationsResponse.data) {
              
              
              // Try multiple field names for the invitation UUID
              const currentInvitation = invitationsResponse.data.find((inv: any) => 
                inv.uuid === invitationUuid || 
                inv.invitation_uuid === invitationUuid ||
                inv.id === invitationUuid
              );
              
              if (currentInvitation) {
                
                // The invitation object has the activity details we need
                const clubUsageData = {
                  website_url: currentInvitation.website_url || (selectedActivity as any)?.website_url,
                  activity_type: currentInvitation.activity_type || (selectedActivity as any)?.activity_type,
                  location: currentInvitation.location || selectedActivity?.location,
                  child_age: child.age || undefined,
                  activity_start_date: currentInvitation.start_date || selectedActivity?.start_date,
                  activity_uuid: currentInvitation.activity_uuid || (selectedActivity as any)?.activity_uuid
                };
                
                
                
                if (clubUsageData.website_url && clubUsageData.activity_type) {
                  await apiService.incrementClubUsage(clubUsageData);
                } else {
                  console.warn('❌ INVITATION ACCEPT: Missing required data for club usage increment:', {
                    website_url: clubUsageData.website_url,
                    activity_type: clubUsageData.activity_type
                  });
                }
              } else {
                console.warn('Could not find current invitation in API response');
              }
            } else {
              console.warn('Failed to get invitations from API');
            }
          } catch (clubError) {
            // Don't fail invitation acceptance if club increment fails
          }
        }
        
        // Close the detail modal and refresh parent data immediately
        goBack();
        onDataChanged?.();

        // Reload activities in the background to update the display
        setTimeout(() => {
          loadActivities();
        }, 100);
      } else {
        alert(`Error: ${response.error || 'Failed to respond to invitation'}`);
      }
    } catch (error) {
      console.error('Failed to respond to invitation:', error);
      alert('Failed to respond to invitation');
    }
  };

  const handleStatusChangeClicked = async (activity: Activity) => {
    // Show participants to see status changes, then mark all as viewed
    try {
      const activityUuid = (activity as any).activity_uuid || (activity as any).uuid;
      
      // Use batch cache data instead of individual API call
      const cachedData = batchParticipantsData[activityUuid];
      if (cachedData && cachedData.success && cachedData.data) {
        setActivityParticipants(cachedData.data);
        
        // Mark all unviewed status changes as viewed for this activity
        // Find the invitation ID from the participants data and mark it as viewed
        if (cachedData.data.participants && cachedData.data.participants.length > 0) {
          const invitationWithStatusChange = cachedData.data.participants.find((p: any) => p.invitation_uuid);
          if (invitationWithStatusChange && invitationWithStatusChange.invitation_uuid) {
            await apiService.markStatusChangeAsViewed(invitationWithStatusChange.invitation_uuid);
          }
        }
        
        // Reload activities to hide the notification icon
        setTimeout(async () => {
          await loadActivities();
        }, 500);
      }
    } catch (error) {
      console.error('Failed to load participants:', error);
    }
  };

  // Load kit tracking data for an activity
  const loadKitTracking = async (activityUuid: string) => {
    try {
      setLoadingKit(true);
      const response = await apiService.getKitTracking(activityUuid);

      if (response.success && response.data) {
        setKitTracking(response.data);
      }
    } catch (error) {
      console.error('Failed to load kit tracking:', error);
    } finally {
      setLoadingKit(false);
    }
  };

  // Toggle kit item packed status
  const handleToggleKitItem = (itemName: string, isPacked: boolean) => {
    if (!selectedActivity) return;

    const activityUuid = selectedActivity.uuid || (selectedActivity as any).activity_uuid;
    const childUuid = selectedActivity.child_uuid || (selectedActivity as any).child_uuid;
    if (!activityUuid || !childUuid) {
      console.error('No activity UUID or child UUID found');
      return;
    }

    // Update local state only - will be saved when Save button is clicked
    const newPackedState = !isPacked;
    const trackingKey = `${activityUuid}-${childUuid}`;
    setSelectedActivity((prev: any) => {
      if (!prev) return prev;
      const updatedKitTracking = { ...(prev.kit_tracking || {}) };
      if (!updatedKitTracking[trackingKey]) {
        updatedKitTracking[trackingKey] = {};
      }
      updatedKitTracking[trackingKey][itemName] = newPackedState;
      return { ...prev, kit_tracking: updatedKitTracking };
    });
  };

  // Add custom kit item
  const handleAddCustomKitItem = async () => {
    if (!selectedActivity || !newKitItem.trim()) return;

    try {
      const activityUuid = selectedActivity.uuid || selectedActivity.activity_uuid;
      if (!activityUuid) {
        console.error('No activity UUID found');
        return;
      }
      const hasSeriesId = (selectedActivity as any).series_id;

      let applyToSeries = false;
      if (hasSeriesId) {
        applyToSeries = window.confirm('This is a recurring activity. Apply this item to all dates in the series?');
      }

      await apiService.addCustomKitItem(activityUuid, newKitItem.trim(), applyToSeries);

      // Reload kit tracking
      await loadKitTracking(activityUuid);
      setNewKitItem('');
    } catch (error) {
      console.error('Failed to add custom kit item:', error);
      alert('Failed to add kit item');
    }
  };

  // Remove custom kit item
  const handleRemoveCustomKitItem = async (itemName: string) => {
    if (!selectedActivity) return;

    try {
      const activityUuid = selectedActivity.uuid || selectedActivity.activity_uuid;
      if (!activityUuid) {
        console.error('No activity UUID found');
        return;
      }
      await apiService.removeCustomKitItem(activityUuid, itemName);

      // Reload kit tracking
      await loadKitTracking(activityUuid);
    } catch (error) {
      console.error('Failed to remove custom kit item:', error);
      alert('Failed to remove kit item');
    }
  };

  // Helper function to generate unique pending IDs
  const generatePendingChildId = (request: any) => {
    // Use target_child_uuid if available (works for both regular and skeleton connections)
    if (request.target_child_uuid) {
      return `pending-child-${request.target_child_uuid}`;
    } else if (request.is_skeleton) {
      // Fallback to skeleton format for old data without target_child_uuid
      return `pending-child-skeleton-${request.id}`;
    } else {
      // Fallback to parent UUID
      return `pending-child-${request.target_parent_uuid}`;
    }
  };

  // Helper function to extract the actual ID from a pending-child string
  const extractPendingId = (pendingChildStr: string) => {
    if (pendingChildStr.startsWith('pending-child-')) {
      return pendingChildStr.replace('pending-child-', '');
    }
    return pendingChildStr;
  };

  // Helper function to check if an ID is a pending connection
  const isPendingChildId = (id: string) => {
    return id.startsWith('pending-child-') || id.startsWith('main-pending-child-');
  };

  const loadRepClasses = async () => {
    try {
      const response = await apiService.getClasses();
      setRepClasses(response.data || []);
    } catch (error) {
      console.error('Failed to load classes:', error);
      setRepClasses([]);
    }
  };

  const loadConnectionsData = async () => {
    try {
      // Load both regular connections and sent requests (including skeleton connections)
      const [connectionsResponse, sentRequestsResponse] = await Promise.all([
        apiService.getChildConnections(child.uuid),
        apiService.getSentConnectionRequests()
      ]);

      const connectedChildren: any[] = [];
      const pendingRequests: any[] = [];

      // Process regular connections
      if (connectionsResponse.success && connectionsResponse.data) {
        connectionsResponse.data.forEach((item: any, index: number) => {

          // Check for status field to determine if connected or pending
          const status = item.status || item.connection_status || 'accepted'; // Default to accepted if no status

          if (status === 'accepted' || status === 'connected' || status === 'active') {
            connectedChildren.push({
              id: item.connected_child_uuid || item.child_uuid || item.uuid,
              uuid: item.connected_child_uuid || item.child_uuid || item.uuid,
              name: item.connected_child_name || item.child_name || item.name,
              parentName: item.connected_parent_name || item.parent_name || item.parentname,
              parentUuid: item.connected_parent_uuid || item.parent_uuid || item.parentuuid,
              family_name: item.connected_family_name || item.family_name
            });
          } else if (status === 'pending') {
            // Skip skeleton connections here - they'll be handled by getSentConnectionRequests
            if (item.is_skeleton !== true) {
              pendingRequests.push({
                id: item.id,
                target_child_name: item.connected_child_name || item.child_name || item.name,
                target_parent_name: item.connected_parent_name || item.parent_name,
                target_child_uuid: item.connected_child_uuid || item.child_uuid || item.uuid,
                target_parent_uuid: item.connected_parent_uuid || item.parent_uuid,
                target_family_name: item.connected_family_name || item.family_name,
                status: 'pending'
              });
            }
          }
        });
      }

      // Process sent requests (including skeleton connections)
      if (sentRequestsResponse.success && sentRequestsResponse.data) {

        sentRequestsResponse.data.forEach((request: any, index: number) => {

          // Add skeleton connections as pending requests
          if (request.is_skeleton === true && request.status === 'pending') {
            const requestId = request.request_uuid || request.uuid || request.id || request.request_id;

            // Generate a unique ID if the backend doesn't provide one
            // Use UUID if available, otherwise create from contact method + child name
            const uniqueId = requestId ||
              `${request.contact_method || request.target_parent_email || request.target_parent_phone}-${request.target_child_name}`.replace(/[^a-zA-Z0-9]/g, '');

            const skeletonRequest = {
              id: uniqueId,
              target_child_name: request.target_child_name,
              target_parent_name: request.target_parent_name || request.target_family_name,
              target_child_uuid: request.target_child_uuid,
              target_parent_uuid: request.target_parent_uuid,
              target_family_name: request.target_family_name,
              status: 'pending',
              is_skeleton: true,
              contact_method: request.contact_method || request.target_parent_email || request.target_parent_phone
            };

            pendingRequests.push(skeletonRequest);
          }
        });
      }


      setConnectedChildren(connectedChildren);
      setPendingConnectionRequests(pendingRequests);

      // Extract unique connected parents for the responsible parent dropdown
      const uniqueParents = new Map<string, {uuid: string, name: string}>();
      connectedChildren.forEach((child: any) => {
        if (child.parentUuid && child.parentName) {
          uniqueParents.set(child.parentUuid, {
            uuid: child.parentUuid,
            name: child.parentName
          });
        }
      });
      setConnectedParents(Array.from(uniqueParents.values()));
    } catch (error) {
      console.error('❌ Failed to load connections data:', error);
      setConnectedChildren([]);
      setPendingConnectionRequests([]);
      setConnectedParents([]);
    }
  };


  const handleAddActivity = () => {
    // Check if there's already a draft - if not, reset form state
    if (!activityDraft) {
      setNewActivity(getInitialActivityState());
      setSelectedDates([]);
      setIsSharedActivity(false);
      setAutoNotifyNewConnections(false);
      setSelectedClassUuids([]);
      setSelectedConnectedChildren([]);
      setJointHostChildren([]);
      // Reset participant selection based on navigation state
      const navState = (location.state as any);
      if (navState?.isParentActivity && navState?.parentUuid) {
        // Coming from parent activity selection
        setSelectedParticipantChildUuids([]);
        setSelectedParticipantParentUuids([navState.parentUuid]);
      } else {
        // Default: select the current child
        setSelectedParticipantChildUuids([child.uuid]);
        setSelectedParticipantParentUuids([]);
      }
    }
    
    // Ensure parent children data is loaded for joint hosting
    if (parentChildren.length === 0) {
      loadParentChildren();
    } else {
    }
    
    // Create a UUID-based URL for activity creation using special "new" identifier
    if (onNavigateToActivity) {
      const newActivityPlaceholder = {
        uuid: 'new',
        activity_uuid: 'new', 
        name: 'New Activity',
        id: null
      };
      onNavigateToActivity(child, newActivityPlaceholder);
    } else {
      // Fallback to old behavior
      navigateToPage('add-activity');
    }
  };

  const loadActivityParticipants = async (activityId: string) => {
    try {
      setLoadingParticipants(true);
      
      // First check if we have cached batch data for this activity
      if (batchParticipantsData[activityId]) {
        const cachedData = batchParticipantsData[activityId];
        
        if (cachedData.success && cachedData.data) {
          setActivityParticipants(cachedData.data);

          // Store invitation data separately if present AND it's for the current child
          if (cachedData.data.user_invitation) {
            const invitation = cachedData.data.user_invitation;

            // Only use this invitation if it's for the current child
            if (invitation.invited_child_uuid === child.uuid) {
              const invitationInfo = {
                isPendingInvitation: invitation.status === 'pending',
                isAcceptedInvitation: invitation.status === 'accepted',
                isDeclinedInvitation: invitation.status === 'declined' || invitation.status === 'rejected',
                invitationUuid: invitation.invitation_uuid || invitation.uuid,
                invitation_message: invitation.invitation_message
              };

              setInvitationData(invitationInfo);
            } else {
              // Don't clear invitation data - keep navigation state
            }
          } else {
            // Don't clear invitation data - preserve navigation state
          }
          setLoadingParticipants(false);
          return;
        }
      }
      
      // If not in batch cache, load individually (pass child UUID)
      console.warn('⚠️ Activity participants not found in batch cache for:', activityId, 'loading individually...');

      const response = await apiService.getActivityParticipants(activityId, child.uuid);

      if (response.success && response.data) {
        setActivityParticipants(response.data);

        // Store invitation data separately if present AND it's for the current child
        if (response.data.user_invitation) {
          const invitation = response.data.user_invitation;

          // Only use this invitation if it's for the current child
          if (invitation.invited_child_uuid === child.uuid) {
            const invitationInfo = {
              isPendingInvitation: invitation.status === 'pending',
              isAcceptedInvitation: invitation.status === 'accepted',
              isDeclinedInvitation: invitation.status === 'declined' || invitation.status === 'rejected',
              invitationUuid: invitation.invitation_uuid || invitation.uuid,
              invitation_message: invitation.invitation_message
            };

            setInvitationData(invitationInfo);
          } else {
            // Don't clear invitation data - keep navigation state
          }
        } else {
          // Don't clear invitation data if it was initialized from navigation state
          // This preserves the status passed from calendar
        }
      } else {
        console.error('❌ Failed to load individual participants:', response);
        setActivityParticipants(null);
        // Don't clear invitation data if it was initialized from navigation state
      }
    } catch (error) {
      console.error('💥 Error loading activity participants:', error);
      setActivityParticipants(null);
    } finally {
      setLoadingParticipants(false);
    }
  };

  // Load tentative responses for tentative activities
  const loadTentativeResponses = async (activityUuid: string) => {
    try {
      console.log('🔍 LOADING TENTATIVE RESPONSES for activity:', activityUuid);
      setLoadingTentativeResponses(true);
      const response = await apiService.getTentativeResponses(activityUuid);
      console.log('🔍 TENTATIVE RESPONSE from API:', response);
      if (response.success) {
        console.log('🔍 Setting tentative responses to:', response.data);
        setTentativeResponses(response.data);
      } else {
        console.error('❌ Failed to load tentative responses:', response.error);
        setTentativeResponses(null);
      }
    } catch (error) {
      console.error('💥 Error loading tentative responses:', error);
      setTentativeResponses(null);
    } finally {
      setLoadingTentativeResponses(false);
    }
  };

  // Template Loading Functions
  const loadActivityTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await apiService.getActivityTemplates();
      if (response.success) {
        setActivityTemplates(response.data || []);
      } else {
        console.error('❌ Failed to load activity templates:', response.error);
        setActivityTemplates([]);
      }
    } catch (error) {
      console.error('💥 Error loading activity templates:', error);
      setActivityTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadActivityTypes = async () => {
    try {
      const response = await apiService.getActivityTypes();
      if (response.success) {
        setActivityTypes(response.data || []);
      } else {
        console.error('❌ Failed to load activity types:', response.error);
        setActivityTypes([]);
      }
    } catch (error) {
      console.error('💥 Error loading activity types:', error);
      setActivityTypes([]);
    }
  };

  const loadClubs = async () => {
    try {
      setLoadingClubs(true);
      const response = await apiService.getClubs();
      if (response.success && response.data) {
        setClubs(response.data);
      }
    } catch (error) {
      console.error('Failed to load clubs:', error);
    } finally {
      setLoadingClubs(false);
    }
  };

  const checkForClubSelection = () => {
    try {

      // Scroll restoration is now handled in useState initializer

      const urlParams = new URLSearchParams(window.location.search);
      const selectedClubData = urlParams.get('clubData');
      const clubCancel = urlParams.get('clubCancel');


      // Check if we're returning from club browsing (either with selection or cancellation)
      const isReturningFromClubs = selectedClubData || clubCancel;

      if (isReturningFromClubs) {
        const draftKey = `activityDraft_${child.uuid}`;
        const savedDraft = localStorage.getItem(draftKey);
        let restoredActivityData = getInitialActivityState();


        if (savedDraft) {
          const draft = JSON.parse(savedDraft);

          // Restore the activity form state
          restoredActivityData = draft.newActivity || getInitialActivityState();
          setSelectedDates(draft.selectedDates || []);
          setIsSharedActivity(draft.isSharedActivity || false);
          setAutoNotifyNewConnections(draft.autoNotifyNewConnections || false);
          setSelectedConnectedChildren(draft.selectedConnectedChildren || []);
          setIsRecurringActivity(draft.isRecurringActivity || false);
          setRecurringDaysOfWeek(draft.recurringDaysOfWeek || []);
          setRecurringStartDate(draft.recurringStartDate || '');
          setRecurringEndDate(draft.recurringEndDate || '');

          // Trigger the add activity modal to show
          setCurrentPage('add-activity');


          // If a club was selected, apply club data to the restored form data
          if (selectedClubData) {
            const clubData = JSON.parse(decodeURIComponent(selectedClubData));
            const fieldType = draft.fieldTypeForReturn;

            // Preserve the original activity name from the draft
            const originalActivityName = restoredActivityData.name;

            // Apply club data to the restored activity data
            const updates: any = {};

            // Always set location and activity_type from club data
            // For location: use club location if available, otherwise keep the existing location
            if (clubData.location && clubData.location.trim()) {
              updates.location = clubData.location;
            }

            if (clubData.activity_type) {
              updates.activity_type = clubData.activity_type;
            }

            // Set venue from club data
            if (clubData.venue && clubData.venue.trim()) {
              updates.venue = clubData.venue;
            }

            // Set the specific field that was browsed for
            if (fieldType === 'website_url' && clubData.website_url) {
              updates.website_url = clubData.website_url;
            }

            // Also populate contact fields from club data
            if (clubData.contact_phone) {
              updates.contact_phone = clubData.contact_phone;
            }

            if (clubData.contact_email) {
              updates.contact_email = clubData.contact_email;
            }

            // PRESERVE the user's activity name - do NOT overwrite with club title
            // Only use club name if the user hadn't set an activity name yet
            if (!originalActivityName || originalActivityName.trim() === '') {
              if (clubData.website_title && clubData.website_title.trim()) {
                updates.name = clubData.website_title;
              } else if (clubData.name) {
                updates.name = clubData.name;
              }
            }

            // Set club UUID and club object for club name display
            if (clubData.uuid) {
              updates.club_uuid = clubData.uuid;
              updates.club = {
                uuid: clubData.uuid,
                name: clubData.name,
                location: clubData.location,
                venue: clubData.venue,
                website_url: clubData.website_url,
                activity_type: clubData.activity_type
              };
              console.log('🏢 Club data applied from URL params:', {
                clubName: clubData.name,
                clubUuid: clubData.uuid,
                fullClubObject: updates.club
              });
            }

            // Apply updates to the restored data
            restoredActivityData = { ...restoredActivityData, ...updates };
          }

          // Scroll restoration is now handled by pre-render script in index.html

          // Clean up the draft from localStorage
          localStorage.removeItem(draftKey);
        } else {
          // No saved draft found
        }

        // Now set the final activity data (either restored draft + club data, or just restored draft)
        setNewActivity(restoredActivityData);

        // If this was just a cancel (no club data), clean URL and we're done
        if (clubCancel && !selectedClubData) {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, '', cleanUrl);
          return; // Exit early for cancel case
        }

        // Clean URL for club selection case
        if (selectedClubData) {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, '', cleanUrl);
        }
      } else {
        // Not returning from clubs
      }
    } catch (error) {
      console.error('Error processing club selection:', error);
    }
  };

  const checkForConnectionUpdates = () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const connectionUpdate = urlParams.get('connectionUpdate');

      if (connectionUpdate === 'true') {

        // Reload connection data to pick up newly created skeleton connections
        setTimeout(() => {
          loadConnectionsData();
        }, 500);

        // Clean URL
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
      }
    } catch (error) {
      console.error('Error checking for connection updates:', error);
    }
  };

  // Helper function to create split input field (text input + optional club browse button)
  const renderClubSplitInput = (
    value: string,
    onChange: (value: string) => void,
    placeholder: string,
    fieldType: 'location' | 'website_url',
    className: string = 'modal-input',
    showBrowseButton: boolean = true
  ) => {
    const handleBrowseClubs = () => {
      // Save scroll position and open modal instead of navigating
      const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      setScrollPosition(currentScrollPosition);
      setClubSelectionFieldType(fieldType);
      setIsClubsModalOpen(true);


      // Force a re-render by using setTimeout
      setTimeout(() => {
        setIsClubsModalOpen(prev => {
          return true;
        });
      }, 0);

      // Prevent background scrolling
      document.body.style.overflow = "hidden";
    };

    if (!showBrowseButton) {
      // Return regular input without browse button
      return (
        <input
          type={fieldType === 'website_url' ? 'url' : 'text'}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            const inputValue = e.target.value;
            onChange(inputValue);
            
            // Auto-set location to user's town_city when typing website URL
            if (fieldType === 'website_url' && inputValue.trim() && !newActivity.location.trim() && user?.town_city) {
              setNewActivity(prev => ({...prev, location: user.town_city || ''}));
            }
          }}
          className={className}
        />
      );
    }

    return (
      <div className="club-split-input">
        <div className="split-input-container">
          <input
            type={fieldType === 'website_url' ? 'url' : 'text'}
            placeholder={placeholder}
            value={value}
            onChange={(e) => {
              const inputValue = e.target.value;
              onChange(inputValue);
              
              // Auto-set location to user's town_city when typing website URL
              if (fieldType === 'website_url' && inputValue.trim() && !newActivity.location.trim() && user?.town_city) {
                setNewActivity(prev => ({...prev, location: user.town_city || ''}));
              }
            }}
            className={`${className} split-text-input`}
          />
          <button
            type="button"
            onClick={handleBrowseClubs}
            className="browse-clubs-btn"
            title={`Browse clubs for ${fieldType === 'location' ? 'location' : 'website'}`}
          >
            🔍 Browse
          </button>
        </div>
      </div>
    );
  };

  const loadParentChildren = async () => {
    try {
      const response = await apiService.getChildren();

      if (response.success && response.data) {
        const allChildren = Array.isArray(response.data) ? response.data : [];

        // Include all children for joint hosting selection

        setParentChildren(allChildren);
      } else {
        console.error('❌ Failed to load parent children:', response.error || 'API returned unsuccessful response');
        setParentChildren([]);
      }
    } catch (error) {
      console.error('💥 Error loading parent children:', error);
      console.error('Full error details:', error);
      setParentChildren([]);
    }
  };

  const loadAccountParents = async () => {
    try {
      const response = await apiService.getParents();
      console.log('👨‍👩‍👧 Parents API response:', response);

      if (response.success && response.data) {
        const parents = response.data.map((parent: any) => ({
          uuid: parent.uuid,
          name: parent.username || parent.email || 'Parent',
          is_primary: parent.is_primary
        }));
        console.log('👨‍👩‍👧 Processed parents:', parents);
        setAccountParents(parents);
      } else {
        console.error('❌ Failed to load account parents:', response.error);
        setAccountParents([]);
      }
    } catch (error) {
      console.error('💥 Error loading account parents:', error);
      setAccountParents([]);
    }
  };

  const loadJointHostConnections = async (childUuid: string) => {
    try {
      const response = await apiService.getChildConnections(childUuid);
      
      if (response.success && response.data) {
        const connectedChildren: any[] = [];
        const pendingRequests: any[] = [];
        
        response.data.forEach((item: any, index: number) => {
          
          // Check for status field to determine if connected or pending
          const status = item.status || item.connection_status || 'accepted'; // Default to accepted if no status
          
          if (status === 'accepted' || status === 'connected' || status === 'active') {
            connectedChildren.push({
              id: item.connected_child_uuid || item.child_uuid || item.uuid,
              uuid: item.connected_child_uuid || item.child_uuid || item.uuid, 
              name: item.connected_child_name || item.child_name || item.name,
              parentName: item.connected_parent_name || item.parent_name || item.parentname,
              parentUuid: item.connected_parent_uuid || item.parent_uuid || item.parentuuid,
              family_name: item.connected_family_name || item.family_name,
              status: status
            });
          } else if (status === 'pending') {
            connectedChildren.push({
              id: item.connected_child_uuid || item.child_uuid || item.uuid,
              uuid: item.connected_child_uuid || item.child_uuid || item.uuid, 
              name: item.connected_child_name || item.child_name || item.name,
              parentName: item.connected_parent_name || item.parent_name || item.parentname,
              parentUuid: item.connected_parent_uuid || item.parent_uuid || item.parentuuid,
              family_name: item.connected_family_name || item.family_name,
              status: 'pending'
            });
          }
        });
        
        
        setJointHostConnections(prev => ({
          ...prev,
          [childUuid]: connectedChildren
        }));
      } else {
        console.error(`❌ Failed to load connections for child ${childUuid}:`, response.error);
        setJointHostConnections(prev => ({
          ...prev,
          [childUuid]: []
        }));
      }
    } catch (error) {
      console.error(`💥 Error loading connections for child ${childUuid}:`, error);
      setJointHostConnections(prev => ({
        ...prev,
        [childUuid]: []
      }));
    }
  };

  const handleTemplateSelect = (template: any) => {
    if (template === null) {
      // "Create from scratch" selected
      setSelectedTemplate(null);
      setTemplateConfirmationMessage('');
      // Reset form to empty state
      setNewActivity(getInitialActivityState());
      setSelectedDates([]);
      setIsSharedActivity(false);
      setAutoNotifyNewConnections(false);
      setJointHostChildren([]);
      clearActivityDraft();
      return;
    }

    setSelectedTemplate(template);
    setTemplateConfirmationMessage(`Template "${template.name}" loaded successfully!`);
    
    // Pre-fill the activity form with template data
    const newActivityData: any = {
      name: template.name || '',
      description: template.description || '',
      start_date: '',
      end_date: '',
      start_time: template.typical_start_time || '',
      end_time: template.typical_start_time ?
        // Add typical duration to start time if available
        (template.typical_duration_hours ?
          addHoursToTime(template.typical_start_time, template.typical_duration_hours) :
          template.typical_start_time) : '',
      location: template.location || user?.town_city || '',
      website_url: template.website_url || '',
      cost: template.cost ? template.cost.toString() : '',
      max_participants: template.max_participants ? template.max_participants.toString() : '',
      activity_type: template.activity_type || '',
      auto_notify_new_connections: false,
      kit_needed: template.kit_needed || []
    };

    // Restore club information if template has a club_uuid
    if (template.club_uuid) {
      newActivityData.club_uuid = template.club_uuid;
      // If template has full club data, restore it
      if (template.club) {
        newActivityData.club = template.club;
      }
    }

    setNewActivity(newActivityData);
    
    setSelectedDates([]);
    setIsSharedActivity(false);
    setAutoNotifyNewConnections(false);
    setJointHostChildren([]);
    clearActivityDraft(); // Clear any saved draft since we're using a template
    
    // Track template usage
    apiService.useActivityTemplate(template.uuid).catch(error => {
      console.error('Failed to track template usage:', error);
    });
    
    // Clear the confirmation message after 3 seconds
    setTimeout(() => {
      setTemplateConfirmationMessage('');
    }, 3000);
  };

  const addHoursToTime = (timeString: string, hours: number): string => {
    if (!timeString) return '';
    const [hourStr, minuteStr] = timeString.split(':');
    const totalMinutes = (parseInt(hourStr) + hours) * 60 + parseInt(minuteStr || '0');
    const newHour = Math.floor(totalMinutes / 60) % 24;
    const newMinute = totalMinutes % 60;
    return `${newHour.toString().padStart(2, '0')}:${newMinute.toString().padStart(2, '0')}`;
  };

  const handleStartFromScratch = () => {
    handleTemplateSelect(null);
  };

  const markActivityNotificationsAsRead = async (activity: any) => {
    // Get notifications for this activity
    const activityNotifications: any[] = []; // Notifications disabled for this screen
    
    if (activityNotifications.length > 0) {
      
      // Mark all notifications for this activity as read
      for (const notification of activityNotifications) {
        try {
          await apiService.markNotificationAsRead(notification.id);
        } catch (error) {
          console.error('Failed to mark notification as read:', error);
        }
      }
      
      // Notifications disabled for this screen
    }
  };

  const handleActivityClick = async (activity: Activity) => {
    // Mark notifications as read when viewing activity
    await markActivityNotificationsAsRead(activity);
    
    // Use proper navigation if available to navigate to activity-specific URL
    if (onNavigateToActivity) {
      onNavigateToActivity(child, activity);
      return;
    }

    // Fallback to old behavior for backwards compatibility
    // Check if this activity has a pending invitation for the current user
    let enhancedActivity = { ...activity };
    
    // Always refresh invitation data to ensure we have the latest status
    // This is important when a user declines an invitation and immediately clicks back on the activity
    try {
      const today = new Date();
      const oneYearLater = new Date();
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
      const startDate = today.toISOString().split('T')[0];
      const endDate = oneYearLater.toISOString().split('T')[0];

      // Check all invitations (pending, accepted, and rejected) to get the latest status
      const allInvitationsResponse = await apiService.getAllInvitations(startDate, endDate);
      if (allInvitationsResponse.success && allInvitationsResponse.data) {
        // Look for any invitation that matches this activity
        const matchingInvitation = allInvitationsResponse.data.find((inv: any) => {
          // First try to match by activity UUID if available
          if (activity.uuid && inv.activity_uuid) {
            return inv.activity_uuid === activity.uuid;
          }
          if (activity.activity_uuid && inv.activity_uuid) {
            return inv.activity_uuid === activity.activity_uuid;
          }
          // Fallback to matching by name, date, and time
          return inv.activity_name === activity.name &&
                 inv.start_date === activity.start_date &&
                 inv.start_time === activity.start_time &&
                 inv.invited_child_name === child.name; // Make sure it's for this child
        });

        if (matchingInvitation) {
          // Update activity based on current invitation status
          if (matchingInvitation.status === 'pending') {
            enhancedActivity = {
              ...activity,
              isPendingInvitation: true,
              isAcceptedInvitation: false,
              isRejectedInvitation: false,
              isDeclinedInvitation: false,
              invitation_status: 'pending',
              invitationUuid: matchingInvitation.uuid,
              invitation_message: matchingInvitation.invitation_message,
              host_child_name: matchingInvitation.host_child_name,
              host_parent_name: matchingInvitation.host_parent_username,
              class_name: matchingInvitation.class_name,
              class_rep_name: matchingInvitation.class_rep_name,
              activity_uuid: matchingInvitation.activity_uuid
            } as any;
          } else if (matchingInvitation.status === 'accepted') {
            enhancedActivity = {
              ...activity,
              isPendingInvitation: false,
              isAcceptedInvitation: true,
              isRejectedInvitation: false,
              isDeclinedInvitation: false,
              invitation_status: 'accepted',
              invitationUuid: matchingInvitation.uuid,
              invitation_message: matchingInvitation.invitation_message,
              host_child_name: matchingInvitation.host_child_name,
              host_parent_name: matchingInvitation.host_parent_username,
              class_name: matchingInvitation.class_name,
              class_rep_name: matchingInvitation.class_rep_name,
              activity_uuid: matchingInvitation.activity_uuid
            } as any;
          } else if (matchingInvitation.status === 'rejected') {
            enhancedActivity = {
              ...activity,
              isPendingInvitation: false,
              isAcceptedInvitation: false,
              isRejectedInvitation: true,
              isDeclinedInvitation: true,
              invitation_status: 'rejected',
              invitationUuid: matchingInvitation.uuid,
              invitation_message: matchingInvitation.invitation_message,
              host_child_name: matchingInvitation.host_child_name,
              host_parent_name: matchingInvitation.host_parent_username,
              class_name: matchingInvitation.class_name,
              class_rep_name: matchingInvitation.class_rep_name,
              activity_uuid: matchingInvitation.activity_uuid
            } as any;
          }
        }
      }

      // If no invitation found above, also check pending invitations for backwards compatibility
      if (!(enhancedActivity as any).isPendingInvitation && !(enhancedActivity as any).isAcceptedInvitation && !(enhancedActivity as any).isRejectedInvitation) {
        const pendingResponse = await apiService.getPendingInvitationsForCalendar(startDate, endDate);
        if (pendingResponse.success && pendingResponse.data) {
          // Look for a pending invitation that matches this activity
          const matchingInvitation = pendingResponse.data.find((inv: any) => {
            // First try to match by activity UUID if available
            if (activity.uuid && inv.activity_uuid) {
              return inv.activity_uuid === activity.uuid;
            }
            if (activity.activity_uuid && inv.activity_uuid) {
              return inv.activity_uuid === activity.activity_uuid;
            }
            // Fallback to matching by name, date, and time
            return inv.activity_name === activity.name && 
                   inv.start_date === activity.start_date &&
                   inv.start_time === activity.start_time;
          });
          
          
          if (matchingInvitation) {
            // Notifications disabled for this screen
            const hasMatchingNotification = false;

            // Only convert to pending invitation format if NO notification exists (fallback to old system)
            if (!hasMatchingNotification) {
              // Determine the actual invitation type based on status
              const invitationStatus = matchingInvitation.status || matchingInvitation.invitation_status;
              const isPending = invitationStatus === 'pending';
              const isRejected = invitationStatus === 'rejected' || invitationStatus === 'declined';
              const isAccepted = invitationStatus === 'accepted';

              enhancedActivity = {
                ...activity,
                isPendingInvitation: isPending,
                isRejectedInvitation: isRejected,
                isDeclinedInvitation: isRejected,
                isAcceptedInvitation: isAccepted,
                invitation_status: invitationStatus,
                invitationUuid: matchingInvitation.uuid,
                invitation_message: matchingInvitation.invitation_message,
                host_child_name: matchingInvitation.host_child_name,
                host_parent_name: matchingInvitation.host_parent_username,
                activity_uuid: matchingInvitation.activity_uuid
              } as any;
            }
          } else {
          }
        }
      }
    } catch (error) {
      console.error('Failed to check for invitation data:', error);
    }
    
    setSelectedActivity(enhancedActivity);
    navigateToPage('activity-detail');

    // Load participants for this activity
    const activityUuid = (enhancedActivity as any).activity_uuid || (enhancedActivity as any).uuid;
    if (activityUuid) {
      loadActivityParticipants(activityUuid);
    }

    // Mark invitation as viewed if this is any type of invitation
    if (enhancedActivity.isPendingInvitation || enhancedActivity.isAcceptedInvitation || enhancedActivity.isDeclinedInvitation) {
      const invitationUuid = (enhancedActivity as any).invitationUuid || (enhancedActivity as any).invitation_uuid;
      if (invitationUuid) {
        try {
          // Call the backend API to mark invitation as viewed using UUID
          await apiService.markInvitationAsViewed(invitationUuid);
          // Set a flag to refresh data when returning from detail view
          sessionStorage.setItem('invitationViewed', 'true');
        } catch (error) {
          console.error('Failed to mark invitation as viewed:', error);
        }
      }
    }
    
    // Populate newActivity state with selected activity data for inline editing
    setNewActivity({
      name: activity.name || '',
      description: activity.description || '',
      start_date: activity.start_date ? activity.start_date.split('T')[0] : '',
      end_date: activity.end_date ? activity.end_date.split('T')[0] : '',
      start_time: activity.start_time || '',
      end_time: activity.end_time || '',
      location: activity.location || '',
      website_url: activity.website_url || '',
      cost: activity.cost ? activity.cost.toString() : '',
      max_participants: activity.max_participants ? activity.max_participants.toString() : '',
      activity_type: (activity as any).activity_type || '',
      auto_notify_new_connections: (activity as any).auto_notify_new_connections || false,
      kit_needed: (activity as any).kit_needed || [],
      responsible_parent_uuid: (activity as any).responsible_parent_uuid || undefined
    });

    // Set the auto-notification checkbox state
    setAutoNotifyNewConnections((activity as any).auto_notify_new_connections || false);

    // Initialize selectedDates for calendar picker based on activity dates
    const activityDates: string[] = [];
    if (activity.start_date) {
      const startDate = activity.start_date.split('T')[0];
      activityDates.push(startDate);
      
      // If there's an end date and it's different from start date, add all dates in between
      if (activity.end_date && activity.end_date.split('T')[0] !== startDate) {
        const endDate = activity.end_date.split('T')[0];
        const start = new Date(startDate);
        const end = new Date(endDate);
        const current = new Date(start);
        current.setDate(current.getDate() + 1); // Start from day after start date
        
        while (current <= end) {
          activityDates.push(current.toISOString().split('T')[0]);
          current.setDate(current.getDate() + 1);
        }
      }
    }
    setSelectedDates(activityDates);
    
    // Load participants for any activity that has a real backend activity ID
    
    // For invitations, we need to get the original activity UUID from the backend
    if (activity.isPendingInvitation || activity.isAcceptedInvitation || activity.isDeclinedInvitation) {
      // For invitations, we need to load participants using the original activity UUID
      // The invitation data should contain the original activity_uuid
      const originalActivityUuid = (activity as any).activity_uuid || (activity as any).uuid;
      if (originalActivityUuid && typeof originalActivityUuid === 'string') {
        loadActivityParticipants(originalActivityUuid);
      } else {
        console.error('❌ No valid activity UUID found for invitation:', activity);
        setActivityParticipants(null);
      }
    } else if ((activity as any).activity_uuid) {
      // For regular activities, use the activity UUID directly
      loadActivityParticipants((activity as any).activity_uuid);
    } else {
      console.error('❌ No activity ID found for activity:', activity);
      setActivityParticipants(null);
    }
    
    // Load connected children for host activities to show uninvited children
    if (activity.is_host) {
      loadConnectionsData();
    }
  };

  const handleEditActivity = (activity: Activity) => {
    const activityName = (activity as any).name;

    // Check if this activity is part of a series using multiple detection methods:
    // 1. Check if activity has series_id or is_recurring flag
    const hasSeries = (activity as any).series_id;
    const isRecurring = (activity as any).is_recurring;

    // 2. Find all activities with the same name (potential series)
    const sameNameActivities = activities.filter(act => (act as any).name === activityName);
    const hasMultipleSameName = sameNameActivities.length > 1;

    // Activity is part of a series if ANY of these conditions are true
    const isPartOfSeries = hasSeries || isRecurring || hasMultipleSameName;

    if (isPartOfSeries) {
      // Show custom dialog for recurring activity update
      setUpdateDialogActivity(activity);
      setUpdateOption('single'); // Reset to default
      setShowUpdateDialog(true);
    } else {
      // Regular single activity edit - proceed directly
      proceedWithEdit(activity);
    }
  };

  const proceedWithEdit = (activity: Activity) => {
    setEditingActivity(activity);
    // Reset all form state first, then populate with activity data
    setSelectedDates([]);
    setIsSharedActivity(false);
    setAutoNotifyNewConnections(false);
    setSelectedConnectedChildren([]);
    setSelectedClassUuids([]);
    setJointHostChildren([]);

    setNewActivity({
      name: activity.name,
      description: activity.description || '',
      start_date: activity.start_date.split('T')[0],
      end_date: activity.end_date ? activity.end_date.split('T')[0] : activity.start_date.split('T')[0],
      start_time: activity.start_time || '',
      end_time: activity.end_time || '',
      location: activity.location || '',
      website_url: activity.website_url || '',
      cost: activity.cost ? activity.cost.toString() : '',
      max_participants: activity.max_participants ? activity.max_participants.toString() : '',
      activity_type: (activity as any).activity_type || '',
      auto_notify_new_connections: (activity as any).auto_notify_new_connections || false,
      kit_needed: (activity as any).kit_needed || [],
      responsible_parent_uuid: (activity as any).responsible_parent_uuid || undefined
    });
    setAutoNotifyNewConnections((activity as any).auto_notify_new_connections || false);
    // Initialize reminder minutes from the activity's stored value
    setSelectedReminderMinutes((activity as any).reminder_minutes ?? null);
    navigateToPage('edit-activity');
  };

  const sendInvitations = async () => {
    
    if (selectedConnectedChildren.length === 0 || !selectedActivity) {
      return { success: true, count: 0 };
    }

    // ✅ RECURRING ACTIVITIES: Check if this is a recurring activity and get all instances
    
    // Debug all activities with the same name
    const sameNameActivities = activities.filter(a => a.name === selectedActivity.name);
    
    // Find all activities with the same name for this child (more robust matching)
    const currentChildId = selectedActivity.child_id || child.id;
    const currentChildUuid = selectedActivity.child_uuid || child.uuid;
    
    const potentialSeriesActivities = activities.filter(a => {
      // Match by name first
      if (a.name !== selectedActivity.name) return false;
      
      // Match by child - try multiple field combinations for robustness
      const matchByChildId = (a.child_id && currentChildId && a.child_id === currentChildId);
      const matchByChildUuid = (a.child_uuid && currentChildUuid && a.child_uuid === currentChildUuid);
      const matchByDirectChildUuid = (a.child_uuid && child.uuid && a.child_uuid === child.uuid);
      
      return matchByChildId || matchByChildUuid || matchByDirectChildUuid;
    });
    
    
    const isRecurringActivity = selectedActivity.name && (
      (selectedActivity as any).series_id || 
      (selectedActivity as any).is_recurring ||
      potentialSeriesActivities.length > 1
    );
    

    let activitiesToInvite = [selectedActivity];
    
    if (isRecurringActivity) {
      activitiesToInvite = potentialSeriesActivities;
    }

    // Send invitations to selected children for all activities in the series
    const invitationPromises = selectedConnectedChildren.map(async (childId) => {
      const childIdStr = String(childId);
      
      if (isPendingChildId(childIdStr)) {
        const pendingId = extractPendingId(childIdStr);
        
        if (isRecurringActivity) {
          // Send invitations to all activities in the series for pending children
          const pendingInvitePromises = activitiesToInvite.map(activity =>
            apiService.createPendingInvitations(activity.uuid || activity.activity_uuid || String(activity.id), [pendingId])
          );
          const results = await Promise.all(pendingInvitePromises);
          return { success: results.some(r => r?.success), series: true, count: results.filter(r => r?.success).length };
        } else {
          return apiService.createPendingInvitations(selectedActivity.uuid || selectedActivity.activity_uuid || String(selectedActivity.id), [pendingId]);
        }
      } else {
        const connectedChild = connectedChildren.find(c => c.uuid === childIdStr || c.connected_child_uuid === childIdStr);
        
        if (connectedChild) {
          const parentId = connectedChild.parentUuid;
          
          if (parentId) {
            if (isRecurringActivity) {
              // Send invitations to all activities in the series
              
              const recurringInvitePromises = activitiesToInvite.map((activity, index) => {
                const activityUuid = activity.uuid || activity.activity_uuid || String(activity.id);
                return apiService.sendActivityInvitation(activityUuid, parentId, childIdStr);
              });
              
              const results = await Promise.all(recurringInvitePromises);
              const successCount = results.filter(r => r?.success).length;
              const failedCount = results.length - successCount;
              
              if (failedCount > 0) {
              }
              
              return { success: successCount > 0, series: true, count: successCount };
            } else {
              
              return apiService.sendActivityInvitation(selectedActivity.uuid || selectedActivity.activity_uuid || String(selectedActivity.id), parentId, childIdStr);
            }
          } else {
            console.error('❌ No parent UUID found for connectedChild:', connectedChild);
          }
        } else {
          console.error('❌ Connected child not found for UUID:', childIdStr);
        }
      }
    });

    try {
      const results = await Promise.all(invitationPromises);
      
      // Count successes, including series invitations
      const successful = results.filter(r => r?.success).length;
      const totalInvitations = results.reduce((total, r) => {
        if ((r as any)?.series && (r as any)?.count) {
          return total + (r as any).count;
        }
        return total + (r?.success ? 1 : 0);
      }, 0);
      
      const hasSeriesInvitations = results.some(r => (r as any)?.series);
      
      
      return { 
        success: true, 
        count: successful, 
        totalInvitations,
        isRecurring: isRecurringActivity,
        seriesCount: isRecurringActivity ? activitiesToInvite.length : 1
      };
    } catch (error) {
      console.error('Failed to send invitations:', error);
      return { success: false, count: 0 };
    }
  };

  const handleUpdateActivity = async () => {

    if (!selectedActivity || !newActivity.name.trim()) {
      alert('Please enter activity name');
      return;
    }

    // Only validate dates when in edit-activity mode, not when just updating activity details from activity-detail page
    if (currentPage === 'edit-activity' && selectedDates.length === 0) {
      alert('Please select at least one date from the calendar');
      return;
    }

    // Check if this is a recurring activity and show dialog if not already shown
    if (!showUpdateDialog && !updateDialogActivity) {
      const seriesId = (selectedActivity as any).series_id;

      // Ignore migration artifacts (series_id starting with "migrated_")
      const isMigrationArtifact = seriesId && typeof seriesId === 'string' && seriesId.startsWith('migrated_');

      // If activity has a valid series_id, it's part of a series
      const isSeriesActivity = seriesId && !isMigrationArtifact;

      console.log('🔍 UPDATE SERIES DETECTION DEBUG:', {
        activityName: selectedActivity.name,
        seriesId,
        isMigrationArtifact,
        isSeriesActivity,
        updateOption
      });

      if (isSeriesActivity) {
        // Show dialog and wait for user choice
        setUpdateDialogActivity(selectedActivity);
        setUpdateOption('single'); // Reset to default
        setShowUpdateDialog(true);
        return; // Stop here, dialog will call this function again after user chooses
      }
    }

    setAddingActivity(true);
    try {
      // Get activity UUID - prioritize UUID over numeric ID for consistency
      const activityUuid = (selectedActivity as any).activity_uuid ||
                           (selectedActivity as any).uuid;

      const originalDate = selectedActivity.start_date.split('T')[0];
      const selectedDatesSorted = [...selectedDates].sort();
      const firstSelectedDate = selectedDatesSorted[0];

      // Base activity data (without dates)
      const baseActivityData = {
        name: newActivity.name,
        description: newActivity.description,
        start_time: newActivity.start_time,
        end_time: newActivity.end_time,
        location: newActivity.location,
        venue: newActivity.venue || undefined,
        city: newActivity.city || undefined,
        activity_type: newActivity.activity_type, // Include activity_type for club persistence
        website_url: newActivity.website_url,
        contact_phone: newActivity.contact_phone || undefined,
        contact_email: newActivity.contact_email || undefined,
        passcode: newActivity.passcode || undefined,
        cost: newActivity.cost ? parseFloat(newActivity.cost) : undefined,
        max_participants: newActivity.max_participants ? parseInt(newActivity.max_participants) : undefined,
        auto_notify_new_connections: isSharedActivity ? autoNotifyNewConnections : false,
        joint_host_children: jointHostChildren.length > 0 ? jointHostChildren : undefined, // Add joint host children
        kit_needed: newActivity.kit_needed || [], // Use edited kit items from form
        kit_tracking: (selectedActivity as any)?.kit_tracking || null, // Preserve existing kit tracking
        club_uuid: (newActivity as any).club_uuid || null, // Include club UUID for club name display
        responsible_parent_uuid: newActivity.responsible_parent_uuid || null, // Include designated parent
        reminder_minutes: selectedReminderMinutes, // Include reminder setting
        reminder_target: selectedReminderTarget || 'start' // Include which time(s) to remind for
      };

      // Update the original activity with the first selected date
      const mainActivityData = {
        ...baseActivityData,
        start_date: firstSelectedDate || originalDate,
        end_date: firstSelectedDate || originalDate
      };


      if (!activityUuid) {
        alert('Unable to update activity - activity UUID not found');
        console.error('❌ No activity UUID found in selectedActivity:', selectedActivity);
        setAddingActivity(false);
        return;
      }

      // Check if we need to update entire series
      const seriesId = (selectedActivity as any).series_id;
      const shouldUpdateSeries = updateOption === 'series';

      if (shouldUpdateSeries) {
        if (!seriesId) {
          console.error('❌ Cannot update series: selectedActivity has no series_id!');
          console.error('selectedActivity:', selectedActivity);
          alert('Error: This activity does not have a series_id. Cannot update series.');
          setAddingActivity(false);
          return;
        }

        console.log('🔄 Updating entire series with series_id:', seriesId);

        // Get series activities from backend (don't rely on activities state which might be empty)
        const currentChildUuid = selectedActivity.child_uuid || child.uuid;

        // Fetch all activities for a wide date range to get the entire series
        const today = new Date();
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(today.getFullYear() - 1);
        const oneYearLater = new Date();
        oneYearLater.setFullYear(today.getFullYear() + 1);

        const startDate = oneYearAgo.toISOString().split('T')[0];
        const endDate = oneYearLater.toISOString().split('T')[0];

        console.log('📡 Fetching series activities from backend...');
        const seriesResponse = await apiService.getCalendarActivities(startDate, endDate);

        if (!seriesResponse.success || !seriesResponse.data) {
          console.error('❌ Failed to fetch activities from backend');
          alert('Failed to fetch series activities. Please try again.');
          setAddingActivity(false);
          return;
        }

        // Filter to get only activities in this series for this child
        const allActivities = Array.isArray(seriesResponse.data) ? seriesResponse.data : [];
        const seriesActivities = allActivities.filter(a => {
          const activitySeriesId = (a as any).series_id;
          const matchesSeriesId = activitySeriesId === seriesId;
          const matchesChild = a.child_uuid === currentChildUuid;

          console.log('  Checking activity:', {
            name: a.name,
            date: a.start_date,
            seriesId: activitySeriesId,
            childUuid: a.child_uuid,
            matchesSeriesId,
            matchesChild
          });

          return matchesSeriesId && matchesChild;
        });

        console.log(`📊 Found ${seriesActivities.length} activities in series to update`);

        // Update all activities in the series
        let updatedCount = 0;
        for (const seriesActivity of seriesActivities) {
          const seriesActivityUuid = (seriesActivity as any).activity_uuid || (seriesActivity as any).uuid;
          if (seriesActivityUuid) {
            const seriesActivityData = {
              ...baseActivityData,
              start_date: seriesActivity.start_date.split('T')[0],
              end_date: seriesActivity.end_date ? seriesActivity.end_date.split('T')[0] : seriesActivity.start_date.split('T')[0]
            };

            try {
              const updateResponse = await apiService.updateActivity(seriesActivityUuid, seriesActivityData);
              if (updateResponse.success) {
                updatedCount++;
              }
            } catch (error) {
              console.error(`Failed to update series activity ${seriesActivityUuid}:`, error);
            }
          }
        }

        alert(`Successfully updated ${updatedCount} activities in the series!`);
        loadActivities(); // Reload activities to reflect changes
        goBack(); // Go back to main page
      } else {
        // Update only the single activity
        const response = await apiService.updateActivity(activityUuid, mainActivityData);

        if (response.success) {
          let successMessage = 'Activity updated successfully!';
          let additionalActivitiesCreated = 0;

          // If there are additional dates selected, create new activities for them
          if (selectedDatesSorted.length > 1) {
            for (let i = 1; i < selectedDatesSorted.length; i++) {
              const additionalDate = selectedDatesSorted[i];
              const additionalActivityData = {
                ...baseActivityData,
                start_date: additionalDate,
                end_date: additionalDate
              };

              try {
                const createResponse = await apiService.createActivity(child.uuid, additionalActivityData);
                if (createResponse.success) {
                  additionalActivitiesCreated++;
                }
              } catch (error) {
                console.error(`Failed to create activity for ${additionalDate}:`, error);
              }
            }

            if (additionalActivitiesCreated > 0) {
              successMessage += ` ${additionalActivitiesCreated} additional activity${additionalActivitiesCreated > 1 ? 'ies' : 'y'} created for other selected dates.`;
            }
          }

          // Automatically send invitations if there are selected children
          const invitationResult = await sendInvitations();
          if (invitationResult.count > 0) {
            if (invitationResult.isRecurring) {
              successMessage += ` ${invitationResult.count} invitation${invitationResult.count > 1 ? 's' : ''} sent for recurring series (${invitationResult.seriesCount} activities each)!`;
            } else {
              successMessage += ` ${invitationResult.count} invitation${invitationResult.count > 1 ? 's' : ''} sent!`;
            }
            setSelectedClassUuids([]);
      setSelectedConnectedChildren([]);
            loadActivityParticipants(selectedActivity.uuid || selectedActivity.activity_uuid || String(selectedActivity.id));
          }

          alert(successMessage);
          loadActivities(); // Reload activities to reflect changes
          goBack(); // Go back to main page
        } else {
          alert(`Error: ${response.error || 'Failed to update activity'}`);
        }
      }
    } catch (error) {
      alert('Failed to update activity');
      console.error('Update activity error:', error);
    } finally {
      setAddingActivity(false);
      // Clean up dialog state after update completes
      setUpdateDialogActivity(null);
      setUpdateOption('single');
    }
  };

  const handleDeleteActivity = async (activity: Activity) => {
    const activityName = (activity as any).name;

    // Check if this activity is part of a series using multiple detection methods:
    // 1. Check if activity has series_id or is_recurring flag
    const hasSeries = (activity as any).series_id;
    const isRecurring = (activity as any).is_recurring;

    // 2. Find all activities with the same name (potential series)
    const sameNameActivities = activities.filter(act => (act as any).name === activityName);
    const hasMultipleSameName = sameNameActivities.length > 1;

    // Activity is part of a series if ANY of these conditions are true
    const isPartOfSeries = hasSeries || isRecurring || hasMultipleSameName;

    if (isPartOfSeries) {
      // Show custom dialog for recurring activity deletion
      setDeleteDialogActivity(activity);
      setDeleteOption('single'); // Reset to default
      setShowDeleteDialog(true);
    } else {
      // Regular single activity deletion
      if (window.confirm(`Are you sure you want to delete "${activity.name}"?`)) {
        await deleteActivity(activity, false);
      }
    }
  };

  const deleteActivity = async (activity: Activity, deleteWholeSeries: boolean = false) => {
    try {
      let response;
      const activityName = (activity as any).name;
      
      if (deleteWholeSeries) {
        // Try to delete entire series using the optimized API endpoint first
        
        try {
          // Use the new series deletion API endpoint
          response = await apiService.deleteActivitySeries(activityName, child.uuid);
          
          if (response.success) {
          } else {
            throw new Error('Series API failed, using fallback');
          }
        } catch (seriesApiError) {
          // Fallback to individual deletion if series API doesn't exist or fails
          
          // Find all activities in the current state that are part of the same series
          const currentSeriesId = (deleteDialogActivity as any).series_id;
          const seriesActivities = currentSeriesId
            ? activities.filter(act => (act as any).series_id === currentSeriesId)
            : activities.filter(act => (act as any).name === activityName);
          
          // Delete each activity in the series
          const deletePromises = seriesActivities.map(act => 
            apiService.deleteActivity(act.uuid || act.activity_uuid || String((act as any).id))
          );
          
          const results = await Promise.allSettled(deletePromises);
          const successCount = results.filter(result => result.status === 'fulfilled').length;
          const failedCount = results.length - successCount;
          
          if (failedCount === 0) {
            response = { success: true, message: `All ${successCount} activities in the series deleted successfully` };
          } else {
            response = { success: false, error: `${successCount} activities deleted, ${failedCount} failed` };
          }
        }
      } else {
        // Delete single activity
        response = await apiService.deleteActivity(activity.uuid || activity.activity_uuid || String(activity.id));
      }
      
      if (response.success) {
        goBack();
        loadActivities();
        const message = deleteWholeSeries 
          ? `Recurring activity series "${activityName}" deleted successfully`
          : `Activity "${activityName}" deleted successfully`;
        alert(message);
      } else {
        alert(`Error: ${response.error || 'Failed to delete activity'}`);
      }
    } catch (error) {
      alert('Failed to delete activity');
      console.error('Delete activity error:', error);
    }
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setDeleteDialogActivity(null);
    setDeleteOption('single'); // Reset to default
  };

  // Function to generate recurring dates between start and end dates
  const generateRecurringDates = (startDate: string, endDate: string, daysOfWeek: number[]): string[] => {
    console.log('🔄 Generating recurring dates:', { startDate, endDate, daysOfWeek });

    const dates: string[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Set to start of day to avoid timezone issues
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const current = new Date(start);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    console.log('📅 Date range:', {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
      today: today.toISOString().split('T')[0]
    });

    while (current <= end) {
      // Check if current day of week is in the selected days
      const dayOfWeek = current.getDay(); // 0 = Sunday, 1 = Monday, etc.

      if (daysOfWeek.includes(dayOfWeek)) {
        // Only add dates that are today or in the future
        current.setHours(0, 0, 0, 0);

        if (current >= today) {
          // ✅ FIX: Use local date formatting to avoid timezone offset issues
          const year = current.getFullYear();
          const month = String(current.getMonth() + 1).padStart(2, '0');
          const day = String(current.getDate()).padStart(2, '0');
          const localDateString = `${year}-${month}-${day}`;

          dates.push(localDateString);
          console.log(`✅ Added date: ${localDateString} (day ${dayOfWeek})`);
        } else {
          console.log(`⏭️ Skipped past date: ${current.toISOString().split('T')[0]} (day ${dayOfWeek})`);
        }
      }

      // Move to next day
      current.setDate(current.getDate() + 1);
    }

    console.log(`🎯 Generated ${dates.length} recurring dates:`, dates);
    return dates.sort();
  };

  const handleCreateActivity = async () => {
    if (!newActivity.name.trim()) {
      alert('Please enter activity name');
      return;
    }

    if (!isClassActivity && !(newActivity as any).club_uuid) {
      alert('Please select a club before saving the activity. Click the "Browse Clubs" button to choose a club.');
      return;
    }

    let datesToCreate: string[] = [];
    
    if (isRecurringActivity) {
      // Validate recurring activity inputs
      if (!recurringStartDate) {
        alert('Please select a start date for the recurring activity');
        return;
      }
      if (!recurringEndDate) {
        alert('Please select an end date for the recurring activity');
        return;
      }
      if (new Date(recurringEndDate) < new Date(recurringStartDate)) {
        alert('End date must be after start date');
        return;
      }
      if (recurringDaysOfWeek.length === 0) {
        alert('Please select at least one day of the week for the recurring activity');
        return;
      }
      
      // Generate dates between start and end dates based on selected days of week
      datesToCreate = generateRecurringDates(recurringStartDate, recurringEndDate, recurringDaysOfWeek);

      if (datesToCreate.length === 0) {
        alert('No valid dates found for the recurring activity. Please ensure:\n\n' +
              '• The start date is today or in the future\n' +
              '• At least one of the selected days of the week falls between the start and end dates\n' +
              '• The end date is after the start date');
        return;
      }
      
    } else {
      // Regular single date activity validation
      if (selectedDates.length === 0) {
        alert('Please select at least one date from the calendar');
        return;
      }
      datesToCreate = selectedDates;
    }

    setAddingActivity(true);
    try {
      const createdActivities: any[] = [];
      
      // Generate a unique series ID for recurring activities
      const seriesId = isRecurringActivity ? `series_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : null;

      // Prepare all activities for batch creation
      const activitiesToCreate = datesToCreate.map(date => ({
        ...newActivity,
        start_date: date,
        end_date: date, // Single day activities for each date
        cost: newActivity.cost ? parseFloat(newActivity.cost) : undefined,
        max_participants: newActivity.max_participants ? parseInt(newActivity.max_participants) : undefined,
        auto_notify_new_connections: isSharedActivity ? autoNotifyNewConnections : false,
        is_shared: isSharedActivity, // Explicitly set is_shared when user creates shared activity
        joint_host_children: jointHostChildren.length > 0 ? jointHostChildren : undefined, // Add joint host children
        series_id: seriesId, // Add series ID for recurring activities
        is_recurring: isRecurringActivity, // Flag to indicate this is part of a recurring series
        recurring_days: isRecurringActivity ? recurringDaysOfWeek : undefined, // Store the days of week for reference
        series_start_date: isRecurringActivity ? recurringStartDate : undefined, // Store the series start date
        club_uuid: (newActivity as any).club_uuid || null, // Include club UUID for club name display
        reminder_minutes: selectedReminderMinutes, // Save reminder to backend so both parents receive it
        reminder_target: selectedReminderTarget || 'start' // Include which time(s) to remind for
      }));

      console.log(`📦 Creating activities for participants:`, {
        children: selectedParticipantChildUuids,
        parents: selectedParticipantParentUuids
      });

      // Use unified endpoint that handles both children and parents in one call
      let response;

      // For recurring activities with multiple dates, we need to create each date separately
      if (activitiesToCreate.length > 1) {
        // Create activities for each date using unified endpoint
        const allResults = await Promise.all(
          activitiesToCreate.map(activity =>
            apiService.createActivitiesUnified(
              selectedParticipantChildUuids,
              selectedParticipantParentUuids,
              activity
            )
          )
        );

        const allData = allResults.flatMap(r => Array.isArray(r.data) ? r.data : [r.data]).filter(Boolean);
        const allSuccessful = allResults.every(r => r.success);
        response = {
          success: allSuccessful || allData.length > 0,
          data: allData,
          error: allSuccessful ? undefined : 'Some activities failed to create'
        };
      } else {
        // Single activity - use unified endpoint
        response = await apiService.createActivitiesUnified(
          selectedParticipantChildUuids,
          selectedParticipantParentUuids,
          activitiesToCreate[0]
        );
      }

      console.log(`👶 Created activities for ${selectedParticipantChildUuids.length} children`);
      console.log(`👔 Created activities for ${selectedParticipantParentUuids.length} parents`);

      console.log(`📝 Activity creation response:`, {
        success: response.success,
        error: response.error,
        hasData: !!response.data,
        count: Array.isArray(response.data) ? response.data.length : 1
      });

      if (response.success) {
        // Handle batch response (array) or single response (object)
        const activitiesData = Array.isArray(response.data) ? response.data : [response.data];

        // Increment club usage for first activity if it has website_url and activity_type
        const firstActivity = activitiesData[0];
        if (firstActivity && activitiesToCreate[0].website_url && activitiesToCreate[0].activity_type) {
          try {
            const clubUsageData = {
              website_url: activitiesToCreate[0].website_url,
              activity_type: activitiesToCreate[0].activity_type,
              location: activitiesToCreate[0].location || undefined,
              child_age: child.age || undefined,
              activity_start_date: activitiesToCreate[0].start_date,
              activity_uuid: firstActivity.uuid
            };

            await apiService.incrementClubUsage(clubUsageData);
          } catch (clubError) {
            // Don't fail activity creation if club increment fails
          }
        }

        // Add all created activities to the list
        activitiesData.forEach((activity: any) => {
          createdActivities.push(activity);
        });

        // Schedule reminders for created activities if user selected a reminder time
        // Only schedule if: no designated parent (both get it) OR current user is the designated parent
        const shouldReceiveReminder = !newActivity.responsible_parent_uuid || newActivity.responsible_parent_uuid === user?.uuid;

        if (selectedReminderMinutes !== null && Capacitor.isNativePlatform() && shouldReceiveReminder) {
          const localNotificationService = LocalNotificationService.getInstance();
          // Request alarm permission (must be in response to user tap for iOS prompt)
          await localNotificationService.requestAlarmPermission();
          for (const activity of activitiesData) {
            const activityUuid = activity.uuid || activity.activity_uuid;
            if (activityUuid && activity.start_date) {
              try {
                await localNotificationService.scheduleReminder({
                  activityUuid,
                  activityName: activity.name || newActivity.name,
                  startDate: activity.start_date,
                  startTime: activity.start_time || newActivity.start_time || '',
                  endTime: activity.end_time || newActivity.end_time || '',
                  minutesBefore: selectedReminderMinutes,
                  reminderTarget: selectedReminderTarget || 'start',
                  childName: child?.name,
                  location: activity.venue || activity.location || newActivity.venue || newActivity.location || ''
                });
                console.log(`✅ Reminder scheduled for activity: ${activityUuid}`);
              } catch (reminderError: any) {
                console.error(`❌ Failed to schedule reminder for activity ${activityUuid}:`, reminderError);
              }
            }
          }
        }
      } else {
        console.error(`❌ Failed to create activities:`, response.error);
        throw new Error(response.error || 'Failed to create activities');
      }


      // Invite whole classes the rep picked. The class endpoint owns who in
      // it actually gets an invitation — members still deciding are held.
      if (selectedClassUuids.length > 0 && createdActivities.length > 0) {
        for (const activity of createdActivities) {
          const activityUuid = activity.uuid || activity.activity_uuid;
          for (const classUuid of selectedClassUuids) {
            try {
              await apiService.inviteClassToActivity(classUuid, activityUuid);
            } catch (error) {
              console.error('❌ Failed to invite class to activity:', error);
            }
          }
        }
      }

      // Track pending connections for each created activity
      
      if (isSharedActivity && createdActivities.length > 0) {
        const pendingConnections = selectedConnectedChildren.filter(id =>
          typeof id === 'string' && isPendingChildId(id)
        ) as string[];
        
        
        if (pendingConnections.length > 0) {
          for (const activity of createdActivities) {
            try {
              // Store pending invitations for this activity
              const activityUuid = activity.uuid || activity.activity_uuid;
              await apiService.createPendingInvitations(activityUuid, pendingConnections);
            } catch (error) {
              console.error('❌ Failed to store pending invitations:', error);
            }
          }
        } else {
        }
      } else {
      }

      // If it's a shared activity, send invitations to selected connected children
      if (isSharedActivity && selectedConnectedChildren.length > 0 && createdActivities.length > 0) {
        
        // First, ensure all joint host connections are loaded and build a local connections map
        const jointHostsNeeded = new Set<string>();
        selectedConnectedChildren.forEach(childId => {
          if (typeof childId === 'string' && childId.startsWith('joint-')) {
            // Format: joint-{hostChildUuid}-{connectedChildUuid}
            // Extract the hostChildUuid (first UUID after 'joint-')
            const withoutPrefix = childId.substring('joint-'.length);
            const uuidParts = withoutPrefix.split('-');
            if (uuidParts.length >= 5) {
              // Reconstruct the first UUID (format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
              const hostChildUuid = `${uuidParts[0]}-${uuidParts[1]}-${uuidParts[2]}-${uuidParts[3]}-${uuidParts[4]}`;
              jointHostsNeeded.add(hostChildUuid);
            }
          }
        });
        
        // Load joint host connections and store them locally for immediate use
        const localJointHostConnections = { ...jointHostConnections };
        if (jointHostsNeeded.size > 0) {
          for (const hostChildUuid of Array.from(jointHostsNeeded)) {
            if (!localJointHostConnections[hostChildUuid]) {
              try {
                const response = await apiService.getChildConnections(hostChildUuid);
                if (response.success && response.data) {
                  const connectedChildren: any[] = [];
                  response.data.forEach((item: any) => {
                    const status = item.status || item.connection_status || 'accepted';
                    if (status === 'accepted' || status === 'connected' || status === 'active') {
                      connectedChildren.push({
                        id: item.connected_child_uuid || item.child_uuid || item.uuid,
                        uuid: item.connected_child_uuid || item.child_uuid || item.uuid, 
                        name: item.connected_child_name || item.child_name || item.name,
                        parentName: item.connected_parent_name || item.parent_name || item.parentname,
                        parentUuid: item.connected_parent_uuid || item.parent_uuid || item.parentuuid,
                                  family_name: item.connected_family_name || item.family_name,
                        status: status
                      });
                    }
                  });
                  localJointHostConnections[hostChildUuid] = connectedChildren;
                }
              } catch (error) {
                console.error(`❌ Failed to load connections for joint host ${hostChildUuid}:`, error);
              }
            }
          }
        }
        
        // Group invitations by host child for joint hosting
        const invitationsByHost = new Map();
        
        for (const childId of selectedConnectedChildren) {
          if (typeof childId === 'string' && isPendingChildId(childId)) {
            continue;
          }
          
          let hostChildUuid = child.uuid; // Default to main host
          let actualChildId = childId;
          
          // Parse prefixed IDs for joint hosting
          if (typeof childId === 'string') {
            if (childId.startsWith('main-')) {
              hostChildUuid = child.uuid;
              actualChildId = childId.replace('main-', '');
            } else if (childId.startsWith('joint-')) {
              // Format: joint-{hostChildUuid}-{connectedChildId}
              const withoutPrefix = childId.substring(6); // Remove 'joint-'
              const uuidParts = withoutPrefix.split('-');
              if (uuidParts.length >= 5) {
                // Reconstruct the full host child UUID (format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
                hostChildUuid = `${uuidParts[0]}-${uuidParts[1]}-${uuidParts[2]}-${uuidParts[3]}-${uuidParts[4]}`;
                // The rest is the connected child UUID
                actualChildId = uuidParts.slice(5).join('-');
              }
            }
          }
          
          if (!invitationsByHost.has(hostChildUuid)) {
            invitationsByHost.set(hostChildUuid, []);
          }
          invitationsByHost.get(hostChildUuid).push(actualChildId);
        }
        
        
        // Send invitations from each host child - handle both single and recurring activities
        for (const [hostChildUuid, childIds] of Array.from(invitationsByHost.entries())) {
          const hostChild = hostChildUuid === child.uuid ? child : parentChildren.find(pc => pc.uuid === hostChildUuid);
          if (!hostChild) continue;
          
          // With child_uuid now included in the response, we can properly filter activities by host
          let hostActivities = [];
          
          
          
          if (jointHostChildren.length === 0) {
            // No joint hosting - all activities belong to primary host
            hostActivities = createdActivities;
          } else {
            // Filter activities by child_uuid to match the current host child
            hostActivities = createdActivities.filter(activity => activity.child_uuid === hostChildUuid);
          }
          
          
          for (const actualChildId of childIds) {
            try {
              // Find the connected child from the appropriate connections array
              let connectedChild: any;
              if (hostChildUuid === child.uuid) {
                // Main host - search in main connectedChildren array
                connectedChild = connectedChildren.find(cc => cc.uuid === actualChildId);
              } else {
                // Joint host - search in localJointHostConnections
                const jointConnections = localJointHostConnections[hostChildUuid] || [];
                connectedChild = jointConnections.find(cc => cc.uuid === actualChildId);
              }
              
              if (connectedChild) {
                
                if (!connectedChild.parentUuid) {
                  console.error(`❌ Parent UUID is missing for connected child:`, connectedChild);
                  console.error(`❌ Available parent fields:`, {
                    parentUuid: connectedChild.parentUuid,
                    parentName: connectedChild.parentName
                  });
                }
                
                // Send invitations for ALL activities of this host
                const hostInvitePromises = hostActivities.map(async (hostActivity, index) => {
                  
                  try {
                    await apiService.sendActivityInvitation(
                      hostActivity.uuid || String(hostActivity.id),
                      connectedChild.parentUuid,
                      actualChildId,
                      `${hostChild.name} would like to invite your child to join: ${hostActivity.name}`
                    );
                    return { success: true };
                  } catch (error) {
                    console.error(`❌ Failed to send invitation ${index + 1}/${hostActivities.length}:`, error);
                    return { success: false, error };
                  }
                });
                
                // Wait for all invitations to be sent
                const results = await Promise.all(hostInvitePromises);
                const successCount = results.filter(r => r.success).length;
                const failedCount = results.length - successCount;
                
              } else {
                console.error(`❌ Could not find connected child with ID: ${actualChildId}`);
              }
            } catch (inviteError) {
              console.error(`💥 Error sending invitations from ${hostChild.name} to child ${actualChildId}:`, inviteError);
            }
          }
        }
      }

      // Show template save prompt after successful activity creation
      const shouldShowTemplatePrompt = createdActivities.length > 0;

      // Capture template data BEFORE resetting the form
      let templateData: any = null;
      if (shouldShowTemplatePrompt) {
        // Calculate duration if both start and end times are provided
        let durationHours = null;
        if (newActivity.start_time && newActivity.end_time) {
          const [startHour, startMin] = newActivity.start_time.split(':').map(Number);
          const [endHour, endMin] = newActivity.end_time.split(':').map(Number);
          
          const startMinutes = startHour * 60 + startMin;
          const endMinutes = endHour * 60 + endMin;
          durationHours = (endMinutes - startMinutes) / 60;
          if (durationHours < 0) durationHours += 24; // Handle overnight activities
        }

        templateData = {
          name: newActivity.name,
          description: newActivity.description || null,
          location: newActivity.location || null,
          website_url: newActivity.website_url || null,
          activity_type: newActivity.activity_type || null,
          cost: newActivity.cost ? parseFloat(newActivity.cost) : null,
          max_participants: newActivity.max_participants ? parseInt(newActivity.max_participants) : null,
          typical_start_time: newActivity.start_time || null,
          typical_duration_hours: durationHours,
          auto_notify_new_connections: newActivity.auto_notify_new_connections || false,
          kit_needed: newActivity.kit_needed || [],
          club_uuid: (newActivity as any).club_uuid || null,
          club: (newActivity as any).club || null
        };
      }

      // Reset form
      setNewActivity(getInitialActivityState());
      setSelectedDates([]);
      setIsSharedActivity(false);
      setAutoNotifyNewConnections(false);
      setSelectedClassUuids([]);
      setSelectedConnectedChildren([]);
      setJointHostChildren([]);
      setSelectedTemplate(null);
      setTemplateConfirmationMessage('');
      setSelectedReminderMinutes(null); // Reset reminder selection
      setSelectedReminderTarget(null); // Reset reminder target selection
      clearActivityDraft(); // Clear the saved draft after successful creation
      
      // Reload activities to include the newly created activity
      // Force reload by clearing the cache date range first AND using forceRefresh=true
      setCurrentDateRange(null);
      await loadActivities(true); // FORCE refresh regardless of month change
      
      // Check if any activities were created
      if (createdActivities.length === 0) {
        // No activities were created - show error
        const errorMsg = isRecurringActivity
          ? `❌ Failed to create recurring activities.\n\n${datesToCreate.length} ${datesToCreate.length === 1 ? 'activity was' : 'activities were'} attempted but all failed.\n\nPlease check your internet connection and try again.`
          : `❌ Failed to create ${datesToCreate.length === 1 ? 'activity' : 'activities'}.\n\nPlease check your internet connection and try again.`;
        alert(errorMsg);
        return;
      }

      // Show template dialog AFTER everything else is done, using setTimeout to ensure it renders

      if (shouldShowTemplatePrompt && templateData) {
        const successMessage = isRecurringActivity && createdActivities.length > 1
          ? `🔄 Recurring activity series created! ${createdActivities.length} activities scheduled over the next 2 months.`
          : createdActivities.length === 1
            ? 'Activity created successfully!'
            : `${createdActivities.length} activities created successfully!`;

        // Dismiss keyboard and scroll to top before showing dialog
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        window.scrollTo({ top: 0, behavior: 'auto' });

        // Use setTimeout to ensure dialog shows after keyboard dismisses
        setTimeout(() => {
          setTemplateDialogMessage(
            `🎉 ${successMessage}\n\n` +
            `💾 SAVE AS TEMPLATE?\n\n` +
            `Would you like to save "${templateData.name}" as a reusable template?\n` +
            `This will help you create similar activities faster in the future.`
          );
          setDialogTemplateData(templateData);
          setShowTemplateDialog(true);
        }, 300);
      } else {
        // Just show success message if no template prompt needed, then navigate
        const message = isRecurringActivity && createdActivities.length > 1
          ? `🔄 Recurring activity series created! ${createdActivities.length} activities scheduled over the next 2 months.`
          : createdActivities.length === 1
            ? 'Activity created successfully!'
            : `${createdActivities.length} activities created successfully!`;
        alert(message);
        
        // Navigate back to activities list (only when no template dialog)
        // Force refresh when returning to calendar
        setCurrentDateRange(null);
        if (onBack) {
          onBack();
        } else {
          navigateToPage('main');
        }
      }
      
    } catch (error) {
      alert('Failed to create activity');
      console.error('Create activity error:', error);
    } finally {
      setAddingActivity(false);
    }
  };

  const handleInviteActivity = async (activity: Activity) => {
    try {
      // Load connected families
      const response = await apiService.getConnections();
      if (response.success && response.data) {
        setConnectedFamilies(response.data);
        setInvitingActivity(activity);
        setInvitationMessage(`Hi! I'd like to invite you to join us for "${activity.name}" on ${formatDate(activity.start_date)}. Let me know if you're interested!`);
        navigateToPage('invite-activity');
      } else {
        alert('Failed to load connected families');
      }
    } catch (error) {
      console.error('Load connections error:', error);
      alert('Failed to load connected families');
    }
  };

  const handleSendInvitation = async (invitedParentUuid: string, childUuid?: string) => {
    if (!invitingActivity) return;

    try {
      const response = await apiService.sendActivityInvitation(
        invitingActivity.uuid || invitingActivity.activity_uuid || String(invitingActivity.id), 
        invitedParentUuid, 
        childUuid, 
        invitationMessage
      );
      
      if (response.success) {
        alert('Invitation sent successfully!');
        goBack();
        setInvitingActivity(null);
        setInvitationMessage('');
      } else {
        alert(`Error: ${response.error || 'Failed to send invitation'}`);
      }
    } catch (error) {
      console.error('Send invitation error:', error);
      alert('Failed to send invitation');
    }
  };

  const handleInviteSpecificChild = async (activity: Activity, child: any) => {
    try {
      const response = await apiService.sendActivityInvitation(
        activity.uuid || String(activity.id),
        child.parentUuid,
        typeof child.uuid === 'string' ? child.uuid : undefined,
        `Hi! I'd like to invite ${child.name} to join us for "${activity.name}" on ${formatDate(activity.start_date)}. Let me know if you're interested!`
      );
      
      if (response.success) {
        alert(`Invitation sent to ${child.name}!`);
        // Reload participants to show the new invitation
        if (activityParticipants) {
          loadActivityParticipants((activity as any).activity_uuid);
        }
      } else {
        alert(`Failed to send invitation: ${response.error}`);
      }
    } catch (error) {
      console.error('Send invitation error:', error);
      alert('Failed to send invitation');
    }
  };

  const handleHostCantAttend = async (activity: Activity) => {
    if (!window.confirm('Are you sure you can\'t attend this activity? The activity will remain active for invited children, but will be marked that you can\'t attend.')) {
      return;
    }

    try {
      const activityUuid = activity.activity_uuid || activity.uuid;
      if (!activityUuid) {
        alert('Activity UUID not found. Cannot update attendance status.');
        return;
      }
      
      const response = await apiService.markActivityCantAttend(activityUuid);
      if (response.success) {
        alert('Activity marked as "Can\'t Attend". The activity will show as grey in your calendar and guests will be notified.');
        loadActivities();
        goBack();
      } else {
        alert(`Failed to update attendance status: ${response.error}`);
      }
    } catch (error) {
      console.error('Host can\'t attend error:', error);
      alert('Failed to update attendance status');
    }
  };

  const handleCanNowAttend = (activity: Activity) => {

    // Check if activity is part of a recurring series
    const seriesId = (activity as any).series_id;
    const isMigrationArtifact = seriesId && typeof seriesId === 'string' && seriesId.startsWith('migrated_');

    // If activity has a valid series_id, it's part of a series
    const isPartOfSeries = seriesId && !isMigrationArtifact;


    if (isPartOfSeries) {
      // Show dialog for series selection
      setReactivateDialogActivity(activity);
      setReactivateOption('single'); // Reset to default
      setShowReactivateDialog(true);
    } else {
      // Single activity - show simple confirmation
      if (window.confirm('Mark that you can now attend this activity? The activity will be reactivated and guests will be notified.')) {
        reactivateActivity(activity, false);
      }
    }
  };

  // Reactivate activity (single or series)
  const reactivateActivity = async (activity: any, reactivateEntireSeries: boolean) => {
    try {
      console.log('🟢 START reactivateActivity');
      const activitySource = activity.activity_source || activity.source;
      const isPitcheroOrSocs = activitySource === 'pitchero' || activitySource === 'socs';
      const invitationUuid = activity.invitationId || activity.invitation_uuid || activity.invitationUuid;

      console.log('🟢 Activity info:', { activitySource, isPitcheroOrSocs, invitationUuid });

      let response;

      // For Pitchero/SOCS activities, use the respond endpoint (has Pitchero API sync)
      if (isPitcheroOrSocs && !reactivateEntireSeries && invitationUuid) {
        console.log(`🔄 Using respond endpoint for Pitchero/SOCS reactivate: invitation=${invitationUuid}, activity=${activity.uuid || activity.activity_uuid}`);
        response = await apiService.respondToActivityInvitation(invitationUuid, 'accept');
        console.log('🟢 Response from Pitchero accept:', response);
      } else {
        // For regular activities, use the reactivate endpoint
        const endpoint = reactivateEntireSeries
          ? '/api/activities/reactivate-series'
          : '/api/activities/reactivate-single';

        console.log('🟢 Using regular reactivate endpoint:', endpoint);
        response = await apiService.post(endpoint, {
          // For Pitchero/SOCS invitations, activity_uuid is the actual activity UUID
          // uuid is the invitation UUID
          activityUuid: activity.activity_uuid || activity.uuid,
          isHost: activity.is_host || true
        });
        console.log('🟢 Response from regular reactivate:', response);
      }

      console.log('🟢 Checking response.success:', response.success);
      if (response.success) {
        console.log('🟢 Success! Starting navigation...');
        const message = reactivateEntireSeries
          ? 'Entire series reactivated! You can now attend all sessions and guests will be notified.'
          : 'Activity reactivated! You can now attend and guests will be notified.';

        alert(message);
        console.log('🟢 Alert shown');

        // Clear cached data BEFORE navigating back
        console.log('🟢 Clearing caches...');
        apiService.clearAllCaches();

        // Close dialogs
        console.log('🟢 Closing dialogs...');
        setShowReactivateDialog(false);
        setReactivateDialogActivity(null);

        // Force immediate refresh before navigation
        console.log('🟢 Loading activities...');
        await loadActivities(true);
        console.log('🟢 Activities loaded');

        // Navigate away after data is refreshed
        console.log('🟢 Setting selectedActivity to null...');
        setSelectedActivity(null);
        console.log('🟢 Calling goBack...');
        goBack();
        console.log('🟢 goBack called');
      } else {
        console.log('🔴 Response not successful:', response);
        alert(`Failed to reactivate: ${response.error}`);
      }
    } catch (error) {
      console.error('❌ Reactivate activity error:', error);
      alert('Failed to reactivate activity');
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateUK(dateString);
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getActivityColor = (activity: Activity | any) => {
    // Green for pending shared activities (receiver sees new invitation)
    if (activity.is_shared === true && activity.invitation_status === 'pending') {
      return {
        background: 'linear-gradient(135deg, #48bb78, #68d391)',
        borderColor: '#38a169'
      };
    }
    
    // Light blue for accepted invitations (guest perspective - accepted invitation)
    if (activity.is_host === false && activity.invitation_status === 'accepted') {
      return {
        background: 'linear-gradient(135deg, #4299e1, #63b3ed)',
        borderColor: '#3182ce'
      };
    }
    
    // Grey for declined invitations (guest perspective - declined invitation)
    if (activity.is_host === false && activity.invitation_status === 'rejected') {
      return {
        background: 'linear-gradient(135deg, #a0aec0, #cbd5e0)',
        borderColor: '#718096'
      };
    }
    
    // Grey for activities where host can't attend (cancelled)
    if (activity.is_cancelled === true) {
      return {
        background: 'linear-gradient(135deg, #a0aec0, #cbd5e0)',
        borderColor: '#718096'
      };
    }
    
    // Light blue for host's shared activities (host perspective - shareable activity)
    if (activity.is_host === true && activity.is_shared === true) {
      return {
        background: 'linear-gradient(135deg, #4299e1, #63b3ed)',
        borderColor: '#3182ce'
      };
    }
    
    // Dark blue for host's private activities (default)
    return {
      background: 'linear-gradient(135deg, #2d3748, #4a5568)',
      borderColor: '#1a202c'
    };
  };

  // Function to group recurring activities in the week view
  const groupRecurringActivitiesInWeek = (days: any[]) => {
    const processedDays = [...days];
    const recurringSeriesFound = new Set<string>();
    
    // First pass: identify all recurring series and collect their date ranges
    const seriesInfo = new Map<string, {
      activities: any[],
      firstDate: string,
      lastDate: string,
      firstDayIndex: number,
      sessionCount: number
    }>();
    
    days.forEach((day, dayIndex) => {
      day.activities.forEach((activity: any) => {
        const seriesId = activity.series_id;
        const isRecurring = activity.is_recurring || seriesId;
        
        if (isRecurring && seriesId) {
          if (!seriesInfo.has(seriesId)) {
            seriesInfo.set(seriesId, {
              activities: [],
              firstDate: day.dateString,
              lastDate: day.dateString,
              firstDayIndex: dayIndex,
              sessionCount: 0
            });
          }
          
          const series = seriesInfo.get(seriesId)!;
          series.activities.push({ ...activity, dayIndex });
          series.sessionCount++;
          
          // Update date range
          if (day.dateString < series.firstDate) {
            series.firstDate = day.dateString;
            series.firstDayIndex = dayIndex;
          }
          if (day.dateString > series.lastDate) {
            series.lastDate = day.dateString;
          }
        }
      });
    });
    
    // Second pass: replace individual recurring activities with grouped ones
    seriesInfo.forEach((series, seriesId) => {
      if (series.activities.length > 1) {
        // Remove all individual occurrences of this series
        processedDays.forEach((day) => {
          day.activities = day.activities.filter((activity: any) => activity.series_id !== seriesId);
        });
        
        // Add a single grouped activity on the first day it appears
        if (processedDays[series.firstDayIndex]) {
          const firstActivity = series.activities[0];
          const groupedActivity = {
            ...firstActivity,
            id: `series-${seriesId}`,
            name: firstActivity.name,
            description: `${firstActivity.description || ''}\n\n🔄 Recurring Series: ${series.sessionCount} sessions`,
            series_id: seriesId,
            isRecurringSeries: true,
            sessionCount: series.sessionCount,
            dateRange: series.firstDate === series.lastDate 
              ? new Date(series.firstDate).toLocaleDateString('en-GB')
              : `${new Date(series.firstDate).toLocaleDateString('en-GB')} - ${new Date(series.lastDate).toLocaleDateString('en-GB')}`,
            seriesFirstDate: series.firstDate,
            seriesLastDate: series.lastDate,
            // Keep invitation status from first activity
            isPendingInvitation: firstActivity.isPendingInvitation,
            isAcceptedInvitation: firstActivity.isAcceptedInvitation,
            isDeclinedInvitation: firstActivity.isDeclinedInvitation,
            showEnvelope: firstActivity.showEnvelope,
            showAcceptedIcon: firstActivity.showAcceptedIcon,
            showDeclinedIcon: firstActivity.showDeclinedIcon
          };
          
          processedDays[series.firstDayIndex].activities.push(groupedActivity);
          
          // Re-sort activities by time
          processedDays[series.firstDayIndex].activities.sort((a: any, b: any) => {
            const timeA = a.start_time || '00:00:00';
            const timeB = b.start_time || '00:00:00';
            return timeA.localeCompare(timeB);
          });
        }
      }
    });
    
    // Update counts and hasActivities flags
    processedDays.forEach((day) => {
      day.count = day.activities.length;
      day.hasActivities = day.activities.length > 0;
    });
    
    return processedDays;
  };

  const generateWeekDays = () => {
    const startOfWeek = new Date(currentWeek);
    const dayOfWeek = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
    
    const days = [];
    const daysToShow = calendarView === '5day' ? 5 : 7;
    const startDay = calendarView === '5day' ? 1 : 0; // Monday for 5-day, Sunday for 7-day
    
    for (let i = startDay; i < startDay + daysToShow; i++) {
      const current = new Date(startOfWeek);
      current.setDate(startOfWeek.getDate() + i);
      const dateString = current.toISOString().split('T')[0];
      
      // Get activities for this day
      const dayActivities = activities.filter(activity => {
        // Convert activity dates to simple date strings for comparison
        const activityStartDate = activity.start_date.split('T')[0];
        const activityEndDate = activity.end_date ? activity.end_date.split('T')[0] : activityStartDate;
        
        return activityStartDate === dateString || 
               (activityStartDate <= dateString && activityEndDate >= dateString);
      });
      
      // Get invited activities for this day (convert to activity format)
      const dayInvitedActivities = invitedActivities.filter(invitation => {
        const invitationStartDate = invitation.start_date.split('T')[0];
        return invitationStartDate === dateString;
      }).map(invitation => ({
        ...invitation,
        id: `accepted-${invitation.id}`,
        activity_id: invitation.id, // Original activity ID from backend for participants API
        name: invitation.activity_name,
        description: invitation.activity_description,
        isAcceptedInvitation: true,
        showAcceptedIcon: !invitation.viewed_at, // Only show green tick if not viewed
        invitationId: invitation.invitation_uuid,
        hostParent: invitation.source === 'pitchero' ? 'Pitchero' : invitation.host_parent_username,
        host_child_name: invitation.host_child_name,
        host_parent_name: invitation.source === 'pitchero' ? 'Pitchero' : invitation.host_parent_username,
        is_shared: true // Mark as shared so it gets the blue color
      }));

      // Get pending invitations for this day (convert to activity format)
      const dayPendingInvitations = pendingInvitations.filter(invitation => {
        const invitationStartDate = invitation.start_date.split('T')[0];
        return invitationStartDate === dateString;
      }).map(invitation => ({
        ...invitation,
        id: `invitation-${invitation.id}`,
        activity_id: invitation.id, // Original activity ID from backend for participants API
        name: invitation.activity_name,
        description: invitation.activity_description,
        isPendingInvitation: true, // Always true for pending invitations (for color)
        showEnvelope: !invitation.viewed_at, // Only show envelope if not viewed
        invitationId: invitation.invitation_uuid,
        hostParent: invitation.source === 'pitchero' ? 'Pitchero' : invitation.host_parent_username,
        host_child_name: invitation.host_child_name,
        host_parent_name: invitation.source === 'pitchero' ? 'Pitchero' : invitation.host_parent_username
      }));
      
      // Get declined invitations for this day (convert to activity format)
      const dayDeclinedInvitations = declinedInvitations.filter(invitation => {
        const invitationStartDate = invitation.start_date.split('T')[0];
        return invitationStartDate === dateString;
      }).map(invitation => ({
        ...invitation,
        id: `declined-${invitation.id}`,
        activity_id: invitation.id, // Original activity ID from backend for participants API
        name: invitation.activity_name,
        description: invitation.activity_description,
        isDeclinedInvitation: true,
        showDeclinedIcon: !invitation.viewed_at, // Only show red cross if not viewed
        invitationId: invitation.invitation_uuid,
        hostParent: invitation.source === 'pitchero' ? 'Pitchero' : invitation.host_parent_username,
        host_child_name: invitation.host_child_name,
        host_parent_name: invitation.source === 'pitchero' ? 'Pitchero' : invitation.host_parent_username
      }));
      
      // Since activities now come with is_shared property from backend, just use them directly
      // and add any invitation-only activities that don't have corresponding activities
      const invitationOnlyActivities = [
        ...dayInvitedActivities.filter(inv => !dayActivities.some(act => act.uuid === inv.uuid || act.id === inv.id)).map(invitation => ({
          ...invitation,
          id: `accepted-${invitation.id}`,
          activity_id: invitation.id,
          name: invitation.activity_name,
          description: invitation.activity_description,
          isAcceptedInvitation: true,
          showAcceptedIcon: !invitation.viewed_at,
          invitationId: invitation.invitation_uuid,
          host_child_name: invitation.host_child_name,
          host_parent_name: invitation.host_parent_username,
          is_shared: true
        })),
        ...dayPendingInvitations.filter(inv => {
          // Don't show as separate invitation if there's already an activity for it
          const hasMatchingActivity = dayActivities.some(act => act.uuid === inv.uuid || act.id === inv.id);
          if (hasMatchingActivity) return false;
          
          // Notifications disabled for this screen
          const hasMatchingNotification = false;
          
          
          return !hasMatchingNotification;
        }).map(invitation => ({
          ...invitation,
          id: `invitation-${invitation.id}`,
          activity_id: invitation.id,
          name: invitation.activity_name,
          description: invitation.activity_description,
          isPendingInvitation: true,
          showEnvelope: !invitation.viewed_at,
          invitationId: invitation.invitation_uuid,
          host_child_name: invitation.host_child_name,
          host_parent_name: invitation.host_parent_username
        })),
        ...dayDeclinedInvitations.filter(inv => {
          // Don't show as separate invitation if there's already an activity for it
          const hasMatchingActivity = dayActivities.some(act => act.uuid === inv.uuid || act.id === inv.id);
          if (hasMatchingActivity) return false;
          
          // Notifications disabled for this screen
          const hasMatchingNotification = false;
          
          return !hasMatchingNotification;
        }).map(invitation => ({
          ...invitation,
          id: `declined-${invitation.id}`,
          activity_id: invitation.id,
          name: invitation.activity_name,
          description: invitation.activity_description,
          isDeclinedInvitation: true,
          showDeclinedIcon: !invitation.viewed_at,
          invitationId: invitation.invitation_uuid,
          host_child_name: invitation.host_child_name,
          host_parent_name: invitation.host_parent_username
        }))
      ];
      
      // Combine activities (which already have is_shared from backend) with any invitation-only activities
      const allDayItems = [...dayActivities, ...invitationOnlyActivities];
      
      // Sort all items by start time
      const sortedItems = allDayItems.sort((a, b) => {
        const timeA = a.start_time || '00:00:00';
        const timeB = b.start_time || '00:00:00';
        return timeA.localeCompare(timeB);
      });
      
      days.push({
        date: new Date(current),
        dateString,
        isToday: dateString === new Date().toISOString().split('T')[0],
        hasActivities: sortedItems.length > 0,
        activities: sortedItems,
        count: sortedItems.length
      });
    }
    
    // Group recurring activities across the entire week
    const processedDays = groupRecurringActivitiesInWeek(days);
    
    return processedDays;
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeek(newWeek);
    
    // Check for month change when navigating weeks
    setTimeout(() => loadActivities(), 100); // Small delay to ensure state is updated
  };

  const getWeekRange = () => {
    const days = generateWeekDays();
    if (days.length === 0) return '';
    const firstDay = days[0].date;
    const lastDay = days[days.length - 1].date;
    
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const firstFormatted = firstDay.toLocaleDateString('en-GB', options);
    const lastFormatted = lastDay.toLocaleDateString('en-GB', options);
    
    if (firstDay.getFullYear() === lastDay.getFullYear()) {
      return `${firstFormatted} - ${lastFormatted}, ${firstDay.getFullYear()}`;
    } else {
      return `${firstFormatted}, ${firstDay.getFullYear()} - ${lastFormatted}, ${lastDay.getFullYear()}`;
    }
  };

  const weekDayNames = calendarView === '5day' 
    ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <div className="child-activity-screen">
        <div className="loading">Loading activities...</div>
      </div>
    );
  }

  const goBack = () => {
    // Always use logical navigation back to the page they came from
    // This goes back to the main child activities page, not browser history

    if (onBack) {
      onBack();
    } else {
      // Fallback: go to main page instead of browser back
      navigateToPage('main');
    }
  };

  // Render different pages based on currentPage state
  
  if (currentPage === 'activity-detail' && selectedActivity) {
    return (
      <div className="child-activity-screen">
        <div className="activity-header">
          <div className="child-info">
            <h2>Activity Details</h2>
            {(selectedActivity as any).is_tentative && (
              <div style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '700',
                marginLeft: '12px',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}>
                📋 TENTATIVE - Gauge Interest
              </div>
            )}
          </div>
        </div>
        <div className="page-content">
          <div className="activity-detail-content">
            {((selectedActivity as any).isPendingInvitation || (selectedActivity as any).isAcceptedInvitation || (selectedActivity as any).isDeclinedInvitation) && (
              <div className="invitation-info">
                <div className="detail-item">
                  <strong>Invitation from:</strong>
                  {(selectedActivity as any).class_name ? (
                    <p>🎓 {(selectedActivity as any).class_rep_name || 'Your class rep'} (Class {(selectedActivity as any).class_name} rep)</p>
                  ) : (
                    <p>👤 {selectedActivity.host_child_name || selectedActivity.child_name}</p>
                  )}
                </div>
                <div className="detail-item">
                  <strong>Status:</strong>
                  <p>
                    {(() => {
                      // Prioritize invitationData over selectedActivity for correct status
                      const isPending = invitationData?.isPendingInvitation !== undefined
                        ? invitationData.isPendingInvitation
                        : (selectedActivity as any).isPendingInvitation;
                      const isAccepted = invitationData?.isAcceptedInvitation !== undefined
                        ? invitationData.isAcceptedInvitation
                        : (selectedActivity as any).isAcceptedInvitation;
                      const isDeclined = invitationData?.isDeclinedInvitation !== undefined
                        ? invitationData.isDeclinedInvitation
                        : (selectedActivity as any).isDeclinedInvitation;
                      const isTentative = (selectedActivity as any).is_tentative;

                      // Show only ONE status (mutually exclusive)
                      if (isDeclined) {
                        return <span style={{ color: '#a0aec0' }}>❌ Declined</span>;
                      } else if (isAccepted) {
                        return <span style={{ color: '#4299e1' }}>✅ Accepted</span>;
                      } else if (isPending) {
                        return (
                          <span style={{ color: isTentative ? '#667eea' : '#48bb78', fontWeight: isTentative ? 'bold' : 'normal' }}>
                            {isTentative ? '📋 Pending - Tentative Invite' : '📩 Pending'}
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </p>
                </div>
                {selectedActivity.invitation_message && (
                  <div className="detail-item">
                    <strong>Message:</strong>
                    <p style={{ fontStyle: 'italic' }}>"{selectedActivity.invitation_message}"</p>
                  </div>
                )}
              </div>
            )}
            

            {/* Activity Details Form - Always Editable for Host */}
            <div className="activity-details-form">
              
              <div className="form-row">
                <label><strong>Activity Name:</strong></label>
                {selectedActivity.is_host && !(selectedActivity as any).isPendingInvitation && !(selectedActivity as any).isAcceptedInvitation && !(selectedActivity as any).isRejectedInvitation ? (
                  <input
                    type="text"
                    value={newActivity.name}
                    onChange={(e) => setNewActivity({...newActivity, name: e.target.value})}
                    className="inline-edit-input"
                    placeholder="Activity name"
                  />
                ) : (
                  <span className="readonly-value">{selectedActivity.name}</span>
                )}
              </div>

              <div className="form-row">
                <label><strong>Description:</strong></label>
                {selectedActivity.is_host && !(selectedActivity as any).isPendingInvitation && !(selectedActivity as any).isAcceptedInvitation && !(selectedActivity as any).isRejectedInvitation ? (
                  <textarea
                    value={newActivity.description}
                    onChange={(e) => setNewActivity({...newActivity, description: e.target.value})}
                    className="inline-edit-textarea"
                    placeholder="Activity description"
                    rows={2}
                  />
                ) : (
                  <span className="readonly-value">{selectedActivity.description || 'No description'}</span>
                )}
              </div>
              
              {/* Activity Calendar Display */}
              {selectedActivity.start_date && (
                <div>
                  <label className="section-label">Activity Date{selectedActivity.end_date && selectedActivity.end_date !== selectedActivity.start_date ? 's' : ''}</label>
                  <p className="section-description">
                    {selectedActivity.end_date && selectedActivity.end_date !== selectedActivity.start_date
                      ? 'This activity spans multiple days. Select a date to view or edit.'
                      : 'Select the date for this activity.'}
                  </p>
                  <CalendarDatePicker
                    selectedDates={[selectedActivity.start_date.split('T')[0]]}
                    onChange={selectedActivity.is_host ? (dates: string[]) => {
                      if (dates.length > 0) {
                        setNewActivity({...newActivity, start_date: dates[0]});
                      }
                    } : () => {}} // Editable for host, read-only for guests
                  />
                </div>
              )}

              <div className="form-row-group">
                <div className="form-row">
                  <label><strong>Start Time:</strong></label>
                  {selectedActivity.is_host && !(selectedActivity as any).isPendingInvitation && !(selectedActivity as any).isAcceptedInvitation && !(selectedActivity as any).isRejectedInvitation ? (
                    <TimePickerDropdown
                      value={newActivity.start_time}
                      onChange={(time) => setNewActivity({...newActivity, start_time: time})}
                      placeholder="Start time"
                      defaultScrollPosition="start"
                    />
                  ) : (
                    <span className="readonly-value">🕐 {selectedActivity.start_time ? formatTime(selectedActivity.start_time) : 'All day'}</span>
                  )}
                </div>

                <div className="form-row">
                  <label><strong>End Time:</strong></label>
                  {selectedActivity.is_host && !(selectedActivity as any).isPendingInvitation && !(selectedActivity as any).isAcceptedInvitation && !(selectedActivity as any).isRejectedInvitation ? (
                    <TimePickerDropdown
                      value={newActivity.end_time}
                      onChange={(time) => setNewActivity({...newActivity, end_time: time})}
                      placeholder="End time"
                      defaultScrollPosition="end"
                    />
                  ) : (
                    <span className="readonly-value">{selectedActivity.end_time ? formatTime(selectedActivity.end_time) : 'Not set'}</span>
                  )}
                </div>
              </div>

              {/* Note: Website, Location, Activity Type, Cost, Max Participants, Contact Phone, and Contact Email
                  are now managed at the club level via Edit Club button in the club details section */}
            </div>


            {/* Club/Activity Details Section */}
            {(() => {
              // Debug logging
              console.log('🔍 Activity Details Debug:', {
                venue: (selectedActivity as any)?.venue,
                city: (selectedActivity as any)?.city,
                club_name: (selectedActivity as any)?.club_name,
                club_location: (selectedActivity as any)?.club_location,
                location: (selectedActivity as any)?.location,
                contact_phone: (selectedActivity as any)?.contact_phone,
                contact_email: (selectedActivity as any)?.contact_email,
                passcode: (selectedActivity as any)?.passcode,
                newActivity_venue: newActivity.venue,
                newActivity_city: newActivity.city,
                newActivity_club_name: (newActivity as any)?.club?.name
              });

              // Use newActivity values if they exist (updated after club selection), otherwise use selectedActivity
              const venue = newActivity.venue || (selectedActivity as any)?.venue || '';
              const city = newActivity.city || (selectedActivity as any)?.city || '';
              const clubName = (newActivity as any)?.club?.name || (selectedActivity as any)?.club_name || '';
              const clubLocation = (selectedActivity as any)?.club_location || '';
              const location = newActivity.location || (selectedActivity as any)?.location || '';
              const contactPhone = newActivity.contact_phone || (selectedActivity as any)?.contact_phone || (selectedActivity as any)?.club_contact_phone || '';
              const contactEmail = newActivity.contact_email || (selectedActivity as any)?.contact_email || (selectedActivity as any)?.club_contact_email || '';
              const passcode = newActivity.passcode !== undefined ? newActivity.passcode : ((selectedActivity as any)?.passcode || '');

              const hasAnyDetails = venue || city || clubName || clubLocation || location || contactPhone || contactEmail || passcode;
              const isHost = selectedActivity.is_host && !(selectedActivity as any).isPendingInvitation && !(selectedActivity as any).isAcceptedInvitation && !(selectedActivity as any).isRejectedInvitation;

              // Show section if there are details OR if user is host (so they can add/edit club)
              if (!hasAnyDetails && !isHost) return null;

              return (
                <div className="club-details-section" style={{
                  marginTop: '24px',
                  padding: '16px',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <strong style={{ fontSize: '16px', color: '#2d3748', marginBottom: '12px', display: 'block' }}>📍 Activity Details</strong>

                  {!hasAnyDetails && isHost ? (
                    <p style={{ fontSize: '14px', color: '#666', fontStyle: 'italic', marginBottom: '8px' }}>
                      No club details yet. Click Edit Club below to add a club.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                      {clubName && (
                        <div><strong>🏢 Club:</strong> {clubName}</div>
                      )}
                      {venue && (
                        <div><strong>🏛️ Venue:</strong> {venue}</div>
                      )}
                      {city && (
                        <div><strong>📍 Town/City:</strong> {city}</div>
                      )}
                      {!venue && !city && (clubLocation || location) && (
                        <div><strong>📍 Location:</strong> {clubLocation || location}</div>
                      )}
                      {contactPhone && (
                        <div><strong>📞 Phone:</strong> {contactPhone}</div>
                      )}
                      {contactEmail && (
                        <div><strong>📧 Email:</strong> {contactEmail}</div>
                      )}
                    </div>
                  )}

                  {/* Passcode field - show separately */}
                  {(passcode || isHost) && (
                    <div style={{
                      background: 'rgba(0, 123, 255, 0.1)',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      marginTop: '12px'
                    }}>
                      <strong>🔐 Access Code:</strong>
                      {isHost ? (
                        <input
                          type="text"
                          value={newActivity.passcode !== undefined ? newActivity.passcode : passcode}
                          onChange={(e) => setNewActivity({...newActivity, passcode: e.target.value})}
                          placeholder="Enter access code"
                          style={{
                            marginLeft: '8px',
                            padding: '4px 8px',
                            border: '1px solid #007bff',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#007bff',
                            width: '150px'
                          }}
                        />
                      ) : (
                        <span style={{ fontWeight: 'bold', color: '#007bff', marginLeft: '8px' }}>{passcode}</span>
                      )}
                    </div>
                  )}

                  {/* Edit Club Button for Host - Inside club details section */}
                  {isHost && (
                    <button
                      type="button"
                      onClick={() => {
                        const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                        setScrollPosition(currentScrollPosition);
                        setClubSelectionFieldType('website_url');
                        setIsClubsModalOpen(true);
                        document.body.style.overflow = "hidden";
                      }}
                      style={{
                        width: '100%',
                        marginTop: '12px',
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'white',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                      }}
                    >
                      ✏️ Edit Club
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Designated Parent Section - Visible to both host and guests */}
            <div className="designated-parent-section" style={{
              marginTop: '24px',
              padding: '16px',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <label style={{ fontSize: '16px', color: '#2d3748', marginBottom: '8px', display: 'block', fontWeight: '600' }}>
                🚗 Designated Parent (Optional)
              </label>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                Select the parent responsible for taking {child.name} to this activity. This helps coordinate transportation and responsibilities.
              </p>
              <select
                value={(() => {
                  const value = newActivity.responsible_parent_uuid || (selectedActivity as any)?.responsible_parent_uuid || '';
                  console.log('🔍 DESIGNATED PARENT DROPDOWN VALUES:', {
                    newActivity_responsible_parent_uuid: newActivity.responsible_parent_uuid,
                    selectedActivity_responsible_parent_uuid: (selectedActivity as any)?.responsible_parent_uuid,
                    selectedActivity_responsible_parent_first_name: (selectedActivity as any)?.responsible_parent_first_name,
                    final_value: value,
                    accountParents: accountParents,
                    selectedActivity_keys: selectedActivity ? Object.keys(selectedActivity) : []
                  });
                  return value;
                })()}
                onChange={(e) => setNewActivity({...newActivity, responsible_parent_uuid: e.target.value || undefined})}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Not assigned</option>
                {accountParents.map((parent) => (
                  <option key={parent.uuid} value={parent.uuid}>
                    {parent.name}{parent.is_primary ? ' (Primary)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Kit Items Section - Only editable by host */}
            {selectedActivity.is_host && !(selectedActivity as any).isPendingInvitation && !(selectedActivity as any).isAcceptedInvitation && !(selectedActivity as any).isRejectedInvitation && (
              <div className="kit-items-section" style={{
                marginTop: '24px',
                padding: '16px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <label style={{ fontSize: '16px', color: '#2d3748', marginBottom: '8px', display: 'block', fontWeight: '600' }}>
                  🎒 Kit Items
                </label>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                  Add or remove items needed for this activity (e.g., PE kit, water bottle, shin pads)
                </p>

                {/* Current kit items */}
                <div style={{ marginBottom: '12px' }}>
                  {newActivity.kit_needed && newActivity.kit_needed.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {newActivity.kit_needed.map((item, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          background: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <span style={{ fontSize: '14px', color: '#2d3748' }}>{item}</span>
                          <button
                            onClick={() => {
                              const updatedKit = newActivity.kit_needed.filter((_, i) => i !== index);
                              setNewActivity({...newActivity, kit_needed: updatedKit});
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#dc3545',
                              cursor: 'pointer',
                              padding: '0 5px',
                              fontSize: '18px'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: '14px', color: '#999', fontStyle: 'italic' }}>No kit items added yet</p>
                  )}
                </div>

                {/* Add new kit item */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={newKitItem}
                    onChange={(e) => setNewKitItem(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newKitItem.trim()) {
                        const updatedKit = [...(newActivity.kit_needed || []), newKitItem.trim()];
                        setNewActivity({...newActivity, kit_needed: updatedKit});
                        setNewKitItem('');
                      }
                    }}
                    placeholder="Type kit item and press Enter or click Add"
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    onClick={() => {
                      if (newKitItem.trim()) {
                        const updatedKit = [...(newActivity.kit_needed || []), newKitItem.trim()];
                        setNewActivity({...newActivity, kit_needed: updatedKit});
                        setNewKitItem('');
                      }
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            )}

            {/* Activity Participants Section */}
            <div className="participants-section">
              <div className="detail-item">
                <strong>Who's Invited:</strong>
                {loadingParticipants ? (
                  <p>Loading participants...</p>
                ) : activityParticipants ? (
                  <div className="participants-list">
                    <div className="host-info">
                      <p>👤 <strong>{activityParticipants.host?.host_child_name || selectedActivity.child_name || 'Unknown Host'} (Host)</strong></p>
                    </div>
                    {activityParticipants.participants?.length > 0 ? (
                      <div className="invited-participants">
                        <h4>Invited Children:</h4>
                        {(() => {
                          const validParticipants = activityParticipants.participants.filter((participant: any) => participant.child_name && participant.child_uuid);
                          const isTentative = (selectedActivity as any).is_tentative;

                          console.log('🔍 PARTICIPANTS RENDER - isTentative:', isTentative);
                          console.log('🔍 PARTICIPANTS RENDER - activityParticipants.tentative_responses:', activityParticipants.tentative_responses);

                          // Create a map of tentative responses by child_uuid
                          const tentativeResponseMap: Record<string, string> = {};
                          if (isTentative && activityParticipants.tentative_responses) {
                            console.log('🔍 PARTICIPANTS RENDER - responses array:', activityParticipants.tentative_responses);
                            activityParticipants.tentative_responses.forEach((resp: any) => {
                              console.log('🔍 MAPPING response:', resp.child_uuid, '->', resp.response);
                              tentativeResponseMap[resp.child_uuid] = resp.response;
                            });
                          }

                          console.log('🔍 PARTICIPANTS RENDER - tentativeResponseMap:', tentativeResponseMap);

                          return validParticipants.length > 0 ? validParticipants.map((participant: any, index: number) => {
                            const tentativeResponse = tentativeResponseMap[participant.child_uuid];
                            console.log('🔍 PARTICIPANT:', participant.child_name, participant.child_uuid, '-> response:', tentativeResponse);

                            return (
                              <div key={index} className="participant-item">
                                <div className="participant-info">
                                  <span className="participant-name">👤 {participant.child_name}</span>
                                </div>
                                <div className="participant-status">
                                  {isTentative && tentativeResponse ? (
                                    // Show tentative response status
                                    tentativeResponse === 'interested' ? (
                                      <span style={{ color: '#48bb78', fontSize: '12px', fontWeight: 'bold' }}>👍 Interested</span>
                                    ) : (
                                      <span style={{ color: '#718096', fontSize: '12px' }}>👎 Not Interested</span>
                                    )
                                  ) : isTentative && participant.status === 'pending' ? (
                                    // Tentative activity but no response yet
                                    <span style={{ color: '#667eea', fontSize: '12px' }}>⏳ No Response Yet</span>
                                  ) : (
                                    // Regular activity statuses
                                    <>
                                      {participant.status === 'pending' && (
                                        <span style={{ color: '#fd7e14', fontSize: '12px' }}>📩 Pending</span>
                                      )}
                                      {participant.status === 'accepted' && (
                                        <span style={{ color: '#48bb78', fontSize: '12px' }}>✅ Accepted</span>
                                      )}
                                      {participant.status === 'rejected' && (
                                        <span style={{ color: '#a0aec0', fontSize: '12px' }}>❌ Rejected</span>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          }) : (
                            <p style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                              No other children invited yet
                            </p>
                          );
                        })()}
                      </div>
                    ) : (
                      <p style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                        No other children invited yet
                      </p>
                    )}
                    {/* Show batch participants (other people this activity was created for) */}
                    {activityParticipants.batch_participants?.length > 0 && (
                      <div className="batch-participants" style={{ marginTop: '16px' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Also Attending:</h4>
                        {activityParticipants.batch_participants.map((participant: any, index: number) => (
                          <div key={index} className="participant-item">
                            <div className="participant-info">
                              <span className="participant-name">
                                {participant.participant_type === 'parent' ? '👤' : '👦'} {participant.participant_name}
                                {participant.participant_type === 'parent' && <span style={{ fontSize: '11px', color: '#718096', marginLeft: '4px' }}>(Parent)</span>}
                              </span>
                            </div>
                            <div className="participant-status">
                              <span style={{ color: '#48bb78', fontSize: '12px' }}>✅ Going</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    Unable to load participant information
                  </p>
                )}
              </div>
            </div>

            {/* Kit Needed Section - Using activity.kit_needed and kit_tracking */}
            {(selectedActivity as any)?.kit_needed && Array.isArray((selectedActivity as any).kit_needed) && (selectedActivity as any).kit_needed.length > 0 && (() => {
              const kitNeeded = (selectedActivity as any).kit_needed;
              const activityUuid = selectedActivity.uuid || (selectedActivity as any).activity_uuid;
              const childUuid = selectedActivity.child_uuid || (selectedActivity as any).child_uuid;
              const kitTrackingFromActivity = (selectedActivity as any).kit_tracking || {};

              // Get tracking data for this child at this activity using activityUuid-childUuid key
              const trackingKey = `${activityUuid}-${childUuid}`;
              const childTracking = kitTrackingFromActivity[trackingKey] || {};

              // Build tracking object in the format the UI expects
              const trackingForActivity: Record<string, { isPacked: boolean, isCustom: boolean }> = {};
              kitNeeded.forEach((item: string) => {
                trackingForActivity[item] = {
                  isPacked: childTracking[item] || false,
                  isCustom: false
                };
              });

              return (
                <div className="kit-needed-section" style={{
                  marginTop: '24px',
                  padding: '16px',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                  }}>
                    <strong style={{ fontSize: '16px', color: '#2d3748' }}>🎒 Kit Needed</strong>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Kit items from activity.kit_needed */}
                    {kitNeeded.map((item: string, kitIndex: number) => {
                      const trackingData = trackingForActivity[item] || { isPacked: false, isCustom: false };
                      return (
                        <div key={kitIndex} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          background: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <input
                            type="checkbox"
                            checked={trackingData.isPacked}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleKitItem(item, trackingData.isPacked);
                            }}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span style={{
                            flex: 1,
                            fontSize: '14px',
                            color: trackingData.isPacked ? '#a0aec0' : '#2d3748',
                            textDecoration: trackingData.isPacked ? 'line-through' : 'none'
                          }}>
                            {item}
                          </span>
                        </div>
                      );
                    })}

                    {/* Custom items - DISABLED for now */}
                    {kitTracking && Object.entries(kitTracking.tracking || {}).map(([item, data]: [string, any]) => {
                      if (!data.isCustom) return null;
                      return (
                        <div key={item} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          background: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <input
                            type="checkbox"
                            checked={data.isPacked}
                            onChange={() => handleToggleKitItem(item, data.isPacked)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span style={{
                            flex: 1,
                            fontSize: '14px',
                            color: data.isPacked ? '#a0aec0' : '#2d3748',
                            textDecoration: data.isPacked ? 'line-through' : 'none'
                          }}>
                            {item}
                          </span>
                          <span style={{
                            fontSize: '11px',
                            color: '#805ad5',
                            background: '#f3e8ff',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: 600
                          }}>
                            Custom
                          </span>
                          <button
                            onClick={() => {
                              if (window.confirm(`Remove "${item}" from your kit list?`)) {
                                handleRemoveCustomKitItem(item);
                              }
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#e53e3e',
                              fontSize: '18px',
                              cursor: 'pointer',
                              padding: '0 4px',
                              lineHeight: 1
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}

                    {/* Add custom item - DISABLED for now */}
                  </div>
                </div>
              );
            })()}

            {/* Invite More Children - Inline Checkboxes */}
            {selectedActivity.is_host && !(selectedActivity as any).isPendingInvitation && !(selectedActivity as any).isAcceptedInvitation && !(selectedActivity as any).isRejectedInvitation && (
              <div className="invite-children-section">
                <div className="children-selection-header">
                  <label>Invite More Children:</label>
                  {(connectedChildren.length > 0 || pendingConnectionRequests.length > 0) && (
                    <div className="selection-controls">
                      <button
                        type="button"
                        onClick={() => {
                          const availableConnectedChildren = connectedChildren.filter(child => {
                            const isAlreadyInvited = activityParticipants?.participants?.some((p: any) => p.child_uuid === child.uuid);
                            return !isAlreadyInvited;
                          });
                          const allConnectedUuids = availableConnectedChildren.map(child => child.uuid);
                          
                          const availablePendingRequests = pendingConnectionRequests.filter(request => {
                            const targetChildUuid = request.target_child_uuid;
                            const targetParentUuid = request.target_parent_uuid;
                            const isAlreadyConnected = connectedChildren.some(child => 
                              child.uuid === targetChildUuid || 
                              child.parentUuid === targetParentUuid
                            );
                            const isAlreadyInvited = activityParticipants?.participants?.some((p: any) => 
                              p.child_uuid === targetChildUuid
                            );
                            return !isAlreadyConnected && !isAlreadyInvited;
                          });
                          const allPendingIds = availablePendingRequests.map(request => `pending-child-${request.target_child_uuid || request.target_parent_uuid}`);
                          const allIds = [...allConnectedUuids, ...allPendingIds];
                          
                          const allSelected = allIds.every(id => selectedConnectedChildren.includes(id));
                          if (allSelected) {
                            setSelectedClassUuids([]);
      setSelectedConnectedChildren([]);
                          } else {
                            setSelectedConnectedChildren(allIds);
                          }
                        }}
                        className="select-all-btn"
                      >
                        {(() => {
                          const availableConnectedChildren = connectedChildren.filter(child => {
                            const isAlreadyInvited = activityParticipants?.participants?.some((p: any) => p.child_uuid === child.uuid);
                            return !isAlreadyInvited;
                          });
                          const availablePendingRequests = pendingConnectionRequests.filter(request => {
                            const targetChildUuid = request.target_child_uuid;
                            const targetParentUuid = request.target_parent_uuid;
                            const isAlreadyConnected = connectedChildren.some(child => 
                              child.uuid === targetChildUuid || 
                              child.parentUuid === targetParentUuid
                            );
                            const isAlreadyInvited = activityParticipants?.participants?.some((p: any) => 
                              p.child_uuid === targetChildUuid
                            );
                            return !isAlreadyConnected && !isAlreadyInvited;
                          });
                          const allConnectedUuids = availableConnectedChildren.map(child => child.uuid);
                          const allPendingIds = availablePendingRequests.map(request => `pending-child-${request.target_child_uuid || request.target_parent_uuid}`);
                          const allIds = [...allConnectedUuids, ...allPendingIds];
                          const allSelected = allIds.every(id => selectedConnectedChildren.includes(id));
                          const totalCount = availableConnectedChildren.length + availablePendingRequests.length;
                          return `${allSelected ? 'Deselect All' : 'Select All'} (${totalCount})`;
                        })()}
                      </button>
                    </div>
                  )}
                </div>

                {connectedChildren.length === 0 && pendingConnectionRequests.length === 0 ? (
                  <p style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                    No connected children found. Connect with other families first to invite their children.
                  </p>
                ) : (
                  <>
                    <div className="children-list">
                      {/* Confirmed connections */}
                      {connectedChildren
                        .filter(child => {
                          const isAlreadyInvited = activityParticipants?.participants?.some((p: any) => p.child_uuid === child.uuid);
                          return !isAlreadyInvited;
                        })
                        .map((connectedChild) => (
                          <label key={connectedChild.uuid} className="child-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedConnectedChildren.includes(connectedChild.uuid)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedConnectedChildren([...selectedConnectedChildren, connectedChild.uuid]);
                                } else {
                                  setSelectedConnectedChildren(
                                    selectedConnectedChildren.filter(id => id !== connectedChild.uuid)
                                  );
                                }
                              }}
                            />
                            ✅ {connectedChild.name}
                          </label>
                        ))}
                      
                      {/* Pending connection requests */}
                      {pendingConnectionRequests
                        .filter((request) => {
                          // Don't show pending connections if they're already invited to this activity
                          const isAlreadyInvited = activityParticipants?.participants?.some((p: any) => 
                            p.child_uuid === request.target_child_uuid
                          );
                          return !isAlreadyInvited;
                        })
                        .map((request) => (
                          <label key={`pending-${request.id}`} className="child-checkbox" style={{ opacity: 0.7 }}>
                            <input
                              type="checkbox"
                              checked={selectedConnectedChildren.includes(generatePendingChildId(request))}
                              onChange={(e) => {
                                const pendingId = generatePendingChildId(request);
                                if (e.target.checked) {
                                  setSelectedConnectedChildren([...selectedConnectedChildren, pendingId]);
                                } else {
                                  setSelectedConnectedChildren(
                                    selectedConnectedChildren.filter(id => id !== pendingId)
                                  );
                                }
                              }}
                            />
                            ⏳ {request.target_child_name || `${request.target_parent_name || request.target_family_name || 'Unknown Parent'} (Any Child)`} - <em style={{ fontSize: '12px' }}>pending connection</em>
                          </label>
                        ))}
                      
                      {connectedChildren.filter(child => {
                        const isAlreadyInvited = activityParticipants?.participants?.some((p: any) => p.child_uuid === child.uuid);
                        return !isAlreadyInvited;
                      }).length === 0 && pendingConnectionRequests.filter(request => {
                        const isAlreadyInvited = activityParticipants?.participants?.some((p: any) => 
                          p.child_uuid === request.target_child_uuid
                        );
                        return !isAlreadyInvited;
                      }).length === 0 && (
                        <p style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                          All connected children have already been invited to this activity.
                        </p>
                      )}
                    </div>

                  </>
                )}
              </div>
            )}

            {/* Activity Reminder Section - Available on iOS/Android */}
            <ActivityReminderComponent
              activityUuid={selectedActivity.uuid || (selectedActivity as any).activity_uuid || ''}
              activityName={selectedActivity.name}
              startDate={selectedActivity.start_date}
              startTime={selectedActivity.start_time || ''}
              endTime={selectedActivity.end_time || ''}
              childName={child?.name}
              location={(selectedActivity as any).venue || (selectedActivity as any).location || ''}
              responsibleParentUuid={(selectedActivity as any).responsible_parent_uuid}
              currentUserUuid={user?.uuid}
              responsibleParentName={(selectedActivity as any).responsible_parent_first_name}
              reminderMinutes={(selectedActivity as any).reminder_minutes}
              reminderTarget={(selectedActivity as any).reminder_target}
              onReminderChange={(minutes, target) => {
                // Update local state when reminder is changed
                setSelectedActivity({
                  ...selectedActivity,
                  reminder_minutes: minutes,
                  reminder_target: target
                } as any);
                // Also update in activities array
                setActivities(prev => prev.map(a =>
                  (a.uuid === selectedActivity.uuid || (a as any).activity_uuid === (selectedActivity as any).activity_uuid)
                    ? { ...a, reminder_minutes: minutes, reminder_target: target } as any
                    : a
                ));
              }}
            />
          </div>
          <div className="page-actions">
            {/* Show interested/not interested buttons for tentative activities */}
            {(() => {
              const isTentative = (selectedActivity as any).is_tentative;
              const isPending = invitationData?.isPendingInvitation !== undefined
                ? invitationData.isPendingInvitation
                : (selectedActivity as any).isPendingInvitation;
              const isDeclined = invitationData?.isDeclinedInvitation !== undefined
                ? invitationData.isDeclinedInvitation
                : (selectedActivity as any).isDeclinedInvitation;
              const isAccepted = invitationData?.isAcceptedInvitation !== undefined
                ? invitationData.isAcceptedInvitation
                : (selectedActivity as any).isAcceptedInvitation;
              const hasInvitationUuid = (selectedActivity as any).invitationUuid || invitationData?.invitationUuid;
              const isHostActivity = selectedActivity.is_host;

              // Show tentative response buttons for non-host guests with pending invitations to tentative activities
              const shouldShow = isTentative && !isHostActivity && isPending && !isDeclined && !isAccepted && hasInvitationUuid;

              return shouldShow;
            })() && (
              <>
                <button
                  onClick={async () => {
                    // For pending invitations, activity_uuid is the correct field
                    const activityUuid = (selectedActivity as any).activity_uuid || selectedActivity.uuid;
                    const childUuid = child.uuid;

                    console.log('🔍 TENTATIVE RESPONSE: activityUuid:', activityUuid, 'childUuid:', childUuid);

                    try {
                      const response = await apiService.respondToTentativeActivity(activityUuid, childUuid, 'interested');
                      if (response.success) {
                        alert('Response sent! The host will be notified that you\'re interested.');
                        // Refresh the activity data
                        if (onBack) {
                          onBack();
                        } else {
                          navigateToPage('main');
                        }
                      } else {
                        alert(`Error: ${response.error || 'Failed to send response'}`);
                      }
                    } catch (error: any) {
                      alert(`Error: ${error.message || 'Failed to send response'}`);
                    }
                  }}
                  className="confirm-btn"
                  style={{ background: 'linear-gradient(135deg, #48bb78, #68d391)', color: 'white', marginRight: '8px' }}
                >
                  👍 I'm Interested
                </button>
                <button
                  onClick={async () => {
                    // For pending invitations, activity_uuid is the correct field
                    const activityUuid = (selectedActivity as any).activity_uuid || selectedActivity.uuid;
                    const childUuid = child.uuid;

                    console.log('🔍 TENTATIVE RESPONSE: activityUuid:', activityUuid, 'childUuid:', childUuid);

                    try {
                      const response = await apiService.respondToTentativeActivity(activityUuid, childUuid, 'not_interested');
                      if (response.success) {
                        alert('Response sent! The host will be notified.');
                        // Refresh the activity data
                        if (onBack) {
                          onBack();
                        } else {
                          navigateToPage('main');
                        }
                      } else {
                        alert(`Error: ${response.error || 'Failed to send response'}`);
                      }
                    } catch (error: any) {
                      alert(`Error: ${error.message || 'Failed to send response'}`);
                    }
                  }}
                  className="cancel-btn"
                  style={{ background: 'linear-gradient(135deg, #e53e3e, #fc8181)', color: 'white' }}
                >
                  👎 Not Interested
                </button>
              </>
            )}

            {/* Show accept/decline buttons for pending invitations (non-tentative) */}
            {(() => {
              const isTentative = (selectedActivity as any).is_tentative;
              const isPending = invitationData?.isPendingInvitation !== undefined
                ? invitationData.isPendingInvitation
                : (selectedActivity as any).isPendingInvitation;
              const isDeclined = invitationData?.isDeclinedInvitation !== undefined
                ? invitationData.isDeclinedInvitation
                : (selectedActivity as any).isDeclinedInvitation;
              const isAccepted = invitationData?.isAcceptedInvitation !== undefined
                ? invitationData.isAcceptedInvitation
                : (selectedActivity as any).isAcceptedInvitation;
              const hasInvitationUuid = (selectedActivity as any).invitationUuid || invitationData?.invitationUuid;

              // Only show for truly pending non-tentative invitations (not declined or accepted)
              const shouldShow = !isTentative && isPending && !isDeclined && !isAccepted && hasInvitationUuid;


              return shouldShow;
            })() && (
              <>
                <button
                  onClick={() => {
                    const invitationUuid = (selectedActivity as any).invitationUuid || invitationData?.invitationUuid;
                    handleInvitationResponse(invitationUuid, 'accept');
                  }}
                  className="confirm-btn"
                  style={{ background: 'linear-gradient(135deg, #48bb78, #68d391)', marginRight: '8px' }}
                >
                  Accept Invitation
                </button>
                <button
                  onClick={() => {
                    const invitationUuid = (selectedActivity as any).invitationUuid || invitationData?.invitationUuid;
                    handleInvitationResponse(invitationUuid, 'reject');
                  }}
                  className="cancel-btn"
                  style={{ background: 'linear-gradient(135deg, #e53e3e, #fc8181)', color: 'white' }}
                >
                  Decline Invitation
                </button>
              </>
            )}
            
            {/* Note: Decline button for accepted invitations is handled in the main guest decline logic below */}
            
            {/* Show accept button for rejected invitations */}
            {(() => {
              // Prioritize invitationData over selectedActivity for correct status
              const isPendingInvitation = invitationData?.isPendingInvitation !== undefined
                ? invitationData.isPendingInvitation
                : (selectedActivity as any).isPendingInvitation;
              const isRejectedInvitation = invitationData?.isDeclinedInvitation !== undefined
                ? invitationData.isDeclinedInvitation
                : ((selectedActivity as any).isRejectedInvitation || (selectedActivity as any).invitation_status === 'rejected');
              const hasDeclinedInvitation = invitationData?.isDeclinedInvitation;
              const hasInvitationUuid = (selectedActivity as any).invitationUuid ||
                                        (selectedActivity as any).invitation_uuid ||
                                        (selectedActivity as any).invitationId ||
                                        invitationData?.invitationUuid;
              const isHostActivity = selectedActivity.is_host;

              // Show "Can Now Attend" for guests who have declined/rejected invitations (NOT for pending!)
              // Note: Uses activity UUID (not invitation UUID) for reactivation API
              const hasActivityUuid = selectedActivity.uuid || (selectedActivity as any).activity_uuid;
              const shouldShow = !isHostActivity && !isPendingInvitation && (isRejectedInvitation || hasDeclinedInvitation) && hasActivityUuid;


              return shouldShow;
            })() && (
              <button
                onClick={() => handleGuestCanNowAttend(selectedActivity)}
                className="confirm-btn"
                style={{
                  background: 'linear-gradient(135deg, #48bb78, #68d391)',
                  color: 'white',
                  marginRight: '8px'
                }}
              >
                Can Now Attend
              </button>
            )}
            
            {/* Show enhanced buttons for activity host */}
            {selectedActivity.is_host && !(selectedActivity as any).isPendingInvitation && !(selectedActivity as any).isAcceptedInvitation && !(selectedActivity as any).isRejectedInvitation && (
              <>
                {/* Show different buttons based on tentative, cancelled, or active status */}
                {(selectedActivity as any).is_tentative && activityParticipants?.tentative_responses?.some((r: any) => r.response === 'interested') ? (
                  // Buttons for tentative activities with interested responses
                  <>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Are you sure you want to confirm this activity? All invited children will receive a notification that the activity is confirmed.')) {
                          return;
                        }

                        try {
                          const activityUuid = selectedActivity.uuid || (selectedActivity as any).activity_uuid;
                          const response = await apiService.confirmTentativeActivity(activityUuid);
                          if (response.success) {
                            alert('Activity confirmed! Notifications sent to all invited children.');
                            // Refresh the page to show updated activity
                            if (onBack) {
                              onBack();
                            } else {
                              navigateToPage('main');
                            }
                          } else {
                            alert(`Error: ${response.error || 'Failed to confirm activity'}`);
                          }
                        } catch (error: any) {
                          alert(`Error: ${error.message || 'Failed to confirm activity'}`);
                        }
                      }}
                      className="confirm-btn"
                      style={{ background: 'linear-gradient(135deg, #48bb78, #68d391)', color: 'white' }}
                    >
                      Confirm Activity ({activityParticipants.tentative_responses.filter((r: any) => r.response === 'interested').length} Interested)
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Are you sure you want to delete this tentative activity? All invited children will receive a notification that the activity has been cancelled.')) {
                          return;
                        }

                        try {
                          await handleDeleteActivity(selectedActivity);
                          // handleDeleteActivity already shows alerts and navigates
                        } catch (error: any) {
                          alert(`Error: ${error.message || 'Failed to delete activity'}`);
                        }
                      }}
                      className="cancel-btn"
                      style={{ background: 'linear-gradient(135deg, #e53e3e, #fc8181)', color: 'white' }}
                    >
                      Delete Activity
                    </button>
                  </>
                ) : selectedActivity.is_cancelled ? (
                  // Buttons for cancelled activities
                  <>
                    <button
                      onClick={() => handleCanNowAttend(selectedActivity)}
                      className="confirm-btn"
                      style={{ background: 'linear-gradient(135deg, #48bb78, #68d391)', color: 'white' }}
                    >
                      Can Now Attend
                    </button>
                    <button
                      onClick={() => handleDeleteActivity(selectedActivity)}
                      className="cancel-btn"
                      style={{ background: 'linear-gradient(135deg, #e53e3e, #fc8181)', color: 'white' }}
                    >
                      Delete Activity
                    </button>
                  </>
                ) : (
                  // Buttons for active activities
                  <>
                    <button
                      onClick={handleUpdateActivity}
                      className="confirm-btn"
                      style={{ background: 'linear-gradient(135deg, #28a745, #34ce57)' }}
                    >
                      Save Changes{selectedConnectedChildren.length > 0 ? ` & Send Invitations (${selectedConnectedChildren.length})` : ''}
                    </button>
                    <button
                      onClick={() => handleDeclineClick(selectedActivity)}
                      className="cancel-btn"
                      style={{ background: 'linear-gradient(135deg, #ff8c00, #ffa500)', color: 'white' }}
                    >
                      Can't Attend
                    </button>
                    <button
                      onClick={() => handleDeleteActivity(selectedActivity)}
                      className="cancel-btn"
                      style={{ background: 'linear-gradient(135deg, #e53e3e, #fc8181)', color: 'white' }}
                    >
                      Delete Activity
                    </button>
                  </>
                )}
              </>
            )}
            
            {/* For non-host activities, show informational text (only when no invitation buttons are shown) */}

            {/* Declined invitation message removed - users can click "Can Now Attend" button */}

            {/* Decline/Not Attending Actions for Guests Only */}
            {(() => {
              // Show decline/delete buttons ONLY for guests (not hosts) with accepted invitations:
              // 1. User has an accepted invitation
              // DO NOT show if:
              // - User is the host
              // - Activity is cancelled
              // - User has already declined the invitation
              // - User has a pending invitation (handled by Accept/Decline buttons above)
              const isHostActivity = selectedActivity.is_host;
              const isActivityCancelled = selectedActivity.is_cancelled;

              // Check both invitationData and selectedActivity for status (OR logic, not ternary)
              // This ensures we detect accepted/declined status from either source
              const hasPendingInvitation = invitationData?.isPendingInvitation
                || !!((selectedActivity as any).isPendingInvitation || (selectedActivity as any).invitation_status === 'pending');
              const hasAcceptedInvitation = invitationData?.isAcceptedInvitation
                || !!((selectedActivity as any).isAcceptedInvitation || (selectedActivity as any).invitation_status === 'accepted');
              const hasDeclinedInvitation = invitationData?.isDeclinedInvitation
                || !!((selectedActivity as any).isDeclinedInvitation || (selectedActivity as any).invitation_status === 'rejected' || (selectedActivity as any).invitation_status === 'declined');
              const isRejectedInvitation = hasDeclinedInvitation;
              const hasInvitationUuid = (selectedActivity as any).invitationUuid ||
                                        (selectedActivity as any).invitation_uuid ||
                                        invitationData?.invitationUuid ||
                                        // For reactivated activities, if we have invitation_status, we can show the button
                                        (selectedActivity as any).invitation_status;

              // Check if this is a private activity (Pitchero/SOCS) that can be deleted
              const activitySource = (selectedActivity as any).activity_source;
              const isPrivateActivity = activitySource === 'pitchero' || activitySource === 'socs';

              // Only show buttons if user is a guest with ACCEPTED invitation (not pending - that's handled above)
              // AND has not already declined AND has an invitation UUID
              const shouldShow = !isHostActivity &&
                                !isActivityCancelled &&
                                !hasDeclinedInvitation &&
                                !isRejectedInvitation &&
                                !hasPendingInvitation &&  // Don't show for pending - Accept/Decline buttons handle that
                                hasInvitationUuid &&
                                hasAcceptedInvitation;

              console.log('🔍 ACCEPTED INVITATION BUTTONS DEBUG:', {
                shouldShow,
                isHostActivity,
                isActivityCancelled,
                hasDeclinedInvitation,
                isRejectedInvitation,
                hasPendingInvitation,
                hasInvitationUuid,
                hasAcceptedInvitation,
                isPrivateActivity,
                activitySource,
                invitationData,
                selectedActivity_isAcceptedInvitation: (selectedActivity as any).isAcceptedInvitation,
                selectedActivity_invitation_status: (selectedActivity as any).invitation_status
              });

              return shouldShow;
            })() && (
              <div className="decline-actions" style={{
                marginTop: '16px',
                textAlign: 'center',
                display: 'flex',
                gap: '12px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => handleDeclineClick(selectedActivity)}
                  className="cancel-btn"
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #e53e3e, #fc8181)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '15px',
                    minWidth: '100px'
                  }}
                >
                  Decline
                </button>
                {/* Show Delete button only for private activities (Pitchero/SOCS) */}
                {(() => {
                  const activitySource = (selectedActivity as any).activity_source;
                  const isPrivateActivity = activitySource === 'pitchero' || activitySource === 'socs';
                  return isPrivateActivity;
                })() && (
                  <button
                    onClick={() => handleDeleteActivity(selectedActivity)}
                    className="cancel-btn"
                    style={{
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #dc3545, #e74c3c)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '15px',
                      minWidth: '100px'
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            )}

            {/* Close button - always show for all activities */}
            <button
              onClick={goBack}
              className="cancel-btn"
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #718096, #a0aec0)',
                color: 'white',
                border: '1px solid #718096',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '15px',
                minWidth: '100px'
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* Delete Activity Dialog for Recurring Activities */}
        {(() => {
          return null;
        })()}
        {showDeleteDialog && deleteDialogActivity && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}>
            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '400px',
              margin: '20px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <h3 style={{
                marginTop: 0,
                marginBottom: '16px',
                color: '#e53e3e',
                fontWeight: '600',
                fontSize: '18px'
              }}>
                🗑️ Delete Activity
              </h3>
              <p style={{
                marginBottom: '20px',
                lineHeight: '1.6',
                color: '#4a5568'
              }}>
                This activity "<strong>{deleteDialogActivity.name}</strong>" is part of a recurring series.
              </p>
              <p style={{
                marginBottom: '20px',
                lineHeight: '1.6',
                color: '#4a5568'
              }}>
                What would you like to delete?
              </p>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '12px',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  backgroundColor: deleteOption === 'single' ? '#f0f8ff' : 'transparent',
                  border: deleteOption === 'single' ? '1px solid #4299e1' : '1px solid transparent'
                }}>
                  <input
                    type="radio"
                    name="deleteOption"
                    value="single"
                    checked={deleteOption === 'single'}
                    onChange={(e) => setDeleteOption(e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Delete just this session
                  </span>
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  backgroundColor: deleteOption === 'series' ? '#f0f8ff' : 'transparent',
                  border: deleteOption === 'series' ? '1px solid #4299e1' : '1px solid transparent'
                }}>
                  <input
                    type="radio"
                    name="deleteOption"
                    value="series"
                    checked={deleteOption === 'series'}
                    onChange={(e) => setDeleteOption(e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Delete entire series
                  </span>
                </label>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={handleDeleteDialogClose}
                  style={{
                    padding: '10px 16px',
                    background: '#f7fafc',
                    color: '#4a5568',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleDeleteDialogClose();
                    deleteActivity(deleteDialogActivity, deleteOption === 'series');
                  }}
                  style={{
                    padding: '10px 16px',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Update Activity Dialog */}
        {showUpdateDialog && updateDialogActivity && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}>
            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '400px',
              margin: '20px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <h3 style={{
                marginTop: 0,
                marginBottom: '16px',
                color: '#4299e1',
                fontWeight: '600',
                fontSize: '18px'
              }}>
                ✏️ Update Activity
              </h3>
              <p style={{
                marginBottom: '20px',
                lineHeight: '1.6',
                color: '#4a5568'
              }}>
                This activity "<strong>{updateDialogActivity.name}</strong>" is part of a recurring series.
              </p>
              <p style={{
                marginBottom: '20px',
                lineHeight: '1.6',
                color: '#4a5568'
              }}>
                What would you like to update?
              </p>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '12px',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  backgroundColor: updateOption === 'single' ? '#f0f8ff' : 'transparent',
                  border: updateOption === 'single' ? '1px solid #4299e1' : '1px solid transparent'
                }}>
                  <input
                    type="radio"
                    name="updateOption"
                    value="single"
                    checked={updateOption === 'single'}
                    onChange={(e) => setUpdateOption(e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Update just this session
                  </span>
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  backgroundColor: updateOption === 'series' ? '#f0f8ff' : 'transparent',
                  border: updateOption === 'series' ? '1px solid #4299e1' : '1px solid transparent'
                }}>
                  <input
                    type="radio"
                    name="updateOption"
                    value="series"
                    checked={updateOption === 'series'}
                    onChange={(e) => setUpdateOption(e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Update entire series
                  </span>
                </label>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => {
                    setShowUpdateDialog(false);
                    setUpdateDialogActivity(null);
                    setUpdateOption('single');
                  }}
                  style={{
                    padding: '10px 16px',
                    background: '#f7fafc',
                    color: '#4a5568',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowUpdateDialog(false);
                    // Don't clear updateDialogActivity yet - it will be used to determine which option was chosen
                    // The handleUpdateActivity function will see that updateDialogActivity is set and proceed with the update
                    setTimeout(() => {
                      handleUpdateActivity();
                    }, 100);
                  }}
                  style={{
                    padding: '10px 16px',
                    background: '#4299e1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Decline Activity Dialog */}
        {showDeclineDialog && declineDialogActivity && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}>
            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '400px',
              margin: '20px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <h3 style={{
                marginTop: 0,
                marginBottom: '16px',
                color: '#e53e3e',
                fontWeight: '600',
                fontSize: '18px'
              }}>
                {declineDialogActivity.is_host ? '🚫 Cancel Activity' : '❌ Can\'t Attend'}
              </h3>

              {(() => {
                // Check if activity is part of a recurring series
                const seriesId = (declineDialogActivity as any).series_id;

                // Ignore migration artifacts (series_id starting with "migrated_")
                const isMigrationArtifact = seriesId && typeof seriesId === 'string' && seriesId.startsWith('migrated_');

                // If activity has a valid series_id, it's part of a series
                const isSeriesActivity = seriesId && !isMigrationArtifact;

                // Debug logging
                console.log('🔍 DECLINE SERIES DETECTION DEBUG:', {
                  activityName: declineDialogActivity.name,
                  seriesId,
                  isMigrationArtifact,
                  isSeriesActivity
                });

                return isSeriesActivity;
              })() ? (
                <>
                  <p style={{
                    marginBottom: '20px',
                    lineHeight: '1.6',
                    color: '#4a5568'
                  }}>
                    This activity "<strong>{declineDialogActivity.name}</strong>" is part of a recurring series.
                  </p>
                  <p style={{
                    marginBottom: '20px',
                    lineHeight: '1.6',
                    color: '#4a5568'
                  }}>
                    {declineDialogActivity.is_host
                      ? 'Which sessions would you like to cancel?'
                      : 'Which sessions can\'t you attend?'
                    }
                  </p>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '12px',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '6px',
                      backgroundColor: declineOption === 'single' ? '#f0f8ff' : 'transparent',
                      border: declineOption === 'single' ? '1px solid #4299e1' : '1px solid transparent'
                    }}>
                      <input
                        type="radio"
                        name="declineOption"
                        value="single"
                        checked={declineOption === 'single'}
                        onChange={(e) => setDeclineOption(e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>
                        {declineDialogActivity.is_host ? 'Cancel just this session' : 'Can\'t attend just this session'}
                      </span>
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '6px',
                      backgroundColor: declineOption === 'series' ? '#f0f8ff' : 'transparent',
                      border: declineOption === 'series' ? '1px solid #4299e1' : '1px solid transparent'
                    }}>
                      <input
                        type="radio"
                        name="declineOption"
                        value="series"
                        checked={declineOption === 'series'}
                        onChange={(e) => setDeclineOption(e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>
                        {declineDialogActivity.is_host ? 'Cancel entire series' : 'Can\'t attend entire series'}
                      </span>
                    </label>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      onClick={handleDeclineDialogClose}
                      style={{
                        padding: '10px 16px',
                        background: '#f7fafc',
                        color: '#4a5568',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        handleDeclineDialogClose();
                        declineActivity(declineDialogActivity, declineOption === 'series');
                      }}
                      style={{
                        padding: '10px 16px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Decline
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{
                    marginBottom: '24px',
                    lineHeight: '1.6',
                    color: '#4a5568'
                  }}>
                    {declineDialogActivity.is_host
                      ? `Are you sure you want to cancel "${declineDialogActivity.name}"? This will notify all participants.`
                      : (() => {
                          const activitySource = declineDialogActivity.activity_source || declineDialogActivity.source;
                          const isPitchero = activitySource === 'pitchero';
                          return isPitchero
                            ? `Are you sure you can't attend "${declineDialogActivity.name}"? The host will be notified.`
                            : `Are you sure you can't attend "${declineDialogActivity.name}"?`;
                        })()
                    }
                  </p>
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      onClick={handleDeclineDialogClose}
                      style={{
                        padding: '10px 16px',
                        background: '#f7fafc',
                        color: '#4a5568',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        handleDeclineDialogClose();
                        declineActivity(declineDialogActivity, false);
                      }}
                      style={{
                        padding: '10px 16px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      {declineDialogActivity.is_host ? 'Cancel Activity' : 'Decline'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Reactivate Activity Dialog */}
        {showReactivateDialog && reactivateDialogActivity && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}>
            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '400px',
              margin: '20px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <h3 style={{
                marginTop: 0,
                marginBottom: '16px',
                color: '#38a169',
                fontWeight: '600',
                fontSize: '18px'
              }}>
                ✅ Can Now Attend
              </h3>

              {(() => {
                // Check if activity is part of a recurring series
                const seriesId = (reactivateDialogActivity as any).series_id;

                // Ignore migration artifacts (series_id starting with "migrated_")
                const isMigrationArtifact = seriesId && typeof seriesId === 'string' && seriesId.startsWith('migrated_');

                // If activity has a valid series_id, it's part of a series
                const isSeriesActivity = seriesId && !isMigrationArtifact;

                // Debug logging
                console.log('🔍 REACTIVATE SERIES DETECTION DEBUG:', {
                  activityName: reactivateDialogActivity.name,
                  seriesId,
                  isMigrationArtifact,
                  isSeriesActivity
                });

                return isSeriesActivity;
              })() ? (
                <>
                  <p style={{
                    marginBottom: '20px',
                    lineHeight: '1.6',
                    color: '#4a5568'
                  }}>
                    This activity "<strong>{reactivateDialogActivity.name}</strong>" is part of a recurring series.
                  </p>
                  <p style={{
                    marginBottom: '20px',
                    lineHeight: '1.6',
                    color: '#4a5568'
                  }}>
                    {reactivateDialogActivity.is_host
                      ? 'Which sessions would you like to reactivate?'
                      : 'Which sessions can you now attend?'
                    }
                  </p>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '12px',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '6px',
                      backgroundColor: reactivateOption === 'single' ? '#f0fff4' : 'transparent',
                      border: reactivateOption === 'single' ? '1px solid #38a169' : '1px solid transparent'
                    }}>
                      <input
                        type="radio"
                        name="reactivateOption"
                        value="single"
                        checked={reactivateOption === 'single'}
                        onChange={(e) => setReactivateOption(e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>
                        {reactivateDialogActivity.is_host ? 'Reactivate just this session' : 'Can attend just this session'}
                      </span>
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '6px',
                      backgroundColor: reactivateOption === 'series' ? '#f0fff4' : 'transparent',
                      border: reactivateOption === 'series' ? '1px solid #38a169' : '1px solid transparent'
                    }}>
                      <input
                        type="radio"
                        name="reactivateOption"
                        value="series"
                        checked={reactivateOption === 'series'}
                        onChange={(e) => setReactivateOption(e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>
                        {reactivateDialogActivity.is_host ? 'Reactivate entire series' : 'Can attend entire series'}
                      </span>
                    </label>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      onClick={handleReactivateDialogClose}
                      style={{
                        padding: '10px 16px',
                        background: '#f7fafc',
                        color: '#4a5568',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        handleReactivateDialogClose();
                        if (reactivateDialogActivity?.is_host) {
                          reactivateActivity(reactivateDialogActivity, reactivateOption === 'series');
                        } else {
                          guestReactivateActivity(reactivateDialogActivity, reactivateOption === 'series');
                        }
                      }}
                      style={{
                        padding: '10px 16px',
                        background: '#38a169',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Reactivate
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{
                    marginBottom: '24px',
                    lineHeight: '1.6',
                    color: '#4a5568'
                  }}>
                    {reactivateDialogActivity.is_host
                      ? `Are you sure you want to reactivate "${reactivateDialogActivity.name}"? This will notify all participants.`
                      : `Are you sure you can now attend "${reactivateDialogActivity.name}"? This will notify the host.`
                    }
                  </p>

                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      onClick={handleReactivateDialogClose}
                      style={{
                        padding: '10px 16px',
                        background: '#f7fafc',
                        color: '#4a5568',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        handleReactivateDialogClose();
                        if (reactivateDialogActivity?.is_host) {
                          reactivateActivity(reactivateDialogActivity, false);
                        } else {
                          guestReactivateActivity(reactivateDialogActivity, false);
                        }
                      }}
                      style={{
                        padding: '10px 16px',
                        background: '#38a169',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      {reactivateDialogActivity.is_host ? 'Reactivate Activity' : 'Accept'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Accept Invitation Dialog */}
        {showAcceptDialog && acceptDialogData && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}>
            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '400px',
              margin: '20px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <h3 style={{
                marginTop: 0,
                marginBottom: '16px',
                color: '#48bb78',
                fontWeight: '600',
                fontSize: '18px'
              }}>
                ✅ Accept Invitation
              </h3>

              <p style={{
                marginBottom: '20px',
                lineHeight: '1.6',
                color: '#4a5568',
                fontSize: '14px'
              }}>
                This is a recurring activity "<strong>{acceptDialogData.activityName}</strong>" with <strong>{acceptDialogData.sessionCount}</strong> pending sessions.
              </p>

              <div style={{
                marginBottom: '24px',
                padding: '12px',
                background: '#f7fafc',
                borderRadius: '8px'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  color: '#718096',
                  lineHeight: '1.5'
                }}>
                  Would you like to accept the entire series or just this single session?
                </p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '12px',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  backgroundColor: acceptOption === 'single' ? '#f0fff4' : 'transparent',
                  border: acceptOption === 'single' ? '1px solid #48bb78' : '1px solid transparent'
                }}>
                  <input
                    type="radio"
                    name="acceptOption"
                    value="single"
                    checked={acceptOption === 'single'}
                    onChange={(e) => setAcceptOption(e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Accept just this session
                  </span>
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  backgroundColor: acceptOption === 'series' ? '#f0fff4' : 'transparent',
                  border: acceptOption === 'series' ? '1px solid #48bb78' : '1px solid transparent'
                }}>
                  <input
                    type="radio"
                    name="acceptOption"
                    value="series"
                    checked={acceptOption === 'series'}
                    onChange={(e) => setAcceptOption(e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Accept entire series
                  </span>
                </label>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={handleAcceptDialogClose}
                  style={{
                    padding: '10px 16px',
                    background: '#f7fafc',
                    color: '#4a5568',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => executeAcceptInvitation(acceptOption === 'series')}
                  style={{
                    padding: '10px 16px',
                    background: 'linear-gradient(135deg, #48bb78, #68d391)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Decline Invitation Dialog */}
        {showDeclineDialog && declineDialogData && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
          }}>
            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '400px',
              margin: '20px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <h3 style={{
                marginTop: 0,
                marginBottom: '16px',
                color: '#e53e3e',
                fontWeight: '600',
                fontSize: '18px'
              }}>
                ❌ Decline Invitation
              </h3>

              <p style={{
                marginBottom: '20px',
                lineHeight: '1.6',
                color: '#4a5568',
                fontSize: '14px'
              }}>
                This is a recurring activity "<strong>{declineDialogData.activityName}</strong>" with <strong>{declineDialogData.sessionCount}</strong> pending sessions.
              </p>

              <div style={{
                marginBottom: '24px',
                padding: '12px',
                background: '#f7fafc',
                borderRadius: '8px'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  color: '#718096',
                  lineHeight: '1.5'
                }}>
                  Would you like to decline the entire series or just this single session?
                </p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '12px',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  backgroundColor: declineInvitationOption === 'single' ? '#fff5f5' : 'transparent',
                  border: declineInvitationOption === 'single' ? '1px solid #e53e3e' : '1px solid transparent'
                }}>
                  <input
                    type="radio"
                    name="declineInvitationOption"
                    value="single"
                    checked={declineInvitationOption === 'single'}
                    onChange={(e) => setDeclineInvitationOption(e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Decline just this session
                  </span>
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  backgroundColor: declineInvitationOption === 'series' ? '#fff5f5' : 'transparent',
                  border: declineInvitationOption === 'series' ? '1px solid #e53e3e' : '1px solid transparent'
                }}>
                  <input
                    type="radio"
                    name="declineInvitationOption"
                    value="series"
                    checked={declineInvitationOption === 'series'}
                    onChange={(e) => setDeclineInvitationOption(e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Decline entire series
                  </span>
                </label>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={handleDeclineInvitationDialogClose}
                  style={{
                    padding: '10px 16px',
                    background: '#f7fafc',
                    color: '#4a5568',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => executeDeclineInvitation(declineInvitationOption === 'series')}
                  style={{
                    padding: '10px 16px',
                    background: 'linear-gradient(135deg, #e53e3e, #fc8181)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Clubs Modal - for activity-detail page */}
        {(() => {
          return isClubsModalOpen;
        })() && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000
            }}
          >
            <div
              style={{
                background: 'white',
                width: '90%',
                height: '90%',
                borderRadius: '12px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div
                style={{
                  padding: '20px',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <h2>Select Club for Activity</h2>
                <button
                  onClick={() => closeClubsModal()}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '5px 10px'
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{ flex: 1, overflow: 'auto' }}>
                <ClubsScreen
                  isSelectionMode={true}
                  selectionFieldType={clubSelectionFieldType}
                  onClubSelect={closeClubsModal}
                  onCancel={() => closeClubsModal()}
                />
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }

  if (currentPage === 'add-activity') {
    // Determine who the activity is for (multi-select support)
    const selectedChildNames = selectedParticipantChildUuids
      .map(uuid => {
        if (uuid === child.uuid) return child.name;
        return parentChildren.find(c => c.uuid === uuid)?.name;
      })
      .filter(Boolean);
    const participantName = selectedChildNames.length > 0
      ? selectedChildNames.join(', ')
      : child.name; // Fallback to child if nothing selected

    return (
      <div className="child-activity-screen">
        <div className="activity-header">
          <div className="child-info">
            <h2>Add New Activity</h2>
          </div>
        </div>
        <div className="page-content">
          {activityDraft && (
            <div className="draft-notification" style={{
              background: 'linear-gradient(135deg, #48bb78, #68d391)',
              color: 'white',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              📝 <strong>Draft Restored:</strong> Your activity details have been saved and restored from when you went to create connections.
              <button
                onClick={clearActivityDraft}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  marginLeft: '8px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Clear Draft
              </button>
            </div>
          )}

          {/* When a class is the audience, the class is the answer to "who is
              this for" — the host child is implied and shown, not chosen. */}
          {isClassActivity && (
            <div style={{
              marginBottom: '20px',
              padding: '14px 16px',
              background: '#f3e9fc',
              borderRadius: '12px',
              border: '1px solid #d9bdf2'
            }}>
              <div style={{ fontWeight: 600, color: '#2d3748', fontSize: '15px' }}>
                For your class
              </div>
              {repClasses.filter((g: any) => selectedClassUuids.includes(g.uuid)).map((group: any) => (
                <div key={group.uuid} style={{ fontSize: '14px', color: '#6b21a8', marginTop: '4px' }}>
                  🎓 {group.name}
                  <span style={{ color: '#666' }}>
                    {' '}— {(group.members || []).length + (group.reps || []).length} child
                    {(group.members || []).length + (group.reps || []).length === 1 ? '' : 'ren'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Participant Selector - Who is this activity for? (Multi-select) */}
          {!isClassActivity && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            background: '#f8f9fa',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <label style={{
              display: 'block',
              marginBottom: '4px',
              fontWeight: '600',
              color: '#2d3748',
              fontSize: '16px'
            }}>
              Who is this activity for? *
            </label>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px', marginTop: 0 }}>
              Select one or more participants. The activity will appear on each selected person's calendar.
            </p>

            {/* Children Section */}
            {parentChildren.filter(c => !c.is_parent).length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px', fontWeight: '500' }}>
                  Children:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {parentChildren.filter(c => !c.is_parent).map((childOption: any) => (
                    <label
                      key={childOption.uuid}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: selectedParticipantChildUuids.includes(childOption.uuid)
                          ? '2px solid #1976d2'
                          : '1px solid #e2e8f0',
                        background: selectedParticipantChildUuids.includes(childOption.uuid)
                          ? '#e3f2fd'
                          : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedParticipantChildUuids.includes(childOption.uuid)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedParticipantChildUuids([...selectedParticipantChildUuids, childOption.uuid]);
                          } else {
                            setSelectedParticipantChildUuids(selectedParticipantChildUuids.filter(uuid => uuid !== childOption.uuid));
                          }
                        }}
                        style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{
                        fontWeight: selectedParticipantChildUuids.includes(childOption.uuid) ? '600' : '400',
                        color: selectedParticipantChildUuids.includes(childOption.uuid) ? '#1976d2' : '#333',
                        fontSize: '14px'
                      }}>
                        👤 {childOption.display_name || childOption.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Classes this parent reps. Not a participant — the activity does
                not land on a class's calendar — but the audience the rep is
                really addressing, so it belongs with the same question. */}
            {repClasses.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px', fontWeight: '500' }}>
                  Classes you rep:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {repClasses.map((group: any) => {
                    const selected = selectedClassUuids.includes(group.uuid);
                    // The rep's own child is in the class as well as the
                    // children they added.
                    const total = (group.members || []).length + (group.reps || []).length;
                    const waiting = (group.members || []).filter((m: any) => m.status !== 'accepted').length;
                    return (
                      <label
                        key={group.uuid}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 14px',
                          borderRadius: '8px',
                          border: selected ? '2px solid #8B3FD9' : '1px solid #e2e8f0',
                          background: selected ? '#f3e9fc' : 'white',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            setSelectedClassUuids(e.target.checked
                              ? [...selectedClassUuids, group.uuid]
                              : selectedClassUuids.filter(uuid => uuid !== group.uuid));
                          }}
                          style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{
                          fontWeight: selected ? '600' : '400',
                          color: selected ? '#8B3FD9' : '#333',
                          fontSize: '14px'
                        }}>
                          🎓 {group.name}
                          <span style={{ color: '#666', fontWeight: 400 }}>
                            {' '}— {total} child{total === 1 ? '' : 'ren'}
                            {waiting ? ` · ${waiting} not yet accepted` : ''}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Parents Section - using parent-child records */}
            {parentChildren.filter(c => c.is_parent).length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px', fontWeight: '500' }}>
                  Parents:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {parentChildren.filter(c => c.is_parent).map((parentChild: any) => (
                    <label
                      key={parentChild.uuid}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: selectedParticipantChildUuids.includes(parentChild.uuid)
                          ? '2px solid #9575cd'
                          : '1px solid #e2e8f0',
                        background: selectedParticipantChildUuids.includes(parentChild.uuid)
                          ? '#ede7f6'
                          : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedParticipantChildUuids.includes(parentChild.uuid)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedParticipantChildUuids([...selectedParticipantChildUuids, parentChild.uuid]);
                          } else {
                            setSelectedParticipantChildUuids(selectedParticipantChildUuids.filter(uuid => uuid !== parentChild.uuid));
                          }
                        }}
                        style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{
                        fontWeight: selectedParticipantChildUuids.includes(parentChild.uuid) ? '600' : '400',
                        color: selectedParticipantChildUuids.includes(parentChild.uuid) ? '#7c4dff' : '#333',
                        fontSize: '14px'
                      }}>
                        👔 {parentChild.display_name || parentChild.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Selection summary */}
            {selectedParticipantChildUuids.length > 0 && (
              <div style={{
                marginTop: '12px',
                padding: '10px 12px',
                background: '#e8f5e9',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#2e7d32'
              }}>
                <strong>✓ Selected ({selectedParticipantChildUuids.length}):</strong>{' '}
                {participantName}
              </div>
            )}
          </div>
          )}

          <div className="page-form">
            {/* Activity Name and Template Selection Row */}
            <div className="activity-name-template-row">
              <div className="activity-name-input-wrapper">
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#2d3748',
                  fontSize: '16px'
                }}>
                  Activity Name *
                </label>
                <input
                  type="text"
                  placeholder="Enter activity name"
                  value={newActivity.name}
                  onChange={(e) => setNewActivity({...newActivity, name: e.target.value})}
                  className="modal-input"
                  autoFocus={!Capacitor.isNativePlatform()}
                  style={{
                    width: '100%',
                    marginBottom: '0'
                  }}
                />
              </div>

              {!isClassActivity && (
              <div className="template-select-wrapper">
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#2d3748',
                  fontSize: '16px'
                }}>
                  OR Use Template
                </label>
                {loadingTemplates ? (
                  <div style={{ 
                    padding: '12px', 
                    background: '#f8f9fa', 
                    borderRadius: '8px',
                    color: '#666',
                    textAlign: 'center',
                    fontSize: '14px'
                  }}>
                    Loading...
                  </div>
                ) : (
                  <select
                    value={selectedTemplate ? selectedTemplate.uuid : ''}
                    onChange={(e) => {
                      const selectedUuid = e.target.value;
                      if (selectedUuid === '') {
                        handleTemplateSelect(null);
                      } else {
                        const template = activityTemplates.find(t => t.uuid === selectedUuid);
                        if (template) {
                          handleTemplateSelect(template);
                        }
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      fontSize: '14px',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">None</option>
                    {activityTemplates.length === 0 ? (
                      <option value="" disabled style={{ fontStyle: 'italic', color: '#999' }}>
                        No templates yet
                      </option>
                    ) : (
                      activityTemplates.map((template) => (
                        <option key={template.uuid} value={template.uuid}>
                          {template.name}
                        </option>
                      ))
                    )}
                  </select>
                )}
              </div>
              )}
            </div>
            
            {/* Template Confirmation Message */}
            {templateConfirmationMessage && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                background: '#d4edda',
                color: '#155724',
                borderRadius: '8px',
                border: '1px solid #c3e6cb',
                fontSize: '14px'
              }}>
                ✅ {templateConfirmationMessage}
              </div>
            )}

            {/* Tentative Activity Option - Prominent placement */}
            {!isClassActivity && (
            <div className="tentative-section" style={{ marginBottom: '20px' }}>
              <label className="section-label">Activity Type</label>
              <div style={{
                background: '#f0f4ff',
                padding: '16px',
                borderRadius: '8px',
                border: '2px solid #667eea'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                  gap: '12px'
                }}>
                  <input
                    type="checkbox"
                    checked={newActivity.is_tentative || false}
                    onChange={(e) => setNewActivity({...newActivity, is_tentative: e.target.checked})}
                    style={{
                      marginTop: '4px',
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: '700', fontSize: '16px', color: '#2d3748' }}>
                      📋 Send as Tentative Invite
                    </span>
                    <p style={{
                      fontSize: '14px',
                      color: '#4a5568',
                      marginTop: '6px',
                      lineHeight: '1.5',
                      fontWeight: '500'
                    }}>
                      Gauge interest first before confirming. Guests can respond if they're interested, and you can confirm the activity later based on responses.
                    </p>
                  </div>
                </label>
              </div>
            </div>
            )}

            {/* Joint Host Selection - Now integrated into participant selector above */}
            {/* The multi-select participant UI replaces the old joint host functionality */}

            {/* Responsible Parent Selection */}
            {!isClassActivity && (
            <div className="responsible-parent-section" style={{ marginBottom: '20px' }}>
              <label className="section-label">Designated Parent (Optional)</label>
              <p className="section-description">
                {selectedParticipantChildUuids.length > 0
                  ? `Select the parent responsible for this activity. This helps coordinate transportation and responsibilities.`
                  : 'Select the parent responsible for this activity. This helps coordinate responsibilities.'
                }
              </p>
              <select
                value={newActivity.responsible_parent_uuid || ''}
                onChange={(e) => setNewActivity({...newActivity, responsible_parent_uuid: e.target.value || undefined})}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  marginTop: '8px'
                }}
              >
                <option value="">Not assigned</option>
                {/* Account Parents Only */}
                {accountParents.map((parent) => (
                  <option key={parent.uuid} value={parent.uuid}>
                    {parent.name}{parent.is_primary ? ' (Primary)' : ''}
                  </option>
                ))}
              </select>
            </div>
            )}

            <div className="date-selection-section">
              <label className="section-label">Activity Schedule</label>
              <div className="recurring-toggle" style={{ marginBottom: '16px' }}>
                <div className="toggle-options">
                  <label className="radio-option" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '12px 16px', 
                    margin: '8px 0',
                    background: !isRecurringActivity ? '#e6f3ff' : '#f8f9fa',
                    borderRadius: '8px',
                    border: !isRecurringActivity ? '2px solid #0066cc' : '1px solid #e2e8f0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      name="activityType"
                      checked={!isRecurringActivity}
                      onChange={() => {
                        setIsRecurringActivity(false);
                        // Clear recurring-specific state when switching to single activity
                        setRecurringDaysOfWeek([]);
                        setRecurringStartDate('');
                        setRecurringEndDate('');
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    <span>📅 Single Date Activity</span>
                  </label>
                  <label className="radio-option" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '12px 16px', 
                    margin: '8px 0',
                    background: isRecurringActivity ? '#e6f3ff' : '#f8f9fa',
                    borderRadius: '8px',
                    border: isRecurringActivity ? '2px solid #0066cc' : '1px solid #e2e8f0',
                    cursor: (isSharedActivity || jointHostChildren.length > 0) ? 'not-allowed' : 'pointer',
                    opacity: (isSharedActivity || jointHostChildren.length > 0) ? 0.5 : 1
                  }}>
                    <input
                      type="radio"
                      name="activityType"
                      checked={isRecurringActivity}
                      disabled={jointHostChildren.length > 0}
                      onChange={() => {
                        if (jointHostChildren.length === 0) {
                          setIsRecurringActivity(true);
                          // Clear single activity state when switching to recurring
                          setSelectedDates([]);
                          // Set default start date to today if not already set
                          if (!recurringStartDate) {
                            setRecurringStartDate(new Date().toISOString().split('T')[0]);
                          }
                          // Set default end date to 2 months from today if not already set
                          if (!recurringEndDate) {
                            const defaultEndDate = new Date();
                            defaultEndDate.setMonth(defaultEndDate.getMonth() + 2);
                            setRecurringEndDate(defaultEndDate.toISOString().split('T')[0]);
                          }
                        }
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    <span>🔄 Recurring Activity</span>
                    {jointHostChildren.length > 0 && (
                      <span style={{ 
                        fontSize: '12px', 
                        color: '#999', 
                        fontStyle: 'italic',
                        marginLeft: '8px' 
                      }}>
                        (Disabled for joint host activities)
                      </span>
                    )}
                  </label>
                </div>
              </div>

              {!isRecurringActivity ? (
                <div>
                  <p className="section-description">Click dates to select/deselect them. You can choose multiple dates.</p>
                  <CalendarDatePicker
                    selectedDates={selectedDates}
                    onChange={setSelectedDates}
                    minDate={new Date().toISOString().split('T')[0]}
                  />
                </div>
              ) : (
                <div className="recurring-options">
                  <p className="section-description">Select the date range and which days of the week this activity will repeat.</p>
                  
                  {/* Start and End Dates Side by Side */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '24px', 
                    marginBottom: '16px',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ 
                      flex: '1', 
                      minWidth: '150px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontWeight: '600',
                        textAlign: 'center',
                        width: '100%'
                      }}>Start Date:</label>
                      <input
                        type="date"
                        value={recurringStartDate}
                        onChange={(e) => setRecurringStartDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          fontSize: '16px',
                          width: '100%',
                          textAlign: 'center'
                        }}
                      />
                      {/* Native pickers render in the BROWSER's locale (08/18/2026 on a US
                          device); this echo states the date unambiguously. */}
                      {recurringStartDate && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {formatDateUK(recurringStartDate)}
                        </div>
                      )}
                    </div>
                    
                    <div style={{ 
                      flex: '1', 
                      minWidth: '150px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontWeight: '600',
                        textAlign: 'center',
                        width: '100%'
                      }}>End Date:</label>
                      <input
                        type="date"
                        value={recurringEndDate}
                        onChange={(e) => setRecurringEndDate(e.target.value)}
                        min={recurringStartDate || new Date().toISOString().split('T')[0]}
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          fontSize: '16px',
                          width: '100%',
                          textAlign: 'center'
                        }}
                      />
                      {/* Native pickers render in the BROWSER's locale (08/18/2026 on a US
                          device); this echo states the date unambiguously. */}
                      {recurringEndDate && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {formatDateUK(recurringEndDate)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600' }}>Days of the Week:</label>
                    <div className="days-of-week-selector" style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap',
                      gap: '6px',
                      marginBottom: '16px',
                      maxWidth: '100%',
                      justifyContent: 'space-between'
                    }}>
                      {[
                        { day: 'Sun', value: 0 },
                        { day: 'Mon', value: 1 },
                        { day: 'Tue', value: 2 },
                        { day: 'Wed', value: 3 },
                        { day: 'Thu', value: 4 },
                        { day: 'Fri', value: 5 },
                        { day: 'Sat', value: 6 }
                      ].map((dayOption) => (
                        <label key={dayOption.value} style={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '12px 6px',
                          background: recurringDaysOfWeek.includes(dayOption.value) ? '#e6f3ff' : '#f8f9fa',
                          borderRadius: '8px',
                          border: recurringDaysOfWeek.includes(dayOption.value) ? '2px solid #0066cc' : '1px solid #e2e8f0',
                          cursor: 'pointer',
                          textAlign: 'center',
                          fontSize: '13px',
                          minWidth: '45px',
                          flex: '1 1 auto',
                          maxWidth: '60px'
                        }}>
                          <input
                            type="checkbox"
                            checked={recurringDaysOfWeek.includes(dayOption.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setRecurringDaysOfWeek([...recurringDaysOfWeek, dayOption.value]);
                              } else {
                                setRecurringDaysOfWeek(recurringDaysOfWeek.filter(d => d !== dayOption.value));
                              }
                            }}
                            style={{ display: 'none' }}
                          />
                          <span style={{ fontWeight: '600', fontSize: '16px' }}>{dayOption.day}</span>
                        </label>
                      ))}
                    </div>
                    {recurringDaysOfWeek.length > 0 && (
                      <div style={{
                        padding: '12px',
                        background: '#d4edda',
                        color: '#155724',
                        borderRadius: '8px',
                        fontSize: '14px',
                        marginTop: '12px'
                      }}>
                        📋 This will create activities every{' '}
                        {recurringDaysOfWeek.map(day => 
                          ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][day]
                        ).join(', ')}{' '}
                        from {recurringStartDate ? new Date(recurringStartDate).toLocaleDateString('en-GB') : 'start date'} to {recurringEndDate ? new Date(recurringEndDate).toLocaleDateString('en-GB') : 'end date'}.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="time-row">
              <div className="time-field">
                <label>Start Time</label>
                <TimePickerDropdown
                  value={newActivity.start_time}
                  onChange={(time) => setNewActivity({...newActivity, start_time: time})}
                  placeholder="Start time"
                  defaultScrollPosition="start"
                />
              </div>
              <div className="time-field">
                <label>End Time</label>
                <TimePickerDropdown
                  value={newActivity.end_time}
                  onChange={(time) => setNewActivity({...newActivity, end_time: time})}
                  placeholder="End time"
                  defaultScrollPosition="end"
                />
              </div>
            </div>

            {/* Browse Clubs Button */}
            {!isClassActivity && (
            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
              <button
                type="button"
                onClick={() => {
                  const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                  setScrollPosition(currentScrollPosition);
                  setClubSelectionFieldType('website_url');
                  setIsClubsModalOpen(true);
                  document.body.style.overflow = "hidden";
                }}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
              >
                🏢 Browse Clubs
              </button>
              <p style={{
                fontSize: '13px',
                color: '#666',
                marginTop: '8px',
                textAlign: 'center'
              }}>
                Find and select a club to automatically fill activity details
              </p>
            </div>
            )}

            {/* Club Information Display (Read-only) - Only show if club has been selected */}
            {!isClassActivity && (newActivity as any).club_uuid && (
              <div style={{
                marginTop: '20px',
                marginBottom: '20px',
                padding: '16px',
                background: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>
                    📋 Club Details
                  </h4>
                  <button
                    type="button"
                    onClick={() => {
                      setNewActivity({
                        ...newActivity,
                        website_url: '',
                        location: '',
                        venue: '',
                        city: '',
                        activity_type: '',
                        cost: '',
                        max_participants: '',
                        contact_phone: '',
                        contact_email: '',
                        club_uuid: null,
                        club: null
                      } as any);
                    }}
                    style={{
                      padding: '6px 12px',
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      color: '#666'
                    }}
                  >
                    Clear
                  </button>
                </div>

                {(() => {
                  console.log('🔍 Rendering Club Details - newActivity.club:', (newActivity as any).club);
                  return (newActivity as any).club?.name && (
                    <div style={{ marginBottom: '8px' }}>
                      <strong style={{ fontSize: '13px', color: '#4a5568' }}>Club Name:</strong>{' '}
                      <span style={{ fontSize: '13px', color: '#2d3748', fontWeight: '500' }}>{(newActivity as any).club.name}</span>
                    </div>
                  );
                })()}

                {newActivity.website_url && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ fontSize: '13px', color: '#4a5568' }}>Website:</strong>{' '}
                    <a
                      href={newActivity.website_url.startsWith('http') ? newActivity.website_url : `https://${newActivity.website_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '13px', color: '#667eea', textDecoration: 'underline' }}
                    >
                      {newActivity.website_url}
                    </a>
                  </div>
                )}

                {newActivity.location && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ fontSize: '13px', color: '#4a5568' }}>Location:</strong>{' '}
                    <span style={{ fontSize: '13px', color: '#2d3748' }}>{newActivity.location}</span>
                  </div>
                )}

                {newActivity.venue && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ fontSize: '13px', color: '#4a5568' }}>Venue:</strong>{' '}
                    <span style={{ fontSize: '13px', color: '#2d3748' }}>{newActivity.venue}</span>
                  </div>
                )}

                {newActivity.city && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ fontSize: '13px', color: '#4a5568' }}>City:</strong>{' '}
                    <span style={{ fontSize: '13px', color: '#2d3748' }}>{newActivity.city}</span>
                  </div>
                )}

                {newActivity.activity_type && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ fontSize: '13px', color: '#4a5568' }}>Activity Type:</strong>{' '}
                    <span style={{ fontSize: '13px', color: '#2d3748' }}>{newActivity.activity_type}</span>
                  </div>
                )}

                {newActivity.cost && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ fontSize: '13px', color: '#4a5568' }}>Cost:</strong>{' '}
                    <span style={{ fontSize: '13px', color: '#2d3748' }}>£{newActivity.cost}</span>
                  </div>
                )}

                {newActivity.max_participants && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ fontSize: '13px', color: '#4a5568' }}>Max Participants:</strong>{' '}
                    <span style={{ fontSize: '13px', color: '#2d3748' }}>{newActivity.max_participants}</span>
                  </div>
                )}

                {newActivity.contact_phone && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ fontSize: '13px', color: '#4a5568' }}>Contact Phone:</strong>{' '}
                    <span style={{ fontSize: '13px', color: '#2d3748' }}>{newActivity.contact_phone}</span>
                  </div>
                )}

                {newActivity.contact_email && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ fontSize: '13px', color: '#4a5568' }}>Contact Email:</strong>{' '}
                    <span style={{ fontSize: '13px', color: '#2d3748' }}>{newActivity.contact_email}</span>
                  </div>
                )}
              </div>
            )}

            {/* Kit Needed Section */}
            <div className="kit-needed-section" style={{ marginTop: '20px' }}>
              <label className="section-label">Kit Needed (Optional)</label>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
                Add items participants should bring to this activity
              </p>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="e.g., Water bottle, Snacks, Sunscreen..."
                  className="modal-input"
                  style={{ flex: 1 }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const input = e.currentTarget;
                      const value = input.value.trim();
                      if (value && !newActivity.kit_needed.includes(value)) {
                        setNewActivity({
                          ...newActivity,
                          kit_needed: [...newActivity.kit_needed, value]
                        });
                        input.value = '';
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                    const value = input.value.trim();
                    if (!value) {
                      alert('Please enter an item name before clicking Add');
                      return;
                    }
                    if (!newActivity.kit_needed.includes(value)) {
                      setNewActivity({
                        ...newActivity,
                        kit_needed: [...newActivity.kit_needed, value]
                      });
                      input.value = '';
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Add Item
                </button>
              </div>

              {newActivity.kit_needed.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {newActivity.kit_needed.map((item: string, index: number) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        background: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      <span style={{ fontSize: '14px', color: '#2d3748' }}>{item}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setNewActivity({
                            ...newActivity,
                            kit_needed: newActivity.kit_needed.filter((_: string, i: number) => i !== index)
                          });
                        }}
                        style={{
                          padding: '4px 8px',
                          background: 'transparent',
                          color: '#e53e3e',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '18px',
                          fontWeight: 'bold'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Passcode Section */}
            {!isClassActivity && (
            <div className="passcode-section" style={{ marginTop: '20px' }}>
              <label className="section-label">Activity Passcode (Optional)</label>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
                Add a passcode for collecting the Children
              </p>
              <input
                type="text"
                placeholder=""
                value={newActivity.passcode || ''}
                onChange={(e) => setNewActivity({...newActivity, passcode: e.target.value})}
                className="modal-input"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px'
                }}
              />
            </div>
            )}

            {/* Designated Parent Section - Only for SOCS/Pitchero activities */}
            {(selectedActivity && ((selectedActivity as any).source === 'socs' || (selectedActivity as any).source === 'pitchero')) && (
              <div className="designated-parent-section" style={{ marginTop: '20px' }}>
                <label className="section-label">Designated Parent</label>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
                  Select which parent will be responsible for taking the child to this activity
                </p>
                <select
                  value={newActivity.responsible_parent_uuid || (selectedActivity as any)?.responsible_parent_uuid || ''}
                  onChange={(e) => setNewActivity({...newActivity, responsible_parent_uuid: e.target.value})}
                  className="modal-input"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select a parent...</option>
                  {accountParents.map((parent) => (
                    <option key={parent.uuid} value={parent.uuid}>
                      {parent.name} {parent.uuid === user?.uuid ? '(You)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Privacy section - only show when children are selected since parent activities aren't shared */}
            {!isClassActivity && selectedParticipantChildUuids.length > 0 && (
            <div className="privacy-section">
              <label className="section-label">Activity Privacy</label>
              <div className="privacy-options">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="privacyMode"
                    checked={!isSharedActivity}
                    onChange={() => setIsSharedActivity(false)}
                  />
                  🔒 Private (Only you and {child.name})
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="privacyMode"
                    checked={isSharedActivity}
                    onChange={() => {
                      setIsSharedActivity(true);
                    }}
                  />
                  🌐 Shared (Invite connected children)
                  {!isSharedActivity && connectedChildren.length === 0 && pendingConnectionRequests.length === 0 && (
                    <span style={{
                      fontSize: '12px',
                      color: '#667eea',
                      fontStyle: 'italic',
                      marginLeft: '8px'
                    }}>
                      - Select to create connections
                    </span>
                  )}
                </label>
              </div>
              
              {isSharedActivity && (
                <div className="connected-children-section">
                  {jointHostChildren.length === 0 ? (
                    // Single host - show original UI
                    <>
                      <div className="children-selection-header">
                        <label>Invite Children:</label>
                        {(connectedChildren.length > 0 || pendingConnectionRequests.length > 0) && (
                          <div className="selection-controls">
                            <button
                              type="button"
                              onClick={() => {
                                const allConnectedUuids = connectedChildren.map(child => child.uuid);
                                const allPendingIds = pendingConnectionRequests.map(request => generatePendingChildId(request));
                                const allIds = [...allConnectedUuids, ...allPendingIds];
                                
                                const allSelected = allIds.every(id => selectedConnectedChildren.includes(id));
                                if (allSelected) {
                                  setSelectedClassUuids([]);
      setSelectedConnectedChildren([]);
                                } else {
                                  setSelectedConnectedChildren(allIds);
                                }
                              }}
                              className="select-all-btn"
                            >
                              {(() => {
                                const allConnectedUuids = connectedChildren.map(child => child.uuid);
                                const allPendingIds = pendingConnectionRequests.map(request => generatePendingChildId(request));
                                const allIds = [...allConnectedUuids, ...allPendingIds];
                                const allSelected = allIds.every(id => selectedConnectedChildren.includes(id));
                                const totalCount = connectedChildren.length + pendingConnectionRequests.length;
                                return allSelected ? 'Deselect All' : 'Select All';
                              })()} ({connectedChildren.length + pendingConnectionRequests.length})
                            </button>
                          </div>
                        )}
                      </div>
                  {connectedChildren.length === 0 && pendingConnectionRequests.length === 0 ? (
                    <div className="no-connections">
                      <p><strong>No connections found.</strong></p>
                      <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                        {jointHostChildren.length > 0 
                          ? `To invite other children, you'll need to connect with their families first. Note: Joint host children (${parentChildren.filter(pc => jointHostChildren.includes(pc.uuid)).map(pc => pc.name).join(', ')}) will automatically have access to this activity.`
                          : 'To invite other children to this activity, you\'ll need to connect with their families first.'
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="children-list">
                      
                      {/* Joint hosting explanation */}
                      {jointHostChildren.length > 0 && (
                        <div style={{
                          background: '#e3f2fd',
                          border: '1px solid #2196f3',
                          borderRadius: '8px',
                          padding: '12px',
                          marginBottom: '12px',
                          fontSize: '14px'
                        }}>
                          <strong>📝 Joint Hosting Active:</strong> Invitations will be sent from both {child.name} and {parentChildren.filter(pc => jointHostChildren.includes(pc.uuid)).map(pc => pc.name).join(', ')} to the selected children below. Each invited child will see separate invitations from each host child.
                        </div>
                      )}
                      
                      {/* Connection Search */}
                      {(connectedChildren.length + pendingConnectionRequests.length) > 5 && (
                        <div style={{ marginBottom: '12px' }}>
                          <input
                            type="text"
                            placeholder="🔍 Search connections..."
                            value={connectionSearchTerm}
                            onChange={(e) => setConnectionSearchTerm(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: '2px solid #e0e0e0',
                              borderRadius: '6px',
                              fontSize: '14px',
                              outline: 'none',
                              transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#2196f3'}
                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                          />
                        </div>
                      )}
                      
                      {/* Confirmed connections */}
                      {getFilteredAndSortedConnections().map((connectedChild) => (
                        <label key={connectedChild.uuid} className="child-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedConnectedChildren.includes(connectedChild.uuid)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedConnectedChildren([...selectedConnectedChildren, connectedChild.uuid]);
                              } else {
                                setSelectedConnectedChildren(
                                  selectedConnectedChildren.filter(id => id !== connectedChild.uuid)
                                );
                              }
                            }}
                          />
                          ✅ {connectedChild.name}
                        </label>
                      ))}
                      
                      {/* Pending connection requests */}
                      {getFilteredAndSortedPendingRequests().map((request) => (
                        <label key={`pending-${request.id}`} className="child-checkbox" style={{ opacity: 0.7 }}>
                          <input
                            type="checkbox"
                            checked={selectedConnectedChildren.includes(generatePendingChildId(request))}
                            onChange={(e) => {
                              const pendingId = generatePendingChildId(request);
                              if (e.target.checked) {
                                setSelectedConnectedChildren([...selectedConnectedChildren, pendingId]);
                              } else {
                                setSelectedConnectedChildren(
                                  selectedConnectedChildren.filter(id => id !== pendingId)
                                );
                              }
                            }}
                          />
                          ⏳ {request.target_child_name || `${request.target_parent_name || request.target_family_name || 'Unknown Parent'} (Any Child)`} - <em style={{ fontSize: '12px' }}>pending connection</em>
                        </label>
                      ))}
                      
                      {/* No search results message */}
                      {connectionSearchTerm.trim() && 
                       getFilteredAndSortedConnections().length === 0 && 
                       getFilteredAndSortedPendingRequests().length === 0 && (
                        <div style={{ 
                          padding: '16px',
                          textAlign: 'center',
                          color: '#666',
                          fontStyle: 'italic',
                          fontSize: '14px'
                        }}>
                          No connections found matching "{connectionSearchTerm}"
                        </div>
                      )}
                      
                      {connectedChildren.length === 0 && pendingConnectionRequests.length > 0 && (
                        <p style={{ fontSize: '14px', color: '#666', fontStyle: 'italic', marginTop: '12px' }}>
                          You can select pending connections above. They'll be invited automatically when they accept your connection request.
                        </p>
                      )}

                    </div>
                  )}
                  
                    </>
                  ) : (
                    // Joint hosting - show separate sections for each host child
                    <div className="joint-host-invitations">
                      <div className="joint-host-header">
                        <h4>Invite Children by Host:</h4>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                          Select which children to invite from each host's contacts. Invitations will be sent from the respective host child.
                        </p>
                      </div>
                      
                      {/* Main host child section */}
                      <div className="host-section" style={{ marginBottom: '20px' }}>
                        <div className="host-section-header" style={{ 
                          background: '#e3f2fd', 
                          padding: '12px', 
                          borderRadius: '8px', 
                          marginBottom: '12px',
                          border: '2px solid #2196f3'
                        }}>
                          <h5 style={{ margin: '0 0 4px 0', color: '#1976d2' }}>
                            👑 {child.name}'s Contacts (Primary Host)
                          </h5>
                          <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                            Invitations from this section will be sent by {child.name}
                          </p>
                        </div>
                        
                        {connectedChildren.length === 0 && pendingConnectionRequests.length === 0 ? (
                          <p style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                            No connections found for {child.name}
                          </p>
                        ) : (
                          <div className="children-list">
                            {connectedChildren.map((connectedChild) => (
                              <label key={`main-${connectedChild.uuid}`} className="child-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedConnectedChildren.includes(`main-${connectedChild.uuid}`)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedConnectedChildren([...selectedConnectedChildren, `main-${connectedChild.uuid}`]);
                                    } else {
                                      setSelectedConnectedChildren(
                                        selectedConnectedChildren.filter(id => id !== `main-${connectedChild.uuid}`)
                                      );
                                    }
                                  }}
                                />
                                {connectedChild.status === 'pending' ? '⏳' : '✅'} {connectedChild.name}{connectedChild.status === 'pending' ? ' (pending)' : ''}
                              </label>
                            ))}
                            
                            {/* Pending connection requests for main host */}
                            {pendingConnectionRequests.map((request) => (
                              <label key={`main-pending-${request.id}`} className="child-checkbox" style={{ opacity: 0.7 }}>
                                <input
                                  type="checkbox"
                                  checked={selectedConnectedChildren.includes(`main-pending-child-${request.target_child_uuid || request.target_parent_uuid}`)}
                                  onChange={(e) => {
                                    const pendingId = `main-pending-child-${request.target_child_uuid || request.target_parent_uuid}`;
                                    if (e.target.checked) {
                                      setSelectedConnectedChildren([...selectedConnectedChildren, pendingId]);
                                    } else {
                                      setSelectedConnectedChildren(
                                        selectedConnectedChildren.filter(id => id !== pendingId)
                                      );
                                    }
                                  }}
                                />
                                ⏳ {request.target_child_name || `${request.target_parent_name || request.target_family_name || 'Unknown Parent'} (Any Child)`} - <em style={{ fontSize: '12px' }}>pending connection</em>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Joint host children sections */}
                      {jointHostChildren.map((jointChildUuid) => {
                        const jointChild = parentChildren.find(pc => pc.uuid === jointChildUuid);
                        if (!jointChild) return null;
                        
                        return (
                          <div key={jointChildUuid} className="host-section" style={{ marginBottom: '20px' }}>
                            <div className="host-section-header" style={{ 
                              background: '#f3e5f5', 
                              padding: '12px', 
                              borderRadius: '8px', 
                              marginBottom: '12px',
                              border: '2px solid #9c27b0'
                            }}>
                              <h5 style={{ margin: '0 0 4px 0', color: '#7b1fa2' }}>
                                🤝 {jointChild.name}'s Contacts (Joint Host)
                              </h5>
                              <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                                Invitations from this section will be sent by {jointChild.name}
                              </p>
                            </div>
                            
                            {(() => {
                              const jointChildConnections = jointHostConnections[jointChildUuid] || [];
                              
                              if (jointChildConnections.length === 0) {
                                return (
                                  <p style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                                    {jointHostConnections[jointChildUuid] === undefined 
                                      ? `Loading connections for ${jointChild.name}...`
                                      : `No connections found for ${jointChild.name}`
                                    }
                                  </p>
                                );
                              }
                              
                              return (
                                <div className="children-list">
                                  {jointChildConnections.map((connectedChild) => (
                                    <label key={`joint-${jointChildUuid}-${connectedChild.uuid}`} className="child-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={selectedConnectedChildren.includes(`joint-${jointChildUuid}-${connectedChild.uuid}`)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedConnectedChildren([...selectedConnectedChildren, `joint-${jointChildUuid}-${connectedChild.uuid}`]);
                                          } else {
                                            setSelectedConnectedChildren(
                                              selectedConnectedChildren.filter(id => id !== `joint-${jointChildUuid}-${connectedChild.uuid}`)
                                            );
                                          }
                                        }}
                                      />
                                      {connectedChild.status === 'pending' ? '⏳' : '✅'} {connectedChild.name}{connectedChild.status === 'pending' ? ' (pending)' : ''}
                                    </label>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Always visible Add New Connection button - appears in all scenarios */}
                  <div style={{ marginTop: '24px', marginBottom: '20px', textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={handleNavigateToConnections}
                      style={{
                        background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '700',
                        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                        transition: 'all 0.2s ease',
                        minWidth: '200px'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(79, 70, 229, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 70, 229, 0.3)';
                      }}
                    >
                      Add New Connection
                    </button>
                  </div>

                  {/* Auto-notification setting for shared activities */}
                </div>
              )}
            </div>
            )}

            {/* Set Reminder - Only show on iOS/Android */}
            {Capacitor.isNativePlatform() && (
              <div className="reminder-section" style={{
                marginTop: '20px',
                padding: '16px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#2d3748',
                  fontSize: '16px'
                }}>
                  {'\u23F0'} Set Reminder (Optional)
                </label>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                  Get notified before this activity
                </p>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
                  Remind me for:
                </label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  {([
                    { label: 'Start Time', value: 'start' as const },
                    { label: 'End Time', value: 'end' as const },
                    { label: 'Both', value: 'both' as const }
                  ]).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSelectedReminderTarget(opt.value)}
                      style={{
                        flex: 1,
                        padding: '10px 8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: selectedReminderTarget === opt.value ? 'white' : '#4a5568',
                        background: selectedReminderTarget === opt.value
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'white',
                        border: selectedReminderTarget === opt.value
                          ? '2px solid #667eea'
                          : '2px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <select
                  value={selectedReminderMinutes ?? ''}
                  onChange={(e) => setSelectedReminderMinutes(e.target.value ? parseInt(e.target.value, 10) : null)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">No reminder</option>
                  {REMINDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {selectedReminderMinutes !== null && selectedReminderTarget === null && (
                  <p style={{ fontSize: '12px', color: '#e53e3e', marginTop: '8px' }}>
                    Please select whether this reminder is for the start time, end time, or both.
                  </p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="page-actions">
              <button
                onClick={handleCancelActivity}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateActivity}
                disabled={addingActivity}
                className={`confirm-btn ${addingActivity ? 'disabled' : ''}`}
              >
                {addingActivity ? 'Adding...' : 'Add Activity'}
              </button>
            </div>
          </div>
        </div>

        {/* Custom Template Dialog with Yes/No buttons */}
        {(() => {
          const shouldShow = showTemplateDialog;
          return shouldShow;
        })() && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{
                fontSize: '16px',
                lineHeight: '1.5',
                color: '#333',
                marginBottom: '25px',
                whiteSpace: 'pre-line'
              }}>
                {templateDialogMessage}
              </div>
              <div style={{
                display: 'flex',
                gap: '15px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={handleTemplateYes}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px 30px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Yes
                </button>
                <button
                  onClick={handleTemplateNo}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 30px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Clubs Modal */}
        {(() => {
          return isClubsModalOpen;
        })() && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000
            }}
          >
            <div
              style={{
                background: 'white',
                width: '90%',
                height: '90%',
                borderRadius: '12px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div
                style={{
                  padding: '20px',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <h2>Select Club for Activity</h2>
                <button
                  onClick={() => closeClubsModal()}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '5px 10px'
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{ flex: 1, overflow: 'auto' }}>
                <ClubsScreen
                  isSelectionMode={true}
                  selectionFieldType={clubSelectionFieldType}
                  onClubSelect={closeClubsModal}
                  onCancel={() => closeClubsModal()}
                />
              </div>
            </div>
          </div>
        )}

        {/* Connections Modal */}
        {isConnectionsModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000
            }}
          >
            <div
              style={{
                background: 'white',
                width: '90%',
                height: '90%',
                borderRadius: '12px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div
                style={{
                  padding: '20px',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <h2>Add New Connection</h2>
                <button
                  onClick={() => closeConnectionsModal()}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '5px 10px'
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{ flex: 1, overflow: 'auto' }}>
                <ConnectionsScreen />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (currentPage === 'edit-activity' && editingActivity) {
    return (
      <div className="child-activity-screen">
        <div className="activity-header">
          <div className="child-info">
            <h2>Edit Activity: {editingActivity.name}</h2>
          </div>
        </div>
        <div className="page-content">
          <div className="page-form">
            <input
              type="text"
              placeholder="Activity name *"
              value={newActivity.name}
              onChange={(e) => setNewActivity({...newActivity, name: e.target.value})}
              className="modal-input"
              autoFocus={!Capacitor.isNativePlatform()}
            />
            <textarea
              placeholder="Description"
              value={newActivity.description}
              onChange={(e) => setNewActivity({...newActivity, description: e.target.value})}
              className="modal-textarea"
              rows={3}
            />

            {/* Responsible Parent Selection */}
            <div className="responsible-parent-section" style={{ marginBottom: '20px' }}>
              <label className="section-label">Who's Taking {child.name}? (Optional)</label>
              <p className="section-description">
                Select the parent responsible for taking {child.name} to this activity.
              </p>
              <select
                value={newActivity.responsible_parent_uuid || ''}
                onChange={(e) => setNewActivity({...newActivity, responsible_parent_uuid: e.target.value || undefined})}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  marginTop: '8px'
                }}
              >
                <option value="">Not assigned</option>
                {/* Account Parents Only */}
                {accountParents.map((parent) => (
                  <option key={parent.uuid} value={parent.uuid}>
                    {parent.name}{parent.is_primary ? ' (Primary)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Reminder Selection - Only show on native platforms */}
            {Capacitor.isNativePlatform() && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#2d3748',
                  fontSize: '16px'
                }}>
                  {'\u23F0'} Set Reminder (Optional)
                </label>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                  {newActivity.responsible_parent_uuid
                    ? 'The designated parent will receive the alarm notification.'
                    : 'Both parents will receive the alarm notification.'}
                </p>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
                  Remind me for:
                </label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  {([
                    { label: 'Start Time', value: 'start' as const },
                    { label: 'End Time', value: 'end' as const },
                    { label: 'Both', value: 'both' as const }
                  ]).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSelectedReminderTarget(opt.value)}
                      style={{
                        flex: 1,
                        padding: '10px 8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: selectedReminderTarget === opt.value ? 'white' : '#4a5568',
                        background: selectedReminderTarget === opt.value
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'white',
                        border: selectedReminderTarget === opt.value
                          ? '2px solid #667eea'
                          : '2px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <select
                  value={selectedReminderMinutes ?? ''}
                  onChange={(e) => setSelectedReminderMinutes(e.target.value ? parseInt(e.target.value, 10) : null)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">No reminder</option>
                  {REMINDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {selectedReminderMinutes !== null && selectedReminderTarget === null && (
                  <p style={{ fontSize: '12px', color: '#e53e3e', marginTop: '8px' }}>
                    Please select whether this reminder is for the start time, end time, or both.
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="section-label">Select Activity Dates</label>
              <p className="section-description">Click dates to select/deselect them. You can choose multiple dates for recurring activities.</p>
              <CalendarDatePicker
                selectedDates={selectedDates}
                onChange={setSelectedDates}
                minDate={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div className="time-row">
              <div className="time-field">
                <label>Start Time</label>
                <TimePickerDropdown
                  value={newActivity.start_time}
                  onChange={(time) => setNewActivity({...newActivity, start_time: time})}
                  placeholder="Start time"
                  defaultScrollPosition="start"
                />
              </div>
              <div className="time-field">
                <label>End Time</label>
                <TimePickerDropdown
                  value={newActivity.end_time}
                  onChange={(time) => setNewActivity({...newActivity, end_time: time})}
                  placeholder="End time"
                  defaultScrollPosition="end"
                />
              </div>
            </div>

            {/* Note: Website URL, Location, Activity Type, Cost, and Max Participants
                are now managed at the club level via Browse Clubs button */}

            {/* Note: Contact Phone and Contact Email are now managed at club level */}
          </div>
          <div className="page-actions">
            <button
              onClick={() => {
                goBack();
                setEditingActivity(null);
                setNewActivity({
                  name: '',
                  description: '',
                  start_date: '',
                  end_date: '',
                  start_time: '',
                  end_time: '',
                  location: user?.town_city || '',
                  website_url: '',
                  cost: '',
                  max_participants: '',
                  activity_type: '',
                  auto_notify_new_connections: false,
                  kit_needed: []
                });
                setAutoNotifyNewConnections(false);
              }}
              className="cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateActivity}
              disabled={addingActivity}
              className={`confirm-btn ${addingActivity ? 'disabled' : ''}`}
            >
              {addingActivity ? 'Updating...' : 'Update Activity'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentPage === 'invite-activity' && invitingActivity) {
    return (
      <div className="child-activity-screen">
        <div className="activity-header">
          <div className="child-info">
            <h2>Invite Children to: {invitingActivity.name}</h2>
          </div>
        </div>
        
        <div className="page-content">
          <div className="page-form">
            {/* Children Selection - Same as Create Activity */}
            <div className="connected-children-section">
              <div className="children-selection-header">
                <label>Invite Children:</label>
                {(connectedChildren.length > 0 || pendingConnectionRequests.length > 0) && (
                  <div className="selection-controls">
                    <button
                      type="button"
                      onClick={() => {
                        const allConnectedUuids = connectedChildren.map(child => child.uuid);
                        const allPendingIds = pendingConnectionRequests.map(request => `pending-child-${request.target_child_uuid || request.target_parent_uuid}`);
                        const allIds = [...allConnectedUuids, ...allPendingIds];
                        
                        const allSelected = allIds.every(id => selectedConnectedChildren.includes(id));
                        if (allSelected) {
                          setSelectedClassUuids([]);
      setSelectedConnectedChildren([]);
                        } else {
                          setSelectedConnectedChildren(allIds);
                        }
                      }}
                      className="select-all-btn"
                    >
                      {(() => {
                        const allConnectedUuids = connectedChildren.map(child => child.uuid);
                        const allPendingIds = pendingConnectionRequests.map(request => `pending-child-${request.target_child_uuid || request.target_parent_uuid}`);
                        const allIds = [...allConnectedUuids, ...allPendingIds];
                        const allSelected = allIds.every(id => selectedConnectedChildren.includes(id));
                        return allSelected ? 'Deselect All' : 'Select All';
                      })()} ({connectedChildren.length + pendingConnectionRequests.length})
                    </button>
                  </div>
                )}
              </div>

              {connectedChildren.length === 0 && pendingConnectionRequests.length === 0 ? (
                <div className="no-connections">
                  <p><strong>No connections found.</strong></p>
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                    To invite other children to this activity, you'll need to connect with their families first.
                  </p>
                </div>
              ) : (
                <div className="children-list">
                  {/* Confirmed connections */}
                  {connectedChildren.map((connectedChild) => (
                    <label key={connectedChild.uuid} className="child-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedConnectedChildren.includes(connectedChild.uuid)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedConnectedChildren([...selectedConnectedChildren, connectedChild.uuid]);
                          } else {
                            setSelectedConnectedChildren(
                              selectedConnectedChildren.filter(id => id !== connectedChild.uuid)
                            );
                          }
                        }}
                      />
                      ✅ {connectedChild.name}
                    </label>
                  ))}
                  
                  {/* Pending connection requests */}
                  {pendingConnectionRequests.map((request) => (
                    <label key={`pending-${request.id}`} className="child-checkbox" style={{ opacity: 0.7 }}>
                      <input
                        type="checkbox"
                        checked={selectedConnectedChildren.includes(generatePendingChildId(request))}
                        onChange={(e) => {
                          const pendingId = generatePendingChildId(request);
                          if (e.target.checked) {
                            setSelectedConnectedChildren([...selectedConnectedChildren, pendingId]);
                          } else {
                            setSelectedConnectedChildren(
                              selectedConnectedChildren.filter(id => id !== pendingId)
                            );
                          }
                        }}
                      />
                      ⏳ {request.target_child_name || `${request.target_parent_name || request.target_family_name || 'Unknown Parent'} (Any Child)`} - <em style={{ fontSize: '12px' }}>pending connection</em>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="page-actions">
            <button
              onClick={() => {
                goBack();
                setInvitingActivity(null);
                setSelectedClassUuids([]);
      setSelectedConnectedChildren([]);
              }}
              className="cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (selectedConnectedChildren.length === 0) {
                  alert('Please select at least one child to invite.');
                  return;
                }
                
                // Send invitations to selected children
                const invitationPromises = selectedConnectedChildren.map(async (childId) => {
                  const childIdStr = String(childId);
                  if (childIdStr.startsWith('pending-child-')) {
                    const pendingId = childIdStr.replace('pending-child-', '');
                    return apiService.createPendingInvitations(invitingActivity.uuid || invitingActivity.activity_uuid || String(invitingActivity.id), [pendingId]);
                  } else {
                    const connectedChild = connectedChildren.find(c => c.uuid === childIdStr);
                    if (connectedChild && connectedChild.parentUuid) {
                      return apiService.sendActivityInvitation(invitingActivity.uuid || invitingActivity.activity_uuid || String(invitingActivity.id), connectedChild.parentUuid, childIdStr);
                    }
                  }
                });

                try {
                  const results = await Promise.all(invitationPromises);
                  const successful = results.filter(r => r?.success).length;
                  
                  if (successful > 0) {
                    alert(`Successfully sent ${successful} invitation${successful > 1 ? 's' : ''}!`);
                    goBack();
                    setInvitingActivity(null);
                    setSelectedClassUuids([]);
      setSelectedConnectedChildren([]);
                  } else {
                    alert('Failed to send invitations. Please try again.');
                  }
                } catch (error) {
                  console.error('Failed to send invitations:', error);
                  alert('Failed to send invitations. Please try again.');
                }
              }}
              className="confirm-btn"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading check moved to top of component for immediate blocking

  // Main page
  return (
    <div className="child-activity-screen">
      <div className="activity-header">
        <div className="child-info">
          <h2>{child.name}'s Activities</h2>
          <p>
            {child.age && `Age: ${child.age}`}
            {child.age && child.grade && ' • '}
            {child.grade && `Grade: ${child.grade}`}
          </p>
        </div>
        <div className="header-actions">
          <div className="view-toggle">
            <button
              onClick={() => {
                setCalendarView('5day');
                setTimeout(() => loadActivities(), 100); // Small delay to ensure state is updated
              }}
              className={`toggle-btn ${calendarView === '5day' ? 'active' : ''}`}
            >
              5 Day
            </button>
            <button
              onClick={() => {
                setCalendarView('7day');
                setTimeout(() => loadActivities(), 100); // Small delay to ensure state is updated
              }}
              className={`toggle-btn ${calendarView === '7day' ? 'active' : ''}`}
            >
              7 Day
            </button>
          </div>
        </div>
      </div>

      {/* Color Legend */}
      <div className="color-legend">
        <h4>Activity Color Guide:</h4>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-color" style={{ background: 'linear-gradient(135deg, #2d3748, #4a5568)' }}></div>
            <span>Private activities</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ background: 'linear-gradient(135deg, #4299e1, #63b3ed)' }}></div>
            <span>Shared/Accepted activities</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ background: 'linear-gradient(135deg, #48bb78, #68d391)' }}></div>
            <span>Pending invitations</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ background: 'linear-gradient(135deg, #a0aec0, #cbd5e0)' }}></div>
            <span>Declined invitations</span>
          </div>
        </div>
      </div>

      {/* Week Calendar View */}
      <div className="week-calendar-container" data-view={calendarView}>
        <div className="calendar-nav">
          <button onClick={() => navigateWeek('prev')} className="nav-btn">
            ‹
          </button>
          <h3 className="week-range">{getWeekRange()}</h3>
          <button onClick={() => navigateWeek('next')} className="nav-btn">
            ›
          </button>
        </div>

        <div className="week-calendar-grid">
          <div className="week-headers">
            {generateWeekDays().map((day, index) => (
              <div key={index} className="week-header">
                <div className="day-name">
                  {day.date.toLocaleDateString('en-GB', { weekday: 'short' })}
                </div>
                <div className={`date-number ${day.isToday ? 'today' : ''}`}>
                  {day.date.getDate()}
                </div>
              </div>
            ))}
          </div>
          
          <div className="week-days">
            {generateWeekDays().map((day, index) => (
              <div 
                key={index} 
                className={`week-day ${day.isToday ? 'today' : ''} ${day.activities.length > 0 ? 'has-activities' : ''}`}
              >
                <div className="day-activities">
                  {day.activities.length > 0 ? (
                    day.activities.map((activity: any, actIndex: number) => {
                      const colors = getActivityColor(activity);
                      const isUserResponsible = activity.responsible_parent_uuid === user?.uuid;
                      return (
                        <div
                          key={actIndex}
                          className="activity-block"
                          style={{
                            background: colors.background,
                            border: isUserResponsible ? '2px solid rgba(255, 255, 255, 0.8)' : 'none',
                            boxShadow: isUserResponsible ? '0 0 8px rgba(255, 255, 255, 0.5)' : 'none'
                          }}
                          onClick={() => handleActivityClick(activity)}
                        >
                          <div className="activity-content">
                            <div className="activity-time">
                              {activity.start_time ? formatTime(activity.start_time) : 'All day'}
                              {isUserResponsible && ' 🚗'}
                            </div>
                            <div className="activity-name">
                              {activity.name}
                            </div>
                          </div>
                          {/* New notification system based on notifications table */}
                          {(() => {
                            const activityNotifications: any[] = []; // Notifications disabled for this screen
                            const hasNotifications = activityNotifications.length > 0;
                            
                            return (activity.isPendingInvitation || activity.isAcceptedInvitation || activity.isDeclinedInvitation || hasNotifications) && (
                              <div className="activity-footer">
                                {activity.isPendingInvitation && activity.showEnvelope !== false && <span className="notification-icon">📩</span>}
                                {activity.isAcceptedInvitation && (activity as any).showAcceptedIcon && <span className="notification-icon">✅</span>}
                                {activity.isDeclinedInvitation && (activity as any).showDeclinedIcon && <span className="notification-icon">❌</span>}
                                {/* New notification icons from notifications table */}
                                {hasNotifications && activityNotifications.map((notification, index) => (
                                  <span key={index} className="notification-icon" title={notification.title}>
                                    {notification.type === 'activity_invitation' ? '📩' : 
                                     notification.type === 'activity_reminder' ? '⏰' :
                                     notification.type === 'activity_update' ? '📝' :
                                     notification.type === 'activity_cancelled' ? '❌' :
                                     '🔔'}
                                  </span>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })
                  ) : (
                    <div className="no-activities">No activities</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add Activity Button */}
      <div className="add-activity-section">
        <button onClick={handleAddActivity} className="add-btn">
          + Add New Activity
        </button>
      </div>

      {/* Week Activities List */}
      <div className="week-activities-list">
        <h3>This Week's Activities</h3>
        {(() => {
          // Get all activities from calendar view (same logic as generateWeekDays)
          const weekDays = generateWeekDays();
          const allWeekActivities = weekDays.flatMap(day => day.activities);
          
          return allWeekActivities.length > 0 ? (
            <div className="activities-grid">
              {allWeekActivities
                .sort((a, b) => {
                  const dateA = new Date(a.start_date + 'T' + (a.start_time || '00:00'));
                  const dateB = new Date(b.start_date + 'T' + (b.start_time || '00:00'));
                  return dateA.getTime() - dateB.getTime();
                })
                .map((activity, index) => {
                const colors = getActivityColor(activity);
                return (
                  <div 
                    key={index} 
                    className="activity-card"
                    style={{
                      borderLeftColor: colors.borderColor,
                      background: colors.background
                    }}
                    onClick={() => {
                      handleActivityClick(activity);
                    }}
                  >
                    <div className="activity-card-header">
                      <h4 className="activity-name">
                        {activity.name}
                        {/* Show recurring series indicator */}
                        {((activity as any).series_id || (activity as any).is_recurring) && (
                          <span 
                            className="series-indicator"
                            style={{
                              marginLeft: '8px',
                              fontSize: '12px',
                              background: 'linear-gradient(135deg, #667eea, #764ba2)',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontWeight: '500'
                            }}
                            title="This is part of a recurring activity series"
                          >
                            🔄 Series
                          </span>
                        )}
                      </h4>
                      {(() => {
                        const activityNotifications: any[] = []; // Notifications disabled for this screen
                        const hasNotifications = activityNotifications.length > 0;
                        const hasStatusChanges = (activity as any).unviewed_status_changes > 0;
                        
                        return (activity.isPendingInvitation || activity.isAcceptedInvitation || activity.isDeclinedInvitation || hasNotifications || hasStatusChanges) && (
                          <div className="activity-status">
                            {activity.isPendingInvitation && activity.showEnvelope !== false && <span className="status-icon">📩</span>}
                            {activity.isAcceptedInvitation && (activity as any).showAcceptedIcon && <span className="status-icon">✅</span>}
                            {activity.isDeclinedInvitation && (activity as any).showDeclinedIcon && <span className="status-icon">❌</span>}
                            
                            {/* New notification icons from notifications table */}
                            {hasNotifications && activityNotifications.map((notification, index) => (
                              <span key={`notif-${index}`} className="status-icon" title={notification.title}>
                                {notification.type === 'activity_invitation' ? '📩' : 
                                 notification.type === 'activity_reminder' ? '⏰' :
                                 notification.type === 'activity_update' ? '📝' :
                                 notification.type === 'activity_cancelled' ? '❌' :
                                 '🔔'}
                              </span>
                            ))}
                            
                            {/* Status change notifications for host's own activities */}
                            {hasStatusChanges && (activity as any).unviewed_statuses && (
                              <>
                                {(activity as any).unviewed_statuses.includes('accepted') && (
                                  <span 
                                    className="status-icon" 
                                  onClick={() => handleStatusChangeClicked(activity)}
                                  style={{ cursor: 'pointer' }}
                                  title="New accepted invitation - click to view"
                                >
                                  ✅
                                </span>
                              )}
                              {(activity as any).unviewed_statuses.includes('rejected') && (
                                <span 
                                  className="status-icon" 
                                  onClick={() => handleStatusChangeClicked(activity)}
                                  style={{ cursor: 'pointer' }}
                                  title="New declined invitation - click to view"
                                >
                                  ❌
                                </span>
                              )}
                            </>
                          )}
                          </div>
                        );
                      })()}
                    </div>
                    {activity.description && (
                      <p className="activity-description">{activity.description}</p>
                    )}
                    <div className="activity-details">
                      <div>{formatDate(activity.start_date)}{activity.start_time && ` ${formatTime(activity.start_time)}${activity.end_time ? ` - ${formatTime(activity.end_time)}` : ''}`}</div>
                      {activity.location && <div>{activity.location}</div>}
                      {activity.cost && <div>${activity.cost}</div>}
                      {(activity as any).responsible_parent_uuid && (
                        <div style={{
                          fontWeight: '600',
                          marginTop: '4px',
                          padding: '4px 8px',
                          background: (activity as any).responsible_parent_uuid === user?.uuid
                            ? 'rgba(102, 126, 234, 0.15)'
                            : 'rgba(128, 128, 128, 0.1)',
                          borderRadius: '4px',
                          border: (activity as any).responsible_parent_uuid === user?.uuid
                            ? '2px solid rgba(102, 126, 234, 0.3)'
                            : 'none'
                        }}>
                          🚗 Designated Parent: {(activity as any).responsible_parent_first_name || 'Not specified'}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="empty-state">
            <h3>No activities this week</h3>
            <p>Add your first activity to get started!</p>
            <button onClick={handleAddActivity} className="add-first-btn">
              + Add Activity
            </button>
          </div>
        );
        })()}
      </div>


      {/* Custom Template Dialog with Yes/No buttons - Available on all pages */}
      {(() => {
        const shouldShow = showTemplateDialog;
        return shouldShow;
      })() && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              fontSize: '16px',
              lineHeight: '1.5',
              color: '#333',
              marginBottom: '25px',
              whiteSpace: 'pre-line'
            }}>
              {templateDialogMessage}
            </div>
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleTemplateYes}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '12px 30px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Yes
              </button>
              <button
                onClick={handleTemplateNo}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '12px 30px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Activity Dialog for Recurring Activities */}
      {(() => {
        return null;
      })()}
      {showDeleteDialog && deleteDialogActivity && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            maxWidth: '400px',
            margin: '20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ 
              marginTop: 0, 
              marginBottom: '16px', 
              color: '#e53e3e', 
              fontWeight: '600',
              fontSize: '18px' 
            }}>
              🗑️ Delete Recurring Activity
            </h3>
            <p style={{ 
              marginBottom: '20px', 
              lineHeight: '1.6',
              color: '#4a5568'
            }}>
              This activity "<strong>{deleteDialogActivity.name}</strong>" is part of a recurring series.
            </p>
            <p style={{ 
              marginBottom: '24px', 
              lineHeight: '1.6',
              color: '#4a5568' 
            }}>
              What would you like to delete?
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '12px',
              flexDirection: 'column'
            }}>
              <button
                onClick={() => {
                  handleDeleteDialogClose();
                  deleteActivity(deleteDialogActivity, false);
                }}
                style={{
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #fd7e14, #fd7e14)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                📅 Just This Activity
              </button>
              <button
                onClick={() => {
                  handleDeleteDialogClose();
                  deleteActivity(deleteDialogActivity, true);
                }}
                style={{
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #e53e3e, #fc8181)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                🔄 Entire Series (All Activities)
              </button>
              <button
                onClick={handleDeleteDialogClose}
                style={{
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #6c757d, #6c757d)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clubs Modal */}
      {(() => {
        return isClubsModalOpen;
      })() && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
        >
          <div
            style={{
              background: 'white',
              width: '90%',
              height: '90%',
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div
              style={{
                padding: '20px',
                borderBottom: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h2>Select Club for Activity</h2>
              <button
                onClick={() => closeClubsModal()}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '5px 10px'
                }}
              >
                ✕
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <ClubsScreen
                isSelectionMode={true}
                selectionFieldType={clubSelectionFieldType}
                onClubSelect={closeClubsModal}
                onCancel={() => closeClubsModal()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildActivityScreen;